import React, { useEffect, useState, useCallback, useRef } from "react";
import Sidebar from "../components/Sidebar";
import api from "../api/axios";
import { parseCfg, gradeFor, SAMPLE_MARKS, COSCHO_GRADES, BAR_COLORS, MiniResultCard, FullResultCard } from "../components/ResultCardPreview";

/* =========================================================
   Export utilities — CSV / Excel / PDF (no external libs)
   ========================================================= */
function exportCSV(filename, headers, rows) {
  const esc = (v) => { const s = String(v ?? ""); return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportExcel(filename, title, headers, rows) {
  const th = (c) => `<th style="background:#1E3A5F;color:#fff;padding:6px 10px;border:1px solid #ccc">${c ?? ""}</th>`;
  const td = (c) => `<td style="padding:5px 10px;border:1px solid #ddd">${c ?? ""}</td>`;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>
    <h3>${title}</h3>
    <table><thead><tr>${headers.map(th).join("")}</tr></thead>
    <tbody>${rows.map((r) => `<tr>${r.map(td).join("")}</tr>`).join("")}</tbody></table>
  </body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportPDF(title, headers, rows) {
  const th = (c) => `<th style="background:#1E3A5F;color:#fff;padding:5px 8px;border:1px solid #ccc;font-size:10px">${c ?? ""}</th>`;
  const td = (c, i) => `<td style="padding:4px 8px;border:1px solid #ddd;font-size:10px;background:${i % 2 === 0 ? "#fff" : "#F8FAFC"}">${c ?? ""}</td>`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;margin:16px}h2{font-size:13px;margin:0 0 10px}table{border-collapse:collapse;width:100%}
    @media print{@page{size:landscape A4;margin:8mm}}</style></head><body>
    <h2>${title}</h2>
    <table><thead><tr>${headers.map(th).join("")}</tr></thead>
    <tbody>${rows.map((r, i) => `<tr>${r.map((c) => td(c, i)).join("")}</tr>`).join("")}</tbody></table>
    <script>window.onload=function(){window.print()}<\/script></body></html>`;
  const win = window.open("", "_blank");
  if (win) { win.document.write(html); win.document.close(); }
}

/* =========================================================
   Responsive Styles (injected once)
   ========================================================= */
const SA_STYLES = `
  .sa-hamburger {
    display: none;
    position: fixed; top: 14px; left: 14px; z-index: 500;
    background: #1E3A5F; color: #fff; border: none; cursor: pointer;
    width: 44px; height: 44px; border-radius: 12px;
    align-items: center; justify-content: center; font-size: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,.2);
  }
  .sa-drawer-overlay {
    position: fixed; inset: 0; z-index: 400;
    background: rgba(0,0,0,.45); backdrop-filter: blur(2px);
    display: none;
  }
  .sa-drawer-overlay.open { display: block; }
  .sa-drawer {
    position: fixed; top: 0; left: 0; bottom: 0; z-index: 450;
    width: 280px; background: #fff;
    box-shadow: 4px 0 24px rgba(0,0,0,.15);
    overflow-y: auto; transform: translateX(-100%);
    transition: transform .25s cubic-bezier(.4,0,.2,1);
  }
  .sa-drawer.open { transform: translateX(0); }
  .sa-card {
    background: #fff; border: 1px solid #E8EDF5; border-radius: 18px;
    padding: 22px 24px; margin-bottom: 18px;
    box-shadow: 0 1px 4px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.04);
  }
  .sa-pill { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:20px; font-size:12px; font-weight:700; }
  .sa-pill-green  { background:#D1FAE5; color:#065F46; border:1px solid #6EE7B7; }
  .sa-pill-amber  { background:#FEF3C7; color:#92400E; border:1px solid #FCD34D; }
  .sa-pill-red    { background:#FEE2E2; color:#991B1B; border:1px solid #FCA5A5; }
  .sa-pill-blue   { background:#DBEAFE; color:#1D4ED8; border:1px solid #93C5FD; }
  .sa-pill-purple { background:#EDE9FE; color:#6D28D9; border:1px solid #C4B5FD; }
  .sa-pill-gray   { background:#F1F5F9; color:#475569; border:1px solid #CBD5E1; }
  @media (max-width: 767px) {
    .sa-hamburger { display: flex !important; }
    .sa-sidebar-wrap { display: none !important; }
    .sa-main { padding-top: 70px !important; padding-left: 12px !important; padding-right: 12px !important; }
    .sa-stat-grid { grid-template-columns: repeat(2,1fr) !important; gap: 10px !important; }
    .sa-info-row  { grid-template-columns: 1fr !important; }
    .sa-form-grid { grid-template-columns: 1fr !important; }
    .sa-page-header { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
    .sa-card { border-radius: 14px !important; padding: 16px !important; }
    .sa-modal-inner { width: 94vw !important; max-width: none !important; border-radius: 16px !important; }
    .sa-quick-grid { grid-template-columns: 1fr 1fr !important; }
    input, select, textarea { font-size: 16px !important; }
    button { min-height: 36px; }
  }
  @media (min-width: 768px) {
    .sa-sidebar-wrap { display: block !important; }
    .sa-hamburger { display: none !important; }
    .sa-drawer-overlay { display: none !important; }
    .sa-drawer { display: none !important; }
  }
`;

function useInjectSAStyles() {
  useEffect(() => {
    const id = "sa-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id; el.textContent = SA_STYLES;
    document.head.appendChild(el);
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);
}

function MobileDrawer({ tabs, activeTab, onTabChange }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="sa-hamburger" onClick={() => setOpen(true)} aria-label="Open menu">☰</button>
      <div className={`sa-drawer-overlay${open ? " open" : ""}`} onClick={() => setOpen(false)} />
      <div className={`sa-drawer${open ? " open" : ""}`}>
        <div style={{ padding: "18px 16px 10px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#1E3A5F" }}>School Admin</span>
          <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748B", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: "8px 0" }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => { onTabChange(t.key); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
                padding: "11px 20px", background: activeTab === t.key ? "#EDE9FE" : "none",
                border: "none", cursor: "pointer", fontSize: 14, fontWeight: activeTab === t.key ? 700 : 500,
                color: activeTab === t.key ? "#6D28D9" : "#374151",
                borderLeft: `3px solid ${activeTab === t.key ? "#7C3AED" : "transparent"}`,
              }}
            >
              <span style={{ fontSize: 18, width: 24, textAlign: "center", flexShrink: 0 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

const TABS = [
  { key: "dashboard",       label: "Dashboard",             icon: "🏠" },
  { key: "classes",         label: "Classes & Subjects",    icon: "🏫" },
  { key: "students",        label: "Students",              icon: "👥" },
  { key: "teachers",        label: "Teachers",              icon: "👨‍🏫" },
  { key: "assignments",     label: "Assignments",           icon: "📌" },
  { key: "exams",           label: "Exams & Panels",        icon: "📝" },
  { key: "marks_entry",     label: "Marks Entry",           icon: "✏️" },
  { key: "class_view",      label: "Class Marks View",      icon: "📋" },
  { key: "publication",     label: "Result Publication",    icon: "📢" },
  { key: "reports",         label: "Reports",               icon: "📊" },
  { key: "export",          label: "Print & Export",        icon: "🖨️" },
  { key: "promotion",       label: "Promotion List",        icon: "🏆" },
  { key: "result_template", label: "Result Templates",      icon: "🎨" },
  { key: "branding",        label: "School Profile & Header", icon: "🏷️" },
  { key: "result_settings", label: "Result Settings",       icon: "⚙️" },
  { key: "attendance",      label: "Attendance",            icon: "📅" },
  { key: "admin_remarks",   label: "Remarks",               icon: "💬" },
  { key: "settings",        label: "Settings",              icon: "🔧" },
  { key: "admins",          label: "Admin Accounts",        icon: "👤" },
];

const VALID_TABS = new Set(TABS.map((t) => t.key));

export default function SchoolAdminDashboard() {
  useInjectSAStyles();
  const [tab, setTab] = useState(() => {
    const saved = sessionStorage.getItem("sa_tab");
    return saved && VALID_TABS.has(saved) ? saved : "dashboard";
  });

  function navigate(t) {
    sessionStorage.setItem("sa_tab", t);
    setTab(t);
  }

  return (
    <div className="app-shell">
      <div className="sa-sidebar-wrap">
        <Sidebar title="School Admin" tabs={TABS} activeTab={tab} onTabChange={navigate} />
      </div>
      <MobileDrawer tabs={TABS} activeTab={tab} onTabChange={navigate} />
      <div className="main-content sa-main">
        {tab === "dashboard" && <Dashboard onNavigate={navigate} />}
        {tab === "classes" && <ClassesSubjects />}
        {tab === "students" && <Students />}
        {tab === "teachers" && <Teachers />}
        {tab === "assignments" && <Assignments />}
        {tab === "exams" && <ExamsPanels />}
        {tab === "marks_entry" && <AdminMarksEntry />}
        {tab === "class_view" && <AdminClassMarksView />}
        {tab === "publication" && <Publication />}
        {tab === "reports" && <Reports />}
        {tab === "export" && <ExportPrint />}
        {tab === "promotion" && <Promotion />}
        {tab === "result_template" && <ResultTemplateGallery />}
        {tab === "branding" && <SchoolBranding />}
        {tab === "result_settings" && <ResultSettings />}
        {tab === "attendance" && <AdminTermAttendance />}
        {tab === "admin_remarks" && <AdminRemarks />}
        {tab === "settings" && <SchoolSettings />}
        {tab === "admins" && <SchoolAdmins />}
      </div>
    </div>
  );
}

/* ======================================================
   Dashboard
   ====================================================== */
function StatCard({ label, value, icon, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff", border: "1px solid #E8EDF5", borderRadius: 16,
        padding: "18px 20px", display: "flex", alignItems: "center", gap: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.04)",
        cursor: onClick ? "pointer" : "default",
        transition: "transform .15s, box-shadow .15s",
      }}
      onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.1)"; }}}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.04)"; }}
    >
      <div style={{
        width: 50, height: 50, borderRadius: 14, flexShrink: 0,
        background: `linear-gradient(135deg, ${color}33, ${color}18)`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#1E293B", lineHeight: 1 }}>{value ?? "—"}</div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 3, fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

function Dashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalCopied, setPortalCopied] = useState(false);

  useEffect(() => {
    api.get("/school-admin/dashboard")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function copyPortalLink(url) {
    navigator.clipboard.writeText(url).then(() => {
      setPortalCopied(true);
      setTimeout(() => setPortalCopied(false), 2000);
    });
  }

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#64748B" }}>Loading dashboard…</div>;
  if (!data) return <div style={{ padding: 32, color: "#DC2626" }}>Failed to load dashboard.</div>;

  const { school, stats, currentSession } = data;
  const expiryDate = school.subscription_expires_at
    ? new Date(school.subscription_expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
  const isExpiringSoon = school.daysLeft !== null && school.daysLeft <= 30;
  const isExpired = school.daysLeft !== null && school.daysLeft <= 0;

  return (
    <div>
      {/* Welcome banner */}
      <div className="sa-card" style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2D5082 100%)", border: "none", color: "#fff", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>School Admin Panel</div>
            <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.2 }}>{school.name}</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
              <span className="sa-pill" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "1px solid rgba(255,255,255,.3)", fontSize: 11 }}>
                {school.plan}
              </span>
            </div>
          </div>
          <button className="btn secondary" onClick={() => onNavigate("settings")} style={{ flexShrink: 0 }}>Settings</button>
        </div>
      </div>

      {/* Alerts */}
      {(isExpired || isExpiringSoon) && (
        <div style={{
          marginBottom: 16, padding: "12px 16px", borderRadius: 12, fontSize: 13, fontWeight: 500,
          background: isExpired ? "#FEF2F2" : "#FFFBEB",
          color: isExpired ? "#991B1B" : "#92400E",
          border: `1px solid ${isExpired ? "#FECACA" : "#FDE68A"}`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 18 }}>{isExpired ? "⚠️" : "🔔"}</span>
          {isExpired
            ? "Your subscription has expired. Please contact the administrator to renew."
            : `Subscription expires in ${school.daysLeft} day${school.daysLeft !== 1 ? "s" : ""} (${expiryDate}). Please renew soon.`}
        </div>
      )}

      {/* Session banner */}
      <div style={{
        marginBottom: 18, padding: "10px 16px", borderRadius: 12, fontSize: 13, fontWeight: 500,
        background: currentSession ? "#F0FDF4" : "#FFF7ED",
        color: currentSession ? "#166534" : "#92400E",
        border: `1px solid ${currentSession ? "#BBF7D0" : "#FDE68A"}`,
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 16 }}>📅</span>
        {currentSession
          ? <>Current Session: <strong>{currentSession.label || currentSession.name}</strong></>
          : <>No active session set. <button className="btn sm secondary" style={{ marginLeft: 8 }} onClick={() => onNavigate("settings")}>Configure</button></>
        }
      </div>

      {/* Stats grid */}
      <div className="sa-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 14 }}>
        <StatCard label="Classes" value={stats.classCount} icon="🏫" color="#2563EB" onClick={() => onNavigate("classes")} />
        <StatCard label="Students" value={stats.studentCount} icon="🎓" color="#16A34A" onClick={() => onNavigate("students")} />
        <StatCard label="Teachers" value={stats.teacherCount} icon="👩‍🏫" color="#7C3AED" onClick={() => onNavigate("teachers")} />
        <StatCard label="Exams" value={stats.examCount} icon="📝" color="#D97706" onClick={() => onNavigate("exams")} />
        <StatCard label="Published" value={stats.publishedCount} icon="✅" color="#059669" onClick={() => onNavigate("publication")} />
      </div>

      {/* Portal engagement row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
        <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 32, lineHeight: 1 }}>👁️</div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#1D4ED8", lineHeight: 1 }}>{stats.viewedCount ?? 0}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1D4ED8", marginTop: 2 }}>Students Viewed Result</div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Unique students who opened their result on the portal</div>
          </div>
        </div>
        <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 32, lineHeight: 1 }}>📥</div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#15803D", lineHeight: 1 }}>{stats.downloadedCount ?? 0}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#15803D", marginTop: 2 }}>Students Downloaded PDF</div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Unique students who downloaded their result card</div>
          </div>
        </div>
      </div>

      {/* Info row */}
      <div className="sa-info-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        {/* Subscription */}
        <div className="sa-card" style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#1E293B" }}>Subscription</div>
            <span className={`sa-pill ${school.status === "active" ? "sa-pill-green" : "sa-pill-amber"}`}>{school.status}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2 }}>Plan</div>
              <div style={{ fontWeight: 700, color: "#1E293B" }}>{school.plan}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2 }}>Expires</div>
              <div style={{ fontWeight: 700, color: isExpired ? "#DC2626" : isExpiringSoon ? "#D97706" : "#1E293B" }}>{expiryDate}</div>
            </div>
            {school.daysLeft !== null && (
              <div style={{ gridColumn: "1/-1" }}>
                <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Days Remaining</div>
                <div style={{ background: "#F1F5F9", borderRadius: 8, height: 8, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 8, transition: "width 0.6s",
                    width: `${Math.min(100, Math.max(2, (school.daysLeft / 365) * 100))}%`,
                    background: isExpired ? "#DC2626" : isExpiringSoon ? "#F59E0B" : "#4F46E5",
                  }} />
                </div>
                <div style={{ fontSize: 12, marginTop: 4, fontWeight: 500, color: isExpired ? "#DC2626" : isExpiringSoon ? "#D97706" : "#1E293B" }}>
                  {isExpired ? "Expired" : `${school.daysLeft} day${school.daysLeft !== 1 ? "s" : ""} left`}
                </div>
              </div>
            )}
          </div>
          {Object.keys(school.planFeatures || {}).length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #F1F5F9" }}>
              <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>Plan Features</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Object.entries(school.planFeatures).map(([k, v]) => {
                  const label = k.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim();
                  const isNum = typeof v === "number";
                  const isOn = isNum ? true : !!v;
                  return (
                    <span key={k} className={`sa-pill ${isOn ? "sa-pill-blue" : "sa-pill-gray"}`} style={{ fontSize: 11 }}>
                      {isNum ? v : isOn ? "✓" : "✗"} {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Student Portal Link */}
        {(() => {
          const portalUrl = `${window.location.origin}/portal/${school.id}`;
          return (
            <div className="sa-card" style={{ marginBottom: 0, borderLeft: "4px solid #7C3AED" }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#1E293B", marginBottom: 6 }}>Student Result Portal</div>
              <p style={{ fontSize: 13, color: "#64748B", marginBottom: 12 }}>
                Share this link with students and parents so they can look up their results anytime.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10, padding: "10px 14px" }}>
                <span style={{ flex: 1, fontSize: 13, color: "#4C1D95", fontWeight: 600, wordBreak: "break-all" }}>{portalUrl}</span>
                <button
                  onClick={() => copyPortalLink(portalUrl)}
                  style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: portalCopied ? "#16A34A" : "#7C3AED", color: "#fff", transition: "background .2s" }}>
                  {portalCopied ? "Copied!" : "Copy"}
                </button>
                <a href={portalUrl} target="_blank" rel="noreferrer"
                  style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 8, border: "1px solid #7C3AED", fontSize: 12, fontWeight: 700, color: "#7C3AED", textDecoration: "none", background: "#fff" }}>
                  Open
                </a>
              </div>
            </div>
          );
        })()}

        {/* Quick Actions */}
        <div className="sa-card" style={{ marginBottom: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#1E293B", marginBottom: 14 }}>Quick Actions</div>
          <div className="sa-quick-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Add Class", icon: "🏫", tab: "classes" },
              { label: "Enroll Students", icon: "🎓", tab: "students" },
              { label: "Manage Teachers", icon: "👩‍🏫", tab: "teachers" },
              { label: "Create Exam", icon: "📝", tab: "exams" },
              { label: "Publish Results", icon: "📢", tab: "publication" },
              { label: "Reports", icon: "📊", tab: "reports" },
              { label: "Print & Export", icon: "🖨️", tab: "export" },
              { label: "Promotion List", icon: "🏆", tab: "promotion" },
            ].map(({ label, icon, tab }) => (
              <button
                key={tab}
                onClick={() => onNavigate(tab)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                  background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12,
                  cursor: "pointer", fontSize: 13, color: "#1E293B", fontWeight: 500,
                  transition: "background .15s, border-color .15s", textAlign: "left",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#93C5FD"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#F8FAFC"; e.currentTarget.style.borderColor = "#E2E8F0"; }}
              >
                <span style={{ fontSize: 16 }}>{icon}</span>
                <span style={{ flex: 1 }}>{label}</span>
                <span style={{ color: "#94A3B8", fontSize: 12 }}>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Settings
   ====================================================== */
function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, flexShrink: 0, cursor: disabled ? "not-allowed" : "pointer" }}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: "absolute", inset: 0, borderRadius: 24, transition: "0.2s",
        background: checked ? "var(--success)" : "#CBD5E1",
      }}>
        <span style={{
          position: "absolute", top: 3, left: checked ? 23 : 3, width: 18, height: 18,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </span>
    </label>
  );
}

function SchoolSettings() {
  // --- School profile ---
  const [school, setSchool] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: "", address: "", contact_email: "", contact_mobile: "" });
  const [profileMsg, setProfileMsg] = useState("");
  const [profileErr, setProfileErr] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  // --- Notification prefs ---
  const [notifPrefs, setNotifPrefs] = useState({ email_enabled: "true", sms_enabled: "true", wa_enabled: "true" });
  const [notifMsg, setNotifMsg] = useState("");
  const [notifLoading, setNotifLoading] = useState(false);

  // --- School SMTP ---
  const [smtpForm, setSmtpForm] = useState({ smtp_host: "", smtp_port: "587", smtp_user: "", smtp_pass: "", smtp_encrypt: "tls", smtp_from: "", smtp_from_name: "" });
  const [smtpMsg, setSmtpMsg] = useState("");
  const [smtpErr, setSmtpErr] = useState("");
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTestTo, setSmtpTestTo] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);

  // --- Academic Sessions ---
  const [sessions, setSessions] = useState([]);
  const [sessionForm, setSessionForm] = useState({ name: "", label: "", setAsCurrent: true });
  const [sessionMsg, setSessionMsg] = useState("");
  const [sessionErr, setSessionErr] = useState("");
  const [sessionLoading, setSessionLoading] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  useEffect(() => {
    api.get("/school-admin/my-school").then((r) => {
      setSchool(r.data);
      setProfileForm({
        name: r.data.name || "",
        address: r.data.address || "",
        contact_email: r.data.contact_email || "",
        contact_mobile: r.data.contact_mobile || "",
      });
    });
    api.get("/school-admin/notification-prefs").then((r) => setNotifPrefs(r.data));
    api.get("/school-admin/school-smtp").then((r) => setSmtpForm((f) => ({ ...f, ...r.data }))).catch(() => {});
    loadSessions();
  }, []);

  function loadSessions() {
    api.get("/school-admin/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }

  async function createSession(e) {
    e.preventDefault();
    setSessionErr(""); setSessionMsg(""); setSessionLoading(true);
    try {
      await api.post("/school-admin/sessions", sessionForm);
      setSessionForm({ name: "", label: "", setAsCurrent: true });
      setSessionMsg("Session created successfully.");
      loadSessions();
      setTimeout(() => setSessionMsg(""), 3000);
    } catch (err) {
      setSessionErr(err.response?.data?.error || "Failed to create session.");
    } finally {
      setSessionLoading(false);
    }
  }

  async function setCurrentSession(s) {
    try {
      await api.put(`/school-admin/sessions/${s.id}/set-current`);
      loadSessions();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to set session.");
    }
  }

  async function deleteSession(s) {
    if (!window.confirm(`Delete session "${s.name}"? This cannot be undone.`)) return;
    setDeletingSessionId(s.id);
    try {
      await api.delete(`/school-admin/sessions/${s.id}`);
      loadSessions();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete session.");
    } finally {
      setDeletingSessionId(null);
    }
  }

  async function saveProfile(e) {
    e.preventDefault();
    setProfileMsg(""); setProfileErr(""); setProfileLoading(true);
    try {
      await api.put("/school-admin/my-school", profileForm);
      setProfileMsg("School details updated successfully.");
    } catch (err) {
      setProfileErr(err.response?.data?.error || "Failed to update.");
    } finally {
      setProfileLoading(false);
    }
  }

  async function saveNotifPref(key, value) {
    const updated = { ...notifPrefs, [key]: String(value) };
    setNotifPrefs(updated);
    setNotifLoading(true);
    try {
      await api.put("/school-admin/notification-prefs", updated);
      setNotifMsg("Notification preferences saved.");
    } catch {
      setNotifMsg("Failed to save preferences.");
    } finally {
      setNotifLoading(false);
      setTimeout(() => setNotifMsg(""), 3000);
    }
  }

  async function saveSchoolSmtp(e) {
    e.preventDefault(); setSmtpMsg(""); setSmtpErr(""); setSmtpLoading(true);
    try {
      await api.put("/school-admin/school-smtp", smtpForm);
      setSmtpMsg("School SMTP settings saved successfully.");
    } catch (err) {
      setSmtpErr(err.response?.data?.error || "Failed to save SMTP settings.");
    } finally {
      setSmtpLoading(false);
    }
  }

  async function testSchoolSmtp() {
    setSmtpMsg(""); setSmtpErr(""); setSmtpTesting(true);
    try {
      const r = await api.post("/school-admin/school-smtp/test", { to: smtpTestTo });
      setSmtpMsg(r.data.message);
    } catch (err) {
      setSmtpErr(err.response?.data?.error || "Test failed.");
    } finally {
      setSmtpTesting(false);
    }
  }

  const p = school;
  const daysLeft = p?.daysLeft;
  const expiryDate = p?.subscription_expires_at
    ? new Date(p.subscription_expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Settings</h1>
        <p className="subtitle">Manage your school profile, notifications, and subscription details.</p>
      </div>

      {/* School Profile */}
      <div className="card">
        <h3 style={{ marginBottom: 4 }}>School Profile</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Update your school's contact information. This is shown in admin views and used for communication.
        </p>
        {profileMsg && <div className="success-text" style={{ marginBottom: 12, fontSize: 13 }}>✓ {profileMsg}</div>}
        {profileErr && <div className="error-text" style={{ marginBottom: 12, fontSize: 13 }}>{profileErr}</div>}
        <form onSubmit={saveProfile}>
          <div className="form-grid">
            <div className="form-field" style={{ gridColumn: "1 / -1" }}>
              <label className="form-label">School Name *</label>
              <input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                placeholder="Full school name"
                required
              />
            </div>
            <div className="form-field" style={{ gridColumn: "1 / -1" }}>
              <label className="form-label">Address</label>
              <input
                value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                placeholder="School address"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Contact Email</label>
              <input
                type="email"
                value={profileForm.contact_email}
                onChange={(e) => setProfileForm({ ...profileForm, contact_email: e.target.value })}
                placeholder="admin@school.edu"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Contact Mobile</label>
              <input
                type="tel"
                value={profileForm.contact_mobile}
                onChange={(e) => setProfileForm({ ...profileForm, contact_mobile: e.target.value })}
                placeholder="+91 9876543210"
              />
            </div>
          </div>
          <button className="btn" type="submit" disabled={profileLoading}>
            {profileLoading ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </div>

      {/* Notification Preferences */}
      <div className="card">
        <h3 style={{ marginBottom: 4 }}>Notification Preferences</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Choose which channels you want to use for sending notifications to teachers, students, and parents.
          These are school-level preferences — the gateway must be configured by the super admin.
        </p>
        {notifMsg && <div className="success-text" style={{ marginBottom: 12, fontSize: 13 }}>✓ {notifMsg}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {[
            {
              key: "email_enabled",
              label: "Email Notifications",
              icon: "📧",
              desc: "Send result publication alerts, login credentials, and reminders via email.",
            },
            {
              key: "sms_enabled",
              label: "SMS Notifications",
              icon: "📱",
              desc: "Send SMS alerts for result publication and important announcements.",
            },
            {
              key: "wa_enabled",
              label: "WhatsApp Notifications",
              icon: "💬",
              desc: "Send WhatsApp messages for result cards and parent communication.",
            },
          ].map(({ key, label, icon, desc }, i, arr) => (
            <div
              key={key}
              style={{
                display: "flex", alignItems: "center", gap: 16, padding: "16px 0",
                borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none",
              }}
            >
              <span style={{ fontSize: 24, flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>{label}</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{desc}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>
                  {notifPrefs[key] === "false" ? "Off" : "On"}
                </span>
                <ToggleSwitch
                  checked={notifPrefs[key] !== "false"}
                  onChange={(e) => saveNotifPref(key, e.target.checked)}
                  disabled={notifLoading}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* School Email (SMTP) */}
      <div className="card">
        <h3 style={{ marginBottom: 4 }}>School Email (SMTP)</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Configure your school's SMTP to send result notification emails using your school's email address and name.
          If left blank, the platform's default SMTP will be used.
        </p>
        {smtpMsg && <div className="success-text" style={{ marginBottom: 12, fontSize: 13 }}>✓ {smtpMsg}</div>}
        {smtpErr && <div className="error-text"   style={{ marginBottom: 12, fontSize: 13 }}>{smtpErr}</div>}
        <form onSubmit={saveSchoolSmtp}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">SMTP Host</label>
              <input placeholder="smtp.hostinger.com" value={smtpForm.smtp_host} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Port</label>
              <input type="number" placeholder="587" value={smtpForm.smtp_port} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Encryption</label>
              <select value={smtpForm.smtp_encrypt} onChange={(e) => {
                const enc = e.target.value;
                const autoPort = enc === "ssl" ? "465" : enc === "tls" ? "587" : "25";
                setSmtpForm({ ...smtpForm, smtp_encrypt: enc, smtp_port: autoPort });
              }}>
                <option value="tls">TLS / STARTTLS (port 587)</option>
                <option value="ssl">SSL (port 465)</option>
                <option value="none">None (port 25)</option>
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Username</label>
              <input placeholder="school@yourdomain.com" value={smtpForm.smtp_user} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Password</label>
              <input type="password" placeholder="Leave blank to keep existing" value={smtpForm.smtp_pass} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_pass: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">From Email</label>
              <input placeholder="noreply@school.edu" value={smtpForm.smtp_from} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">From Name</label>
              <input placeholder="School Name" value={smtpForm.smtp_from_name} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_name: e.target.value })} />
            </div>
          </div>
          <button className="btn" type="submit" disabled={smtpLoading}>{smtpLoading ? "Saving…" : "Save SMTP Settings"}</button>
        </form>
        <hr style={{ margin: "20px 0", border: "1px solid var(--border)" }} />
        <h4 style={{ margin: "0 0 12px" }}>Send Test Email</h4>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            placeholder="Recipient email (defaults to From Email)"
            value={smtpTestTo}
            onChange={(e) => setSmtpTestTo(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn secondary" onClick={testSchoolSmtp} disabled={smtpTesting}>
            {smtpTesting ? "Sending…" : "Send Test"}
          </button>
        </div>
      </div>

      {/* Academic Sessions */}
      <div className="card">
        <h3 style={{ marginBottom: 4 }}>Academic Sessions</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Sessions (e.g. "2024-25") keep marks, exams, and results separate by year.
          Only one session is active at a time — all new exams and marks are recorded under the current session.
        </p>

        {/* Existing sessions list */}
        {sessions.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>All Sessions</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sessions.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 16px", borderRadius: 10,
                    border: `1px solid ${s.is_current ? "#BFDBFE" : "var(--border)"}`,
                    background: s.is_current ? "#EFF6FF" : "#fff",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "var(--navy)" }}>{s.name}</div>
                    {s.label && s.label !== s.name && (
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{s.label}</div>
                    )}
                  </div>
                  {s.is_current
                    ? <span className="badge active" style={{ fontSize: 12 }}>Current Session</span>
                    : (
                      <button
                        className="btn sm secondary"
                        onClick={() => setCurrentSession(s)}
                      >
                        Set as Current
                      </button>
                    )
                  }
                  {!s.is_current && (
                    <button
                      className="btn sm danger"
                      onClick={() => deleteSession(s)}
                      disabled={deletingSessionId === s.id}
                    >
                      {deletingSessionId === s.id ? "…" : "Delete"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create new session */}
        <div style={{ borderTop: sessions.length > 0 ? "1px solid var(--border)" : "none", paddingTop: sessions.length > 0 ? 20 : 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginBottom: 12 }}>
            + Create New Session
          </div>
          {sessionMsg && <div className="success-text" style={{ marginBottom: 10, fontSize: 13 }}>✓ {sessionMsg}</div>}
          {sessionErr && <div className="error-text" style={{ marginBottom: 10, fontSize: 13 }}>{sessionErr}</div>}
          <form onSubmit={createSession}>
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">Session Name * <span className="text-muted" style={{ fontWeight: 400 }}>(e.g. 2025-26)</span></label>
                <input
                  value={sessionForm.name}
                  onChange={(e) => setSessionForm({ ...sessionForm, name: e.target.value })}
                  placeholder="2025-26"
                  required
                />
              </div>
              <div className="form-field">
                <label className="form-label">Label <span className="text-muted" style={{ fontWeight: 400 }}>(optional display name)</span></label>
                <input
                  value={sessionForm.label}
                  onChange={(e) => setSessionForm({ ...sessionForm, label: e.target.value })}
                  placeholder="Academic Year 2025-26"
                />
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={sessionForm.setAsCurrent}
                onChange={(e) => setSessionForm({ ...sessionForm, setAsCurrent: e.target.checked })}
                style={{ width: 15, height: 15 }}
              />
              Set this as the current active session
            </label>
            <button className="btn" type="submit" disabled={sessionLoading}>
              {sessionLoading ? "Creating…" : "Create Session"}
            </button>
          </form>
        </div>
      </div>

      {/* Plan & Subscription */}
      {p && (
        <div className="card">
          <h3 style={{ marginBottom: 4 }}>Plan &amp; Subscription</h3>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
            Your current subscription details. Contact the administrator to upgrade or extend.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Current Plan", value: p.Plan?.name || "—" },
              { label: "Status", value: p.status },
              { label: "Expiry Date", value: expiryDate },
              { label: "Days Remaining", value: daysLeft !== null ? (daysLeft <= 0 ? "Expired" : `${daysLeft} days`) : "—" },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 16px",
              }}>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>{label}</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "var(--navy)" }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Subscription bar */}
          {daysLeft !== null && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
                <span>Subscription progress</span>
                <span>{daysLeft <= 0 ? "Expired" : `${daysLeft} days left`}</span>
              </div>
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, height: 10, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 8,
                  width: `${Math.min(100, Math.max(2, (daysLeft / (p.Plan?.duration_days || 365)) * 100))}%`,
                  background: daysLeft <= 0 ? "#DC2626" : daysLeft <= 30 ? "#F59E0B" : "#4F46E5",
                  transition: "width 0.6s",
                }} />
              </div>
            </div>
          )}

          {/* Plan features */}
          {p.Plan?.feature_flags && Object.keys(p.Plan.feature_flags).length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>Plan Features</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                {Object.entries(p.Plan.feature_flags).map(([k, v]) => {
                  const label = k.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim();
                  const isNum = typeof v === "number";
                  const isOn = isNum ? true : !!v;
                  return (
                    <div key={k} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                      background: isOn ? "#EFF6FF" : "#F8FAFC",
                      border: `1px solid ${isOn ? "#BFDBFE" : "#E2E8F0"}`,
                      borderRadius: 8, fontSize: 13,
                    }}>
                      <span style={{ color: isOn ? "#2563EB" : "#94A3B8", flexShrink: 0, fontWeight: 700 }}>
                        {isNum ? v : isOn ? "✓" : "✗"}
                      </span>
                      <span style={{ color: "#1E293B", fontWeight: 500 }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ marginTop: 20, padding: "12px 16px", background: "#EFF6FF", borderRadius: 10, fontSize: 13, color: "#1D4ED8", border: "1px solid #BFDBFE" }}>
            To upgrade your plan or extend subscription, please contact your administrator.
          </div>
        </div>
      )}

      {/* Account Security */}
      <div className="card">
        <h3 style={{ marginBottom: 4 }}>Account</h3>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Logged in as School Administrator.
        </p>
        <button
          className="btn danger"
          onClick={() => {
            localStorage.removeItem("vrm_token");
            localStorage.removeItem("vrm_user");
            sessionStorage.removeItem("sa_tab");
            window.location.href = "/login";
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

/* ======================================================
   Reports
   ====================================================== */
function Reports() {
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);

  // Report type: "exam" | "session"
  const [reportType, setReportType] = useState("exam");

  // Shared selectors
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedClass, setSelectedClass] = useState("");

  // Exam report
  const [sessionExams, setSessionExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [examSubType, setExamSubType] = useState("class"); // "class" | "student"
  const [includeWithheld, setIncludeWithheld] = useState(false);

  useEffect(() => {
    api.get("/school-admin/sessions").then((r) => {
      setSessions(r.data);
      const curr = r.data.find((s) => s.is_current);
      if (curr) setSelectedSession(curr.name);
    }).catch(() => {});
    api.get("/school-admin/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedSession) { setSessionExams([]); setSelectedExam(""); return; }
    api.get(`/school-admin/exams?session=${encodeURIComponent(selectedSession)}`).then((r) => {
      setSessionExams(r.data);
      setSelectedExam("");
    }).catch(() => {});
  }, [selectedSession]);

  useEffect(() => {
    if (!selectedClass) { setStudents([]); setSelectedStudent(""); return; }
    api.get(`/school-admin/students?classSectionId=${selectedClass}`).then((r) => {
      setStudents(r.data);
      setSelectedStudent("");
    }).catch(() => {});
  }, [selectedClass]);

  const selectedCls = classes.find((c) => String(c.id) === String(selectedClass));

  function buildExamClassUrl() {
    if (!selectedExam || !selectedClass) return null;
    const p = new URLSearchParams({ examId: selectedExam, classSectionId: selectedClass, includeWithheld });
    return `/api/school-admin/reports/class-excel.xlsx?${p}`;
  }

  function buildStudentPdfUrl() {
    if (!selectedStudent || !selectedExam) return null;
    return `/api/school-admin/print/student/${selectedStudent}/exam/${selectedExam}`;
  }

  function buildSessionExcelUrl() {
    if (!selectedSession || !selectedClass) return null;
    const p = new URLSearchParams({ session: selectedSession, classSectionId: selectedClass });
    return `/api/school-admin/reports/session-excel.xlsx?${p}`;
  }

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Reports</h1>
        <p className="subtitle">Download result reports, Excel sheets, and individual report cards — filtered by session.</p>
      </div>

      {sessions.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-text">No sessions found. Create a session in Settings first.</div>
          </div>
        </div>
      )}

      {sessions.length > 0 && (
        <>
          {/* Session + Report type selector */}
          <div className="card">
            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">Academic Session *</label>
                <select value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)}>
                  <option value="">— Select Session —</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.label || s.name}{s.is_current ? " (Current)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Report Type *</label>
                <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                  <option value="exam">Exam-wise Report (Single Exam)</option>
                  <option value="session">Session Report (All Exams Combined)</option>
                </select>
              </div>
            </div>
          </div>

          {selectedSession && (
            <>
              {/* ── Exam-wise report ── */}
              {reportType === "exam" && (
                <div className="card">
                  <h3 style={{ marginBottom: 4 }}>Exam-wise Report</h3>
                  <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
                    Download an Excel sheet for a full class, or print an individual student's result card PDF.
                  </p>

                  {/* Sub-type toggle */}
                  <div style={{ display: "flex", gap: 0, marginBottom: 20, border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", width: "fit-content" }}>
                    {[{ id: "class", label: "📊 Class Excel" }, { id: "student", label: "📄 Student PDF" }].map(({ id, label }) => (
                      <button key={id} type="button"
                        onClick={() => setExamSubType(id)}
                        style={{
                          padding: "8px 20px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                          background: examSubType === id ? "var(--navy)" : "#fff",
                          color: examSubType === id ? "#fff" : "var(--muted)",
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Exam *</label>
                      <select value={selectedExam} onChange={(e) => setSelectedExam(e.target.value)}>
                        <option value="">— Select Exam —</option>
                        {sessionExams.length === 0
                          ? <option disabled>No exams in this session</option>
                          : sessionExams.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)
                        }
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-label">Class / Section *</label>
                      <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                        <option value="">— Select Class —</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
                      </select>
                    </div>

                    {examSubType === "student" && selectedClass && (
                      <div className="form-field">
                        <label className="form-label">Student *</label>
                        <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)}>
                          <option value="">— Select Student —</option>
                          {students.map((s) => <option key={s.id} value={s.id}>{s.roll_no}. {s.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {examSubType === "class" && (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, marginBottom: 16 }}>
                      <input type="checkbox" checked={includeWithheld}
                        onChange={(e) => setIncludeWithheld(e.target.checked)} style={{ width: 15, height: 15 }} />
                      Include withheld (unpublished) results
                    </label>
                  )}

                  {examSubType === "class" && (
                    <ReportDownloadButton
                      label="Download Class Excel"
                      url={buildExamClassUrl()}
                      filename="class-results.xlsx"
                      disabled={!selectedExam || !selectedClass}
                      hint={`${selectedCls ? `${selectedCls.name}-${selectedCls.section}` : "class"} · ${sessionExams.find((e) => String(e.id) === String(selectedExam))?.name || "exam"} · Excel`}
                    />
                  )}
                  {examSubType === "student" && (
                    <ReportDownloadButton
                      label="Open Report Card (Print / Save PDF)"
                      url={selectedStudent && selectedExam ? `/print/student/${selectedStudent}/exam/${selectedExam}` : null}
                      isPrintRoute={true}
                      disabled={!selectedExam || !selectedStudent}
                      hint={`Opens a print-ready result card using the selected template`}
                    />
                  )}
                </div>
              )}

              {/* ── Session-wise report ── */}
              {reportType === "session" && (
                <div className="card">
                  <h3 style={{ marginBottom: 4 }}>Session Report — {selectedSession}</h3>
                  <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
                    Downloads an Excel workbook with all exams in the session: a summary sheet + one sheet per exam.
                  </p>

                  {sessionExams.length === 0 && (
                    <div className="empty-state" style={{ padding: "20px 0" }}>
                      <div className="empty-state-text">No exams found in session <strong>{selectedSession}</strong>. Create exams in the Exams &amp; Panels tab.</div>
                    </div>
                  )}

                  {sessionExams.length > 0 && (
                    <>
                      {/* Exams in session summary */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>
                          Exams in {selectedSession} ({sessionExams.length} exams)
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {sessionExams.map((ex) => (
                            <span key={ex.id} style={{
                              padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                              background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE",
                            }}>
                              {ex.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="form-field" style={{ maxWidth: 320, marginBottom: 20 }}>
                        <label className="form-label">Class / Section *</label>
                        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                          <option value="">— Select Class —</option>
                          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
                        </select>
                      </div>

                      <ReportDownloadButton
                        label="Download Session Report (Excel)"
                        url={buildSessionExcelUrl()}
                        filename="session-report.xlsx"
                        disabled={!selectedClass}
                        hint={`All ${sessionExams.length} exams · ${selectedCls ? `${selectedCls.name}-${selectedCls.section}` : ""} · Multi-sheet Excel`}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Opens an authenticated API URL in a new tab (for PDFs) or triggers a download (for files).
// window.open("/api/...") doesn't send the Bearer token; this fetches with the token
// then creates a blob URL the browser can display or download.
function openAuthenticatedUrl(url, filename) {
  const token = localStorage.getItem("vrm_token");
  // For PDFs: open a blank window immediately (avoids popup blocker), then navigate it to the blob.
  const newWin = !filename ? window.open("", "_blank") : null;
  if (newWin) newWin.document.write("<html><body style='font-family:sans-serif;padding:40px'>Loading result card…</body></html>");

  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((res) => {
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      if (newWin) {
        newWin.location.href = objectUrl;
      } else {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    })
    .catch((e) => {
      if (newWin) newWin.close();
      alert("Error opening file: " + e.message);
    });
}

function ReportDownloadButton({ label, url, disabled, hint, filename, isPrintRoute }) {
  function handleClick() {
    if (!url) return;
    if (isPrintRoute) {
      window.open(url, "_blank");
    } else {
      openAuthenticatedUrl(url, filename);
    }
  }
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      background: disabled ? "var(--bg)" : "#EFF6FF",
      border: `1px solid ${disabled ? "var(--border)" : "#BFDBFE"}`,
      borderRadius: 12, padding: "16px 20px",
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, color: disabled ? "var(--muted)" : "var(--navy)" }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{hint}</div>}
      </div>
      <button
        className="btn"
        disabled={disabled}
        onClick={handleClick}
        style={{ flexShrink: 0, opacity: disabled ? 0.5 : 1 }}
      >
        {isPrintRoute ? "🖨 Open" : "⬇ Download"}
      </button>
    </div>
  );
}

/* ======================================================
   Admin — Term Attendance (Working Days & Present Days)
   ====================================================== */
/* ======================================================
   Admin Remarks
   ====================================================== */
function AdminRemarks() {
  const [classes, setClasses]   = useState([]);
  const [exams, setExams]       = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selExam, setSelExam]   = useState("");
  const [students, setStudents] = useState([]);
  const [remarks, setRemarks]   = useState({});   // { studentId: string }
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(null);  // studentId being saved
  const [savingAll, setSavingAll] = useState(false);
  const [saved, setSaved]       = useState({});    // { studentId: bool }
  const [sortBy, setSortBy]     = useState("roll");
  const [sortDir, setSortDir]   = useState("asc");
  const [search, setSearch]     = useState("");

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/school-admin/exams").then((r) => setExams(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selClass || !selExam) { setStudents([]); setRemarks({}); return; }
    setLoading(true); setSaved({});
    api.get("/school-admin/class-remarks", { params: { classSectionId: selClass, examId: selExam } })
      .then((r) => {
        setStudents(r.data);
        const m = {};
        r.data.forEach((s) => { m[s.id] = s.remark || ""; });
        setRemarks(m);
      })
      .catch((err) => alert(err.response?.data?.error || "Failed to load remarks."))
      .finally(() => setLoading(false));
  }, [selClass, selExam]);

  async function saveOne(studentId) {
    setSaving(studentId);
    try {
      await api.post("/school-admin/class-remarks", {
        studentId, examId: Number(selExam), remark: remarks[studentId] || "",
      });
      setSaved((p) => ({ ...p, [studentId]: true }));
      setTimeout(() => setSaved((p) => ({ ...p, [studentId]: false })), 2500);
    } catch (err) {
      alert(err.response?.data?.error || "Could not save remark.");
    } finally { setSaving(null); }
  }

  async function saveAll() {
    setSavingAll(true);
    for (const s of students) await saveOne(s.id);
    setSavingAll(false);
  }

  const dir = sortDir === "asc" ? 1 : -1;
  const displayed = students
    .filter((s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || String(s.rollNo ?? "").includes(search))
    .sort((a, b) =>
      sortBy === "name"
        ? dir * a.name.localeCompare(b.name)
        : dir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0))
    );

  const selClassObj = classes.find((c) => String(c.id) === String(selClass));
  const selExamObj  = exams.find((e) => String(e.id) === String(selExam));

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h2 className="page-title">Student Remarks</h2>
        <p className="page-subtitle">View and edit remarks for any class and exam. Remarks appear on result cards.</p>
      </div>

      {/* Selectors */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label className="form-label">Class / Section</label>
            <select className="form-control" value={selClass} onChange={(e) => setSelClass(e.target.value)}>
              <option value="">— Select Class —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Exam</label>
            <select className="form-control" value={selExam} onChange={(e) => setSelExam(e.target.value)}>
              <option value="">— Select Exam —</option>
              {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>Loading remarks…</div>}

      {!loading && (!selClass || !selExam) && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32, fontSize: 15 }}>
          Select a class and exam above to view and edit remarks.
        </div>
      )}

      {!loading && students.length === 0 && selClass && selExam && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>No students found.</div>
      )}

      {!loading && students.length > 0 && (
        <div className="card">
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                {selClassObj?.name}{selClassObj?.section ? `-${selClassObj.section}` : ""} &mdash; {selExamObj?.name}
                <span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>{students.length} students</span>
              </h3>
            </div>
            <button className="btn" onClick={saveAll} disabled={savingAll} style={{ fontWeight: 700 }}>
              {savingAll ? "Saving…" : "💾 Save All"}
            </button>
          </div>

          {/* Search + Sort */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
            <input
              type="text" placeholder="Search name or roll no…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 160, fontSize: 13, padding: "7px 12px", borderRadius: 10, border: "1px solid #E2E8F0" }}
            />
            <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Sort:</span>
            {[{ val: "roll", label: "Roll No" }, { val: "name", label: "A–Z" }].map(({ val, label }) => {
              const active = sortBy === val;
              return (
                <button key={val}
                  onClick={() => { if (sortBy === val) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(val); setSortDir("asc"); } }}
                  style={{
                    padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                    background: active ? "#EDE9FE" : "#F1F5F9", color: active ? "#6D28D9" : "#64748B",
                    border: `1px solid ${active ? "#C4B5FD" : "#E2E8F0"}`,
                  }}
                >
                  {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              );
            })}
          </div>

          {/* Remarks list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {displayed.map((s) => (
              <div key={s.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 0", borderBottom: "1px solid #F1F5F9", flexWrap: "wrap" }}>
                {/* Student info */}
                <div style={{ minWidth: 160, flexShrink: 0, paddingTop: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>
                    Roll: {s.rollNo ?? "—"}
                    {s.admissionNo && <span style={{ marginLeft: 8 }}>· {s.admissionNo}</span>}
                  </div>
                </div>
                {/* Textarea */}
                <div style={{ flex: 1, minWidth: 200 }}>
                  <textarea
                    rows={2}
                    value={remarks[s.id] ?? ""}
                    onChange={(e) => setRemarks((p) => ({ ...p, [s.id]: e.target.value }))}
                    placeholder="Write remark for this student (appears on result card)…"
                    style={{
                      width: "100%", fontSize: 13, resize: "vertical", borderRadius: 10,
                      borderColor: saved[s.id] ? "#6EE7B7" : undefined,
                      background: saved[s.id] ? "#F0FDF4" : undefined,
                      transition: "background .3s, border-color .3s",
                    }}
                  />
                </div>
                {/* Save button */}
                <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, paddingTop: 4 }}>
                  <button
                    className="btn sm"
                    onClick={() => saveOne(s.id)}
                    disabled={saving === s.id}
                    style={{ minWidth: 64 }}
                  >
                    {saving === s.id ? "…" : "Save"}
                  </button>
                  {saved[s.id] && <span style={{ fontSize: 11, color: "#16A34A", fontWeight: 600 }}>✓ Saved</span>}
                </div>
              </div>
            ))}
          </div>

          {displayed.length < students.length && (
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 10 }}>
              Showing {displayed.length} of {students.length} students
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Admin Term Attendance
   ====================================================== */
function AdminTermAttendance() {
  const [classes, setClasses]   = useState([]);
  const [sessions, setSessions] = useState([]);
  const [classId, setClassId]   = useState("");
  const [session, setSession]   = useState("");
  const [data, setData]         = useState(null);
  const [workingDays, setWorkingDays] = useState("");
  const [present, setPresent]   = useState({});   // { studentId: days }
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");
  const [loading, setLoading]   = useState(false);
  const [sortBy, setSortBy]     = useState("roll");
  const [sortDir, setSortDir]   = useState("asc");
  const [search, setSearch]     = useState("");

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/school-admin/sessions").then((r) => {
      setSessions(r.data);
      const cur = r.data.find((s) => s.is_current);
      if (cur) setSession(cur.name);
    }).catch(() => {});
  }, []);

  async function load() {
    if (!classId || !session) return;
    setLoading(true); setMsg("");
    try {
      const { data: d } = await api.get("/school-admin/attendance/term", { params: { classSectionId: classId, session } });
      setData(d);
      setWorkingDays(String(d.workingDays || ""));
      const p = {};
      d.students.forEach((s) => { p[s.id] = s.daysPresent !== null ? String(s.daysPresent) : ""; });
      setPresent(p);
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [classId, session]); // eslint-disable-line

  async function save() {
    const wd = Number(workingDays);
    if (!wd || wd < 0) { setMsg("Please enter valid working days."); return; }
    setSaving(true); setMsg("");
    try {
      const records = data.students.map((s) => ({
        studentId: s.id,
        daysPresent: present[s.id] !== "" ? Number(present[s.id]) : 0,
      }));
      await api.post("/school-admin/attendance/term", { classSectionId: classId, session, workingDays: wd, records });
      setMsg("Saved successfully.");
      load();
    } catch (err) { setMsg("Error: " + (err.response?.data?.error || "Could not save.")); }
    finally { setSaving(false); }
  }

  function fillAllPresent() {
    const p = {};
    data?.students.forEach((s) => { p[s.id] = workingDays; });
    setPresent(p);
  }

  const selClass = classes.find((c) => String(c.id) === String(classId));

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Term Attendance</h1>
        <p className="subtitle">
          Set total working days and days present per student for each session. Used on result cards.
          Class teachers can also update this from their dashboard.
        </p>
      </div>

      <div className="card">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" }}>
          <div className="form-field" style={{ margin: 0, minWidth: 180 }}>
            <label className="form-label">Class</label>
            <select value={classId} onChange={(e) => { setClassId(e.target.value); setData(null); }}>
              <option value="">— Select class —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.section}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0, minWidth: 160 }}>
            <label className="form-label">Session</label>
            <select value={session} onChange={(e) => { setSession(e.target.value); setData(null); }}>
              <option value="">— Select session —</option>
              {sessions.map((s) => <option key={s.id} value={s.name}>{s.name}{s.is_current ? " (Current)" : ""}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>Loading…</div>}

      {!loading && data && (
        <div className="card">
          {/* Class teacher info */}
          {data.classTeacher ? (
            <div style={{ background: "#EDE9FE", border: "1px solid #C4B5FD", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: "#6D28D9" }}>⭐ Class Teacher: </span>
              <span style={{ color: "#5B21B6" }}>{data.classTeacher.name}</span>
              {data.classTeacher.designation && <span style={{ color: "#7C3AED", marginLeft: 6 }}>({data.classTeacher.designation})</span>}
              <span style={{ color: "#8B5CF6", marginLeft: 8, fontSize: 12 }}>— can also edit this from their dashboard</span>
            </div>
          ) : (
            <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#92400E" }}>
              No class teacher assigned for {selClass?.name}. Go to <strong>Assignments</strong> → set a class teacher.
            </div>
          )}

          <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div className="form-field" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>Total Working Days *</label>
              <input
                type="number" min="0" max="365"
                value={workingDays}
                onChange={(e) => setWorkingDays(e.target.value)}
                style={{ width: 120, fontSize: 18, fontWeight: 700, textAlign: "center" }}
                placeholder="e.g. 220"
              />
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>Applies to all students in this class</div>
            </div>
            <button type="button" className="btn sm secondary" onClick={fillAllPresent} disabled={!workingDays}>
              Fill all present = Working days
            </button>
          </div>

          {msg && (
            <div style={{
              marginBottom: 16, padding: "10px 14px", borderRadius: 8, fontSize: 14,
              background: msg.startsWith("Error") ? "#FEF2F2" : "#F0FDF4",
              color: msg.startsWith("Error") ? "#DC2626" : "#166534",
              border: `1px solid ${msg.startsWith("Error") ? "#FECACA" : "#BBF7D0"}`,
            }}>{msg}</div>
          )}

          {data.students.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <div className="empty-state-text">No students in this class.</div>
            </div>
          ) : (() => {
            const attDir = sortDir === "asc" ? 1 : -1;
            const attStudents = data.students
              .filter((s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || String(s.rollNo ?? "").includes(search))
              .sort((a, b) =>
                sortBy === "name"
                  ? attDir * a.name.localeCompare(b.name)
                  : attDir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0))
              );
            return (
            <>
              {/* Search + Sort */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                <input
                  type="text" placeholder="Search name or roll no…" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ flex: 1, minWidth: 160, fontSize: 13, padding: "7px 12px", borderRadius: 10, border: "1px solid #E2E8F0" }}
                />
                <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Sort:</span>
                {[{ val: "roll", label: "Roll No" }, { val: "name", label: "A–Z" }].map(({ val, label }) => {
                  const active = sortBy === val;
                  return (
                    <button key={val}
                      onClick={() => { if (sortBy === val) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(val); setSortDir("asc"); } }}
                      style={{
                        padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                        background: active ? "#EDE9FE" : "#F1F5F9", color: active ? "#6D28D9" : "#64748B",
                        border: `1px solid ${active ? "#C4B5FD" : "#E2E8F0"}`,
                      }}
                    >
                      {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </button>
                  );
                })}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Roll No</th>
                      <th>Student Name</th>
                      <th style={{ width: 60 }}>Adm No</th>
                      <th style={{ width: 140 }}>Days Present</th>
                      <th style={{ width: 80, textAlign: "center" }}>% Attend.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attStudents.map((s) => {
                      const dp = Number(present[s.id]) || 0;
                      const wd = Number(workingDays) || 0;
                      const pct = wd > 0 ? Math.round((dp / wd) * 100) : null;
                      const pctColor = pct === null ? "var(--muted)" : pct >= 75 ? "#16A34A" : pct >= 60 ? "#D97706" : "#DC2626";
                      return (
                        <tr key={s.id}>
                          <td style={{ textAlign: "center", fontWeight: 700 }}>{s.rollNo}</td>
                          <td style={{ fontWeight: 600 }}>{s.name}</td>
                          <td style={{ fontSize: 12, color: "var(--muted)" }}>{s.admissionNo}</td>
                          <td>
                            <input
                              type="number" min="0" max={wd || 365}
                              value={present[s.id] ?? ""}
                              onChange={(e) => setPresent((p) => ({ ...p, [s.id]: e.target.value }))}
                              style={{ width: 90, textAlign: "center" }}
                              placeholder="0"
                            />
                            {wd > 0 && Number(present[s.id]) > wd && (
                              <span style={{ fontSize: 11, color: "#DC2626", marginLeft: 6 }}>exceeds working days</span>
                            )}
                          </td>
                          <td style={{ textAlign: "center", fontWeight: 700, color: pctColor }}>
                            {pct !== null ? `${pct}%` : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <button className="btn" onClick={save} disabled={saving || !workingDays}>
                  {saving ? "Saving…" : "Save Term Attendance"}
                </button>
                {attStudents.length < data.students.length && (
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>Showing {attStudents.length} of {data.students.length} students</span>
                )}
              </div>
            </>
            );
          })()
          }
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Classes & Subjects
   ====================================================== */
function ClassesSubjects() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classForm, setClassForm] = useState({ name: "", section: "" });
  const [classError, setClassError] = useState("");

  // Edit / delete class section
  const [editModal, setEditModal] = useState(null);
  const [editError, setEditError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // Quick "add section" inline
  const [addSectionFor, setAddSectionFor] = useState(null);
  const [newSection, setNewSection] = useState("");
  const [addSectionError, setAddSectionError] = useState("");

  // Subject form state
  const [subjTab, setSubjTab] = useState("scholastic"); // "scholastic" | "co_scholastic"
  const [subjClassName, setSubjClassName] = useState("");
  const [subjSections, setSubjSections] = useState([]);
  const [scholForm, setScholForm] = useState({ name: "", max_marks: 100, pass_marks: 33, isOptional: false, optional_group: "" });
  const [cosForm, setCosForm] = useState({ name: "", gradePreset: "5grade", customGrades: [{ grade: "", label: "" }] });
  const [subjectError, setSubjectError] = useState("");
  const [subjectMsg, setSubjectMsg] = useState("");

  // Subject edit / delete
  const [editSubjectModal, setEditSubjectModal] = useState(null); // { id, name, category, max_marks, pass_marks, grade_scale, customGrades }
  const [editSubjectError, setEditSubjectError] = useState("");
  const [deleteSubjectConfirm, setDeleteSubjectConfirm] = useState(null); // { id, name, markCount, force }
  const [deleteSubjectError, setDeleteSubjectError] = useState("");

  // Subject list filter
  const [filterClass, setFilterClass] = useState("");
  const [filterCat, setFilterCat] = useState("");

  // Optional subject assignments panel
  const [optClassSection, setOptClassSection] = useState("");
  const [optData, setOptData] = useState(null); // { groups, students, assignments }
  const [optDraft, setOptDraft] = useState({}); // { "studentId_group": subjectId }
  const [optSaving, setOptSaving] = useState(false);
  const [optMsg, setOptMsg] = useState("");

  function loadClasses() { api.get("/school-admin/classes").then((r) => setClasses(r.data)); }
  function loadSubjects() { api.get("/school-admin/subjects").then((r) => setSubjects(r.data)); }
  useEffect(() => { loadClasses(); loadSubjects(); }, []);

  async function loadOptionalData(classSectionId) {
    if (!classSectionId) { setOptData(null); return; }
    try {
      const r = await api.get(`/school-admin/subject-optionals?classSectionId=${classSectionId}`);
      setOptData(r.data);
      // Initialize draft from existing assignments
      const draft = {};
      r.data.assignments.forEach((a) => {
        draft[`${a.studentId}_${a.optionalGroup}`] = a.subjectId;
      });
      setOptDraft(draft);
      setOptMsg("");
    } catch { setOptData(null); }
  }

  // Derive ordered group list (sorted by display_order, already sorted by API)
  const grouped = {};
  classes.forEach((c) => { if (!grouped[c.name]) grouped[c.name] = []; grouped[c.name].push(c); });
  // groupOrder: unique names in the order they first appear (API already sorted by display_order)
  const groupOrder = [];
  classes.forEach((c) => { if (!groupOrder.includes(c.name)) groupOrder.push(c.name); });

  // ---- Class actions ----
  async function createClass(e) {
    e.preventDefault(); setClassError("");
    if (!classForm.name.trim()) return setClassError("Class name is required.");
    if (!classForm.section.trim()) return setClassError("Section is required.");
    try {
      await api.post("/school-admin/classes", { name: classForm.name.trim(), section: classForm.section.trim().toUpperCase() });
      setClassForm({ name: "", section: "" });
      loadClasses();
    } catch (err) { setClassError(err.response?.data?.error || "Failed to add class."); }
  }

  async function addSectionToClass(e) {
    e.preventDefault(); setAddSectionError("");
    if (!newSection.trim()) return setAddSectionError("Section is required.");
    try {
      await api.post("/school-admin/classes", { name: addSectionFor, section: newSection.trim().toUpperCase() });
      setAddSectionFor(null); setNewSection("");
      loadClasses();
    } catch (err) { setAddSectionError(err.response?.data?.error || "Failed to add section."); }
  }

  async function saveEdit(e) {
    e.preventDefault(); setEditError("");
    if (!editModal.name.trim() || !editModal.section.trim()) return setEditError("Both fields are required.");
    try {
      await api.put(`/school-admin/classes/${editModal.id}`, { name: editModal.name.trim(), section: editModal.section.trim().toUpperCase() });
      setEditModal(null); loadClasses();
    } catch (err) { setEditError(err.response?.data?.error || "Failed to update."); }
  }

  async function confirmDelete() {
    setDeleteError("");
    try {
      await api.delete(`/school-admin/classes/${deleteConfirm.id}`);
      setDeleteConfirm(null); loadClasses();
    } catch (err) { setDeleteError(err.response?.data?.error || "Failed to delete."); }
  }

  // Move group up/down — swap display_order values with adjacent group
  async function moveGroup(idx, dir) {
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= groupOrder.length) return;
    const a = groupOrder[idx];
    const b = groupOrder[swapIdx];
    const orderA = grouped[a][0].display_order;
    const orderB = grouped[b][0].display_order;
    // Swap: give a the order of b, give b the order of a (if equal, offset by 1)
    const newOrderA = orderB === orderA ? orderA + dir : orderB;
    const newOrderB = orderB === orderA ? orderB - dir : orderA;
    await api.put("/school-admin/classes/reorder", [
      { name: a, display_order: newOrderA },
      { name: b, display_order: newOrderB },
    ]);
    loadClasses();
  }

  // Grade presets for co-scholastic
  const GRADE_PRESETS = [
    { id: "5grade", label: "5-Grade Scale (A+ to D)", grades: [
      { grade: "A+", label: "Outstanding" }, { grade: "A", label: "Excellent" },
      { grade: "B",  label: "Good" },        { grade: "C", label: "Fair" },
      { grade: "D",  label: "Needs Improvement" },
    ]},
    { id: "4grade", label: "4-Grade Scale (A to D)", grades: [
      { grade: "A", label: "Excellent" }, { grade: "B", label: "Good" },
      { grade: "C", label: "Average" },   { grade: "D", label: "Below Average" },
    ]},
    { id: "3grade", label: "3-Grade Scale (A to C)", grades: [
      { grade: "A", label: "Excellent" }, { grade: "B", label: "Good" },
      { grade: "C", label: "Needs Improvement" },
    ]},
    { id: "sat", label: "Satisfactory / Not Satisfactory", grades: [
      { grade: "S", label: "Satisfactory" }, { grade: "NS", label: "Not Satisfactory" },
    ]},
    { id: "custom", label: "Custom Grade Scale", grades: [] },
  ];

  function getActiveGrades() {
    if (cosForm.gradePreset === "custom") return cosForm.customGrades.filter((g) => g.grade.trim());
    return GRADE_PRESETS.find((p) => p.id === cosForm.gradePreset)?.grades || [];
  }

  // Subject form helpers
  function handleSubjClassChange(name) {
    setSubjClassName(name);
    setSubjSections((grouped[name] || []).map((c) => c.id));
    setSubjectError(""); setSubjectMsg("");
  }

  function toggleSection(id) {
    setSubjSections((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function createSubjects(e) {
    e.preventDefault();
    setSubjectError(""); setSubjectMsg("");
    if (!subjClassName) return setSubjectError("Select a class first.");
    if (subjSections.length === 0) return setSubjectError("Select at least one section.");

    if (subjTab === "scholastic") {
      if (!scholForm.name.trim()) return setSubjectError("Subject name is required.");
      const payload = {
        class_section_ids: subjSections,
        name: scholForm.name.trim(),
        category: "scholastic",
        max_marks: Number(scholForm.max_marks) || 100,
        pass_marks: Number(scholForm.pass_marks) || 33,
        optional_group: (scholForm.isOptional && scholForm.optional_group.trim()) ? scholForm.optional_group.trim() : null,
      };
      try {
        const r = await api.post("/school-admin/subjects/bulk", payload);
        setSubjectMsg(`✓ "${scholForm.name}" added to ${r.data.count} section(s).`);
        setScholForm({ name: "", max_marks: 100, pass_marks: 33, isOptional: false, optional_group: "" });
        loadSubjects();
      } catch (err) { setSubjectError(err.response?.data?.error || "Failed to add subject."); }
    } else {
      if (!cosForm.name.trim()) return setSubjectError("Activity name is required.");
      const grades = getActiveGrades();
      if (grades.length === 0) return setSubjectError("Add at least one grade to the scale.");
      const payload = {
        class_section_ids: subjSections,
        name: cosForm.name.trim(),
        category: "co_scholastic",
        grade_scale: grades,
      };
      try {
        const r = await api.post("/school-admin/subjects/bulk", payload);
        setSubjectMsg(`✓ "${cosForm.name}" added to ${r.data.count} section(s).`);
        setCosForm({ name: "", gradePreset: "5grade", customGrades: [{ grade: "", label: "" }] });
        loadSubjects();
      } catch (err) { setSubjectError(err.response?.data?.error || "Failed to add activity."); }
    }
  }

  function openEditSubject(s) {
    let gs = s.grade_scale;
    if (typeof gs === "string") { try { gs = JSON.parse(gs); } catch { gs = []; } }
    if (!Array.isArray(gs)) gs = [];
    setEditSubjectModal({
      id: s.id,
      name: s.name,
      category: s.category,
      max_marks: s.max_marks ?? 100,
      pass_marks: s.pass_marks ?? 33,
      customGrades: gs.length > 0 ? gs : [{ grade: "", label: "" }],
    });
    setEditSubjectError("");
  }

  async function saveEditSubject(e) {
    e.preventDefault(); setEditSubjectError("");
    if (!editSubjectModal.name.trim()) return setEditSubjectError("Subject name is required.");
    try {
      let payload = { name: editSubjectModal.name.trim() };
      if (editSubjectModal.category === "scholastic") {
        payload.max_marks  = Number(editSubjectModal.max_marks)  || 100;
        payload.pass_marks = Number(editSubjectModal.pass_marks) || 33;
      } else {
        const grades = editSubjectModal.customGrades.filter((g) => g.grade.trim());
        if (grades.length === 0) return setEditSubjectError("Add at least one grade to the scale.");
        payload.grade_scale = grades;
      }
      await api.put(`/school-admin/subjects/${editSubjectModal.id}`, payload);
      setEditSubjectModal(null);
      loadSubjects();
    } catch (err) { setEditSubjectError(err.response?.data?.error || "Failed to update subject."); }
  }

  async function confirmDeleteSubject(force = false) {
    setDeleteSubjectError("");
    try {
      const url = `/school-admin/subjects/${deleteSubjectConfirm.id}${force ? "?force=true" : ""}`;
      await api.delete(url);
      setDeleteSubjectConfirm(null);
      loadSubjects();
    } catch (err) {
      const data = err.response?.data;
      if (data?.canForce) {
        // Server says marks exist — show force-delete confirmation
        setDeleteSubjectConfirm((prev) => ({ ...prev, markCount: data.markCount, force: true }));
        setDeleteSubjectError("");
      } else {
        setDeleteSubjectError(data?.error || "Failed to delete subject.");
      }
    }
  }

  // Filtered subject list
  let filteredSubjects = subjects;
  if (filterClass) filteredSubjects = filteredSubjects.filter((s) => classes.find((c) => c.id === s.class_section_id && c.name === filterClass));
  if (filterCat)   filteredSubjects = filteredSubjects.filter((s) => s.category === filterCat);

  const sectionsForSelectedClass = grouped[subjClassName] || [];

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Classes &amp; Subjects</h1>
        <p className="subtitle">Set up class sections, control their order, and add subjects across multiple sections at once.</p>
      </div>

      {/* ── Add New Class ── */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}>Add New Class</h3>
        {classError && <div className="error-text" style={{ marginBottom: 12 }}>{classError}</div>}
        <form onSubmit={createClass}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Class Name *</label>
              <input
                placeholder="e.g. Nursery, LKG, Class 8"
                value={classForm.name}
                onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                list="class-name-suggestions"
                required
              />
              <datalist id="class-name-suggestions">
                {groupOrder.map((n) => <option key={n} value={n} />)}
              </datalist>
            </div>
            <div className="form-field">
              <label className="form-label">Section *</label>
              <input
                placeholder="A"
                value={classForm.section}
                onChange={(e) => setClassForm({ ...classForm, section: e.target.value })}
                required
                style={{ maxWidth: 100 }}
              />
            </div>
          </div>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
            First section created for a new class gets auto-placed at the bottom of the order. Use ↑ ↓ to reorder. Add more sections via "+ Add Section" on each class.
          </p>
          <button className="btn" type="submit">Add Class</button>
        </form>
      </div>

      {/* ── Class Sections with ordering ── */}
      {groupOrder.length > 0 && (
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Class Sections</h3>
            <span className="text-muted" style={{ fontSize: 12 }}>Use ↑ ↓ to set the order in which classes appear in all dropdowns</span>
          </div>

          {groupOrder.map((groupName, idx) => (
            <div key={groupName} style={{ marginBottom: 16 }}>
              {/* Group header */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "var(--navy)", borderRadius: "var(--radius) var(--radius) 0 0",
                padding: "8px 14px",
              }}>
                {/* Order controls */}
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <button
                    title="Move Up"
                    disabled={idx === 0}
                    onClick={() => moveGroup(idx, -1)}
                    style={{
                      background: "none", border: "none", color: idx === 0 ? "rgba(255,255,255,0.3)" : "#fff",
                      cursor: idx === 0 ? "default" : "pointer", padding: "0 4px", lineHeight: 1, fontSize: 12,
                    }}
                  >▲</button>
                  <button
                    title="Move Down"
                    disabled={idx === groupOrder.length - 1}
                    onClick={() => moveGroup(idx, 1)}
                    style={{
                      background: "none", border: "none", color: idx === groupOrder.length - 1 ? "rgba(255,255,255,0.3)" : "#fff",
                      cursor: idx === groupOrder.length - 1 ? "default" : "pointer", padding: "0 4px", lineHeight: 1, fontSize: 12,
                    }}
                  >▼</button>
                </div>
                <span style={{ fontWeight: 700, fontSize: 14, color: "#fff", flex: 1 }}>
                  #{idx + 1} — {groupName}
                </span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                  {grouped[groupName].length} section{grouped[groupName].length !== 1 ? "s" : ""}
                </span>
                <button
                  className="btn sm"
                  style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
                  onClick={() => { setAddSectionFor(groupName); setNewSection(""); setAddSectionError(""); }}
                >
                  + Add Section
                </button>
              </div>

              {/* Quick-add section inline */}
              {addSectionFor === groupName && (
                <form onSubmit={addSectionToClass} style={{
                  display: "flex", gap: 8, alignItems: "center", padding: "8px 14px",
                  background: "#EFF6FF", border: "1px solid #BFDBFE", borderTop: "none",
                }}>
                  <span style={{ fontSize: 13, color: "var(--navy)", whiteSpace: "nowrap" }}>{groupName} —</span>
                  <input autoFocus placeholder="Section letter (e.g. B)" value={newSection}
                    onChange={(e) => setNewSection(e.target.value)} style={{ width: 160 }} required />
                  {addSectionError && <span className="error-text" style={{ fontSize: 12 }}>{addSectionError}</span>}
                  <button className="btn sm" type="submit">Add</button>
                  <button className="btn sm secondary" type="button" onClick={() => setAddSectionFor(null)}>Cancel</button>
                </form>
              )}

              {/* Sections rows */}
              <div style={{ border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 var(--radius) var(--radius)", overflow: "hidden" }}>
                <table style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>Section</th>
                      <th>Full Name</th>
                      <th style={{ width: 150 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[groupName].map((c) => (
                      <tr key={c.id}>
                        <td>
                          <span style={{
                            display: "inline-block", padding: "2px 10px", borderRadius: 20,
                            background: "#EFF6FF", color: "var(--blue)", fontWeight: 700, fontSize: 13,
                          }}>{c.section}</span>
                        </td>
                        <td style={{ fontWeight: 500 }}>{c.name}-{c.section}</td>
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button className="btn sm secondary"
                              onClick={() => { setEditModal({ id: c.id, name: c.name, section: c.section }); setEditError(""); }}>
                              Edit
                            </button>
                            <button className="btn sm danger"
                              onClick={() => { setDeleteConfirm({ id: c.id, label: `${c.name}-${c.section}` }); setDeleteError(""); }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Edit Section Modal ── */}
      {editModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit Class Section</strong>
              <button className="tpl-modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              <form onSubmit={saveEdit}>
                {editError && <div className="error-text" style={{ marginBottom: 12 }}>{editError}</div>}
                <div className="form-field">
                  <label className="form-label">Class Name *</label>
                  <input value={editModal.name}
                    onChange={(e) => setEditModal({ ...editModal, name: e.target.value })}
                    placeholder="e.g. Nursery, Class 8" required />
                </div>
                <div className="form-field">
                  <label className="form-label">Section *</label>
                  <input value={editModal.section}
                    onChange={(e) => setEditModal({ ...editModal, section: e.target.value })}
                    placeholder="e.g. A" required style={{ maxWidth: 120 }} />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn" type="submit">Save Changes</button>
                  <button className="btn secondary" type="button" onClick={() => setEditModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div className="tpl-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 400, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px" }}>Delete {deleteConfirm.label}?</h3>
            <p className="text-muted" style={{ marginBottom: 16 }}>
              Cannot delete if students or subjects are linked to this section.
            </p>
            {deleteError && <div className="error-text" style={{ marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn danger" onClick={confirmDelete}>Yes, Delete</button>
              <button className="btn secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Subject Modal ── */}
      {editSubjectModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditSubjectModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit {editSubjectModal.category === "scholastic" ? "Subject" : "Activity"}</strong>
              <button className="tpl-modal-close" onClick={() => setEditSubjectModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              <form onSubmit={saveEditSubject}>
                {editSubjectError && <div className="error-text" style={{ marginBottom: 12 }}>{editSubjectError}</div>}
                <div className="form-field">
                  <label className="form-label">{editSubjectModal.category === "scholastic" ? "Subject" : "Activity"} Name *</label>
                  <input
                    value={editSubjectModal.name}
                    onChange={(e) => setEditSubjectModal({ ...editSubjectModal, name: e.target.value })}
                    required
                  />
                </div>

                {editSubjectModal.category === "scholastic" && (
                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">Maximum Marks *</label>
                      <input type="number" min="1" max="1000"
                        value={editSubjectModal.max_marks}
                        onChange={(e) => setEditSubjectModal({ ...editSubjectModal, max_marks: e.target.value })}
                      />
                    </div>
                    <div className="form-field">
                      <label className="form-label">Minimum Pass Marks *</label>
                      <input type="number" min="0"
                        value={editSubjectModal.pass_marks}
                        onChange={(e) => setEditSubjectModal({ ...editSubjectModal, pass_marks: e.target.value })}
                      />
                      <span className="text-muted" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
                        {editSubjectModal.max_marks > 0
                          ? `${Math.round((editSubjectModal.pass_marks / editSubjectModal.max_marks) * 100)}% of max marks`
                          : ""}
                      </span>
                    </div>
                  </div>
                )}

                {editSubjectModal.category === "co_scholastic" && (
                  <div className="form-field">
                    <label className="form-label">Grade Scale *</label>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>Edit grades from highest to lowest:</div>
                    {editSubjectModal.customGrades.map((g, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                        <input
                          placeholder="Grade (e.g. A+)"
                          value={g.grade}
                          onChange={(e) => {
                            const upd = [...editSubjectModal.customGrades];
                            upd[i] = { ...upd[i], grade: e.target.value };
                            setEditSubjectModal({ ...editSubjectModal, customGrades: upd });
                          }}
                          style={{ width: 90 }}
                        />
                        <input
                          placeholder="Label (e.g. Outstanding)"
                          value={g.label}
                          onChange={(e) => {
                            const upd = [...editSubjectModal.customGrades];
                            upd[i] = { ...upd[i], label: e.target.value };
                            setEditSubjectModal({ ...editSubjectModal, customGrades: upd });
                          }}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => setEditSubjectModal({ ...editSubjectModal, customGrades: editSubjectModal.customGrades.filter((_, j) => j !== i) })}
                          style={{ background: "none", border: "1px solid var(--danger)", color: "var(--danger)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 13 }}
                          disabled={editSubjectModal.customGrades.length <= 1}
                        >✕</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn sm secondary"
                      style={{ marginTop: 4 }}
                      onClick={() => setEditSubjectModal({ ...editSubjectModal, customGrades: [...editSubjectModal.customGrades, { grade: "", label: "" }] })}
                    >+ Add Grade</button>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button className="btn" type="submit">Save Changes</button>
                  <button className="btn secondary" type="button" onClick={() => setEditSubjectModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Subject Confirm Modal ── */}
      {deleteSubjectConfirm && (
        <div className="tpl-modal-overlay" onClick={() => { setDeleteSubjectConfirm(null); setDeleteSubjectError(""); }}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 420, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px" }}>Delete "{deleteSubjectConfirm.name}"?</h3>
            {deleteSubjectConfirm.force ? (
              <>
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                  <p style={{ margin: 0, color: "#B91C1C", fontWeight: 600, fontSize: 14 }}>
                    ⚠️ Warning: {deleteSubjectConfirm.markCount} mark record(s) will also be permanently deleted.
                  </p>
                  <p style={{ margin: "6px 0 0", color: "#7F1D1D", fontSize: 13 }}>
                    This cannot be undone. All student marks for this subject will be lost.
                  </p>
                </div>
                {deleteSubjectError && <div className="error-text" style={{ marginBottom: 12 }}>{deleteSubjectError}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn danger" onClick={() => confirmDeleteSubject(true)}>Delete Subject &amp; All Marks</button>
                  <button className="btn secondary" onClick={() => { setDeleteSubjectConfirm(null); setDeleteSubjectError(""); }}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted" style={{ marginBottom: 16 }}>
                  This will permanently remove the subject and its teacher assignments.
                </p>
                {deleteSubjectError && <div className="error-text" style={{ marginBottom: 12 }}>{deleteSubjectError}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn danger" onClick={() => confirmDeleteSubject(false)}>Yes, Delete</button>
                  <button className="btn secondary" onClick={() => { setDeleteSubjectConfirm(null); setDeleteSubjectError(""); }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Add Subject / Co-Scholastic Activity ── */}
      <div className="card">
        {/* Type tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20, border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", width: "fit-content" }}>
          {[
            { id: "scholastic",    label: "📚 Scholastic Subject" },
            { id: "co_scholastic", label: "🎨 Co-Scholastic Activity" },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => { setSubjTab(id); setSubjectError(""); setSubjectMsg(""); }}
              style={{
                padding: "8px 20px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                background: subjTab === id ? "var(--navy)" : "#fff",
                color: subjTab === id ? "#fff" : "var(--muted)",
                transition: "all 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
          {subjTab === "scholastic"
            ? "Add a scholastic subject with max marks and pass marks. Select a class and sections — one submission adds the subject to all selected sections."
            : "Add a co-scholastic activity (Art, Music, Sports, etc.) with a grade scale. No marks — students are graded on the scale you define."}
        </p>

        {subjectError && <div className="error-text" style={{ marginBottom: 12 }}>{subjectError}</div>}
        {subjectMsg   && <div className="success-text" style={{ marginBottom: 12 }}>{subjectMsg}</div>}

        <form onSubmit={createSubjects}>
          {/* Step 1 — Class */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Step 1 — Select Class *</label>
            <select value={subjClassName} onChange={(e) => handleSubjClassChange(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">— Choose a class —</option>
              {groupOrder.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>

          {/* Step 2 — Sections */}
          {subjClassName && (
            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Step 2 — Select Sections *</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                <button type="button" className="btn sm secondary"
                  onClick={() => setSubjSections((grouped[subjClassName] || []).map((c) => c.id))}>
                  Select All
                </button>
                <button type="button" className="btn sm secondary" onClick={() => setSubjSections([])}>
                  Deselect All
                </button>
                {(grouped[subjClassName] || []).map((c) => {
                  const on = subjSections.includes(c.id);
                  return (
                    <label key={c.id} style={{
                      display: "flex", alignItems: "center", gap: 7, padding: "6px 14px",
                      border: `2px solid ${on ? "var(--blue)" : "var(--border)"}`,
                      borderRadius: 8, cursor: "pointer", userSelect: "none",
                      background: on ? "#EFF6FF" : "#fff", fontWeight: on ? 600 : 400,
                      color: on ? "var(--blue)" : "var(--text)", transition: "all 0.15s",
                    }}>
                      <input type="checkbox" checked={on} onChange={() => toggleSection(c.id)} style={{ display: "none" }} />
                      <span style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0, transition: "all 0.15s",
                        border: `2px solid ${on ? "var(--blue)" : "#CBD5E1"}`,
                        background: on ? "var(--blue)" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontSize: 10,
                      }}>{on && "✓"}</span>
                      {subjClassName}-{c.section}
                    </label>
                  );
                })}
              </div>
              {subjSections.length > 0 && (
                <p className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Will be added to {subjSections.length} section{subjSections.length !== 1 ? "s" : ""}.
                </p>
              )}
            </div>
          )}

          {/* Step 3 — Subject/Activity Details */}
          {subjClassName && subjTab === "scholastic" && (
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Step 3 — Subject Details *</label>
              <div style={{
                background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "16px 20px", marginTop: 8,
              }}>
                <div className="form-grid" style={{ marginBottom: 0 }}>
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Subject Name *</label>
                    <input
                      placeholder="e.g. Mathematics, English, Science, Hindi"
                      value={scholForm.name}
                      onChange={(e) => setScholForm({ ...scholForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Maximum Marks *</label>
                    <input type="number" min="1" max="1000"
                      value={scholForm.max_marks}
                      onChange={(e) => setScholForm({ ...scholForm, max_marks: e.target.value })}
                    />
                    <span className="text-muted" style={{ fontSize: 11, marginTop: 4, display: "block" }}>Full marks for this subject</span>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Minimum Pass Marks *</label>
                    <input type="number" min="0"
                      value={scholForm.pass_marks}
                      onChange={(e) => setScholForm({ ...scholForm, pass_marks: e.target.value })}
                    />
                    <span className="text-muted" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
                      {scholForm.max_marks > 0
                        ? `${Math.round((scholForm.pass_marks / scholForm.max_marks) * 100)}% of max marks`
                        : "Minimum marks to pass"}
                    </span>
                  </div>
                </div>

                {/* Optional subject toggle */}
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #BBF7D0" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
                    <input
                      type="checkbox"
                      checked={scholForm.isOptional}
                      onChange={(e) => setScholForm({ ...scholForm, isOptional: e.target.checked, optional_group: "" })}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                      Optional Subject (students choose between alternatives)
                    </span>
                  </label>
                  {scholForm.isOptional && (
                    <div style={{ marginTop: 10 }}>
                      <label className="form-label">Optional Group Name *</label>
                      <input
                        placeholder="e.g. Science Elective, Group 1"
                        value={scholForm.optional_group}
                        onChange={(e) => setScholForm({ ...scholForm, optional_group: e.target.value })}
                        style={{ maxWidth: 280 }}
                        required={scholForm.isOptional}
                      />
                      <span className="text-muted" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
                        Subjects with the same group name are treated as alternatives. Students pick exactly one.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {subjClassName && subjTab === "co_scholastic" && (
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Step 3 — Activity Details *</label>
              <div style={{
                background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10, padding: "16px 20px", marginTop: 8,
              }}>
                <div className="form-field" style={{ marginBottom: 16 }}>
                  <label className="form-label">Activity Name *</label>
                  <input
                    placeholder="e.g. Art & Craft, Music, Physical Education, Dance, Discipline"
                    value={cosForm.name}
                    onChange={(e) => setCosForm({ ...cosForm, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-field" style={{ marginBottom: cosForm.gradePreset !== "custom" ? 0 : 12 }}>
                  <label className="form-label">Grade Scale *</label>
                  <select
                    value={cosForm.gradePreset}
                    onChange={(e) => setCosForm({ ...cosForm, gradePreset: e.target.value, customGrades: [{ grade: "", label: "" }] })}
                    style={{ maxWidth: 320 }}
                  >
                    {GRADE_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>

                {/* Preview of selected preset */}
                {cosForm.gradePreset !== "custom" && (
                  <div style={{ marginTop: 12 }}>
                    <span className="text-muted" style={{ fontSize: 12 }}>Grade preview:</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                      {(GRADE_PRESETS.find((p) => p.id === cosForm.gradePreset)?.grades || []).map((g) => (
                        <div key={g.grade} style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "4px 12px",
                          background: "#fff", border: "1px solid #FED7AA", borderRadius: 20,
                          fontSize: 12,
                        }}>
                          <span style={{ fontWeight: 700, color: "#C2410C", minWidth: 24, textAlign: "center" }}>{g.grade}</span>
                          <span style={{ color: "var(--muted)" }}>—</span>
                          <span>{g.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom grade builder */}
                {cosForm.gradePreset === "custom" && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
                      Define your grades from highest to lowest:
                    </div>
                    {cosForm.customGrades.map((g, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                        <input
                          placeholder="Grade (e.g. A+)"
                          value={g.grade}
                          onChange={(e) => {
                            const upd = [...cosForm.customGrades];
                            upd[i] = { ...upd[i], grade: e.target.value };
                            setCosForm({ ...cosForm, customGrades: upd });
                          }}
                          style={{ width: 90 }}
                        />
                        <input
                          placeholder="Label (e.g. Outstanding)"
                          value={g.label}
                          onChange={(e) => {
                            const upd = [...cosForm.customGrades];
                            upd[i] = { ...upd[i], label: e.target.value };
                            setCosForm({ ...cosForm, customGrades: upd });
                          }}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          onClick={() => setCosForm({ ...cosForm, customGrades: cosForm.customGrades.filter((_, j) => j !== i) })}
                          style={{ background: "none", border: "1px solid var(--danger)", color: "var(--danger)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 13 }}
                          disabled={cosForm.customGrades.length <= 1}
                        >✕</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn sm secondary"
                      onClick={() => setCosForm({ ...cosForm, customGrades: [...cosForm.customGrades, { grade: "", label: "" }] })}
                    >
                      + Add Grade
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {subjClassName && (
            <button
              className="btn"
              type="submit"
              disabled={subjSections.length === 0 || (subjTab === "scholastic" ? !scholForm.name.trim() : !cosForm.name.trim())}
              style={{ background: subjTab === "co_scholastic" ? "#C2410C" : undefined }}
            >
              {subjTab === "scholastic" ? "📚" : "🎨"} Add to {subjSections.length} Section{subjSections.length !== 1 ? "s" : ""}
            </button>
          )}
        </form>
      </div>

      {/* ── Subject & Activity List ── */}
      {subjects.length > 0 && (
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Subjects &amp; Activities ({filteredSubjects.length} of {subjects.length})</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} style={{ width: "auto", minWidth: 160 }}>
                <option value="">All Classes</option>
                {groupOrder.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ width: "auto", minWidth: 160 }}>
                <option value="">All Types</option>
                <option value="scholastic">Scholastic Only</option>
                <option value="co_scholastic">Co-Scholastic Only</option>
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Class-Section</th>
                <th>Type</th>
                <th>Marks / Grade Scale</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubjects.map((s) => {
                const cls = classes.find((c) => c.id === s.class_section_id);
                const isScholastic = s.category === "scholastic";
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>
                      {s.name}
                      {s.optional_group && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 999, background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}>
                          Optional: {s.optional_group}
                        </span>
                      )}
                    </td>
                    <td>{cls ? `${cls.name}-${cls.section}` : "—"}</td>
                    <td>
                      {isScholastic
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", background: "#EFF6FF", color: "var(--blue)", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>📚 Scholastic</span>
                        : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", background: "#FFF7ED", color: "#C2410C", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>🎨 Co-Scholastic</span>
                      }
                    </td>
                    <td>
                      {isScholastic ? (
                        <span style={{ fontSize: 13 }}>
                          <strong>{s.max_marks}</strong>
                          <span className="text-muted"> max · </span>
                          <strong>{s.pass_marks}</strong>
                          <span className="text-muted"> pass ({s.max_marks > 0 ? Math.round((s.pass_marks / s.max_marks) * 100) : 0}%)</span>
                        </span>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {(() => {
                            let gs = s.grade_scale;
                            if (typeof gs === "string") { try { gs = JSON.parse(gs); } catch { gs = []; } }
                            if (!Array.isArray(gs)) gs = [];
                            if (gs.length === 0) return <span className="text-muted" style={{ fontSize: 12 }}>No grade scale set</span>;
                            return gs.map((g) => (
                              <span key={g.grade} title={g.label} style={{
                                padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                background: "#FFF7ED", color: "#C2410C", border: "1px solid #FED7AA",
                              }}>{g.grade}</span>
                            ));
                          })()}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn sm secondary"
                          onClick={() => openEditSubject(s)}>
                          Edit
                        </button>
                        <button className="btn sm danger"
                          onClick={() => { setDeleteSubjectConfirm({ id: s.id, name: s.name }); setDeleteSubjectError(""); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ── Optional Subject Assignments ── */}
      {subjects.some((s) => s.optional_group) && (
        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Optional Subject Assignments</h3>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
            Assign each student their chosen optional subject. Only students assigned to a subject appear in that subject's marks entry.
          </p>

          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Select Class Section</label>
            <select
              value={optClassSection}
              onChange={(e) => { setOptClassSection(e.target.value); loadOptionalData(e.target.value); }}
              style={{ maxWidth: 260 }}
            >
              <option value="">— Choose class section —</option>
              {classes.filter((c) => subjects.some((s) => s.class_section_id === c.id && s.optional_group)).map((c) => (
                <option key={c.id} value={c.id}>{c.name}-{c.section}</option>
              ))}
            </select>
          </div>

          {optMsg && <div className="success-text" style={{ marginBottom: 12 }}>{optMsg}</div>}

          {optData && Object.keys(optData.groups).length === 0 && (
            <p className="text-muted" style={{ fontSize: 13 }}>No optional subjects found for this class section.</p>
          )}

          {optData && Object.entries(optData.groups).map(([groupName, groupSubjects]) => (
            <div key={groupName} style={{ marginBottom: 24, border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ background: "#F8FAFC", padding: "10px 16px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#1E3A5F" }}>Optional Group: {groupName}</span>
                  <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                    Alternatives: {groupSubjects.map((s) => s.name).join(" · ")}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {groupSubjects.map((sub) => (
                    <button key={sub.id} type="button" className="btn sm secondary"
                      onClick={() => {
                        const newDraft = { ...optDraft };
                        optData.students.forEach((st) => { newDraft[`${st.id}_${groupName}`] = sub.id; });
                        setOptDraft(newDraft);
                      }}
                    >
                      Assign all → {sub.name}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "8px 14px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>Roll No.</th>
                      <th style={{ padding: "8px 14px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>Student Name</th>
                      {groupSubjects.map((sub) => (
                        <th key={sub.id} style={{ padding: "8px 14px", textAlign: "center", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>{sub.name}</th>
                      ))}
                      <th style={{ padding: "8px 14px", textAlign: "center", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>Clear</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optData.students.map((student, idx) => {
                      const chosen = optDraft[`${student.id}_${groupName}`];
                      return (
                        <tr key={student.id} style={{ background: idx % 2 === 0 ? "#F8FAFC" : "#fff" }}>
                          <td style={{ padding: "8px 14px", color: "#64748b", fontSize: 13 }}>{student.rollNo}</td>
                          <td style={{ padding: "8px 14px", fontWeight: 600, fontSize: 13 }}>{student.name}</td>
                          {groupSubjects.map((sub) => (
                            <td key={sub.id} style={{ padding: "8px 14px", textAlign: "center" }}>
                              <input
                                type="radio"
                                name={`opt_${student.id}_${groupName}`}
                                checked={chosen === sub.id}
                                onChange={() => setOptDraft({ ...optDraft, [`${student.id}_${groupName}`]: sub.id })}
                                style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#2563EB" }}
                              />
                            </td>
                          ))}
                          <td style={{ padding: "8px 14px", textAlign: "center" }}>
                            {chosen && (
                              <button type="button" className="btn sm danger"
                                onClick={() => { const d = { ...optDraft }; delete d[`${student.id}_${groupName}`]; setOptDraft(d); }}
                              >✕</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {optData && Object.keys(optData.groups).length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
              <button
                className="btn"
                disabled={optSaving}
                onClick={async () => {
                  setOptSaving(true); setOptMsg("");
                  try {
                    const records = Object.entries(optDraft).map(([key, subjectId]) => {
                      const [studentId, ...groupParts] = key.split("_");
                      const optionalGroup = groupParts.join("_");
                      return { studentId: Number(studentId), subjectId, optionalGroup };
                    }).filter((r) => r.subjectId);
                    if (records.length === 0) { setOptMsg("No assignments to save."); setOptSaving(false); return; }
                    await api.post("/school-admin/subject-optionals/bulk", records);
                    setOptMsg(`✓ Saved ${records.length} assignment(s).`);
                    loadOptionalData(optClassSection);
                  } catch (err) { setOptMsg("Failed to save assignments."); }
                  finally { setOptSaving(false); }
                }}
              >
                {optSaving ? "Saving…" : "Save Assignments"}
              </button>
              <span className="text-muted" style={{ fontSize: 12 }}>
                {Object.keys(optDraft).length} assignment{Object.keys(optDraft).length !== 1 ? "s" : ""} in draft
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Students
   ====================================================== */
// All student fields definition — used in form, CSV, and result-settings
const STUDENT_FIELDS = [
  { key: "name",         label: "Student Name",   type: "text",   alwaysRequired: true,  csvKey: "name" },
  { key: "admission_no", label: "Admission No.",  type: "text",   alwaysRequired: true,  csvKey: "admission_no" },
  { key: "roll_no",      label: "Roll No.",        type: "text",   alwaysRequired: true,  csvKey: "roll_no" },
  { key: "dob",          label: "Date of Birth",  type: "date",   alwaysRequired: false, csvKey: "dob" },
  { key: "gender",       label: "Gender",          type: "select", alwaysRequired: false, csvKey: "gender" },
  { key: "mobile",       label: "Mobile Number",  type: "tel",    alwaysRequired: false, csvKey: "mobile" },
  { key: "father_name",  label: "Father Name",    type: "text",   alwaysRequired: false, csvKey: "father_name" },
  { key: "mother_name",  label: "Mother Name",    type: "text",   alwaysRequired: false, csvKey: "mother_name" },
  { key: "email",        label: "Email",           type: "email",  alwaysRequired: false, csvKey: "email" },
  { key: "height",       label: "Height",          type: "text",   alwaysRequired: false, csvKey: "height", placeholder: "e.g. 145 cm" },
  { key: "weight",       label: "Weight",          type: "text",   alwaysRequired: false, csvKey: "weight", placeholder: "e.g. 35 kg" },
  { key: "photo",        label: "Photo",           type: "photo",  alwaysRequired: false, csvKey: null },
];

const CARD_SELECTABLE_FIELDS = STUDENT_FIELDS.filter((f) => f.key !== "photo"); // photo handled separately

// Format a YYYY-MM-DD date string as DD/MM/YYYY without timezone shifting
function formatDob(dob) {
  if (!dob) return "—";
  const parts = String(dob).slice(0, 10).split("-");
  if (parts.length !== 3) return dob;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

// Normalise date to YYYY-MM-DD; returns null for blank / unparseable values
function normalizeDate(val) {
  if (!val || !val.trim()) return null;
  const v = val.trim();
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  // DD/MM/YYYY or DD-MM-YYYY (Indian formats)
  const m = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // YYYY/MM/DD
  const m2 = v.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  return null; // unparseable — send null so DB ignores it
}

function parseCSV(text) {
  const lines = text.replace(/\r/g, "").trim().split("\n").filter((l) => l.trim());
  if (lines.length < 2) return { error: "CSV must have a header row and at least one data row." };

  // Strip annotations like " (required)" / " (optional)" added by sample download
  const rawHeaders = lines[0].split(",").map((h) =>
    h.trim().toLowerCase().replace(/\s*\(required\)|\s*\(optional\)/g, "").replace(/\s+/g, "_")
  );
  const required = ["admission_no", "roll_no", "name"];
  const missing = required.filter((r) => !rawHeaders.includes(r));
  if (missing.length > 0) return { error: `Missing required columns: ${missing.join(", ")}` };

  const rows = [];
  const errors = [];
  for (let i = 1; i < lines.length; i++) {
    // Handle quoted fields with commas inside
    const values = [];
    let cur = "", inQ = false;
    for (const ch of lines[i]) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { values.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    values.push(cur.trim());

    if (values.every((v) => !v)) continue;
    const row = {};
    rawHeaders.forEach((h, idx) => { row[h] = values[idx] || ""; });

    if (!row.admission_no || !row.roll_no || !row.name) {
      errors.push(`Row ${i}: admission_no, roll_no, and name are required.`);
      continue;
    }

    // Normalise dob — empty string or bad format must become null, not ""
    if ("dob" in row) row.dob = normalizeDate(row.dob);

    // Normalise gender to exact enum value; anything unrecognised → null
    if ("gender" in row) {
      const gMap = { male: "Male", female: "Female", other: "Other", m: "Male", f: "Female", o: "Other" };
      row.gender = gMap[row.gender.toLowerCase().trim()] || null;
    }

    // Null-out other empty optional strings so backend ENUM/DATE columns don't choke
    ["mobile", "father_name", "mother_name", "email", "height", "weight"].forEach((f) => {
      if (f in row && row[f] === "") row[f] = null;
    });

    rows.push(row);
  }
  return { rows, errors };
}

function downloadSampleCSV(className, section, cardFields) {
  const label = className && section ? `${className}-${section}` : "Class-Section";
  const required = new Set([
    ...STUDENT_FIELDS.filter((f) => f.alwaysRequired).map((f) => f.key),
    ...(cardFields || []),
  ]);

  // All fields that can be in CSV (exclude photo)
  const csvFields = STUDENT_FIELDS.filter((f) => f.csvKey);

  // Header row: mark required fields with * so user knows which are mandatory
  const headerRow = csvFields.map((f) => required.has(f.key) ? `${f.csvKey} (required)` : f.csvKey).join(",");

  const sampleData = [
    {
      name: "Rahul Sharma",
      admission_no: "ADM-001",
      roll_no: "01",
      dob: "2010-05-15",
      gender: "Male",
      mobile: "9876543210",
      father_name: "Rajesh Sharma",
      mother_name: "Sunita Sharma",
      email: "rahul@example.com",
      height: "145 cm",
      weight: "35 kg",
    },
    {
      name: "Priya Patel",
      admission_no: "ADM-002",
      roll_no: "02",
      dob: "2010-08-22",
      gender: "Female",
      mobile: "9876543211",
      father_name: "Mukesh Patel",
      mother_name: "Neha Patel",
      email: "priya@example.com",
      height: "140 cm",
      weight: "32 kg",
    },
    {
      name: "Anjali Verma",
      admission_no: "ADM-003",
      roll_no: "03",
      dob: "2011-01-10",
      gender: "Female",
      mobile: "9876543212",
      father_name: "Suresh Verma",
      mother_name: "Kavita Verma",
      email: "anjali@example.com",
      height: "138 cm",
      weight: "30 kg",
    },
  ];

  const rows = sampleData.map((s) =>
    csvFields.map((f) => {
      const val = s[f.csvKey] || "";
      // Wrap in quotes if value contains comma
      return val.includes(",") ? `"${val}"` : val;
    }).join(",")
  );

  const csv = [headerRow, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `students_sample_${label}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function StudentFieldInput({ field, value, onChange, required }) {
  if (field.type === "select") return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} required={required}>
      <option value="">— Select —</option>
      <option value="Male">Male</option>
      <option value="Female">Female</option>
      <option value="Other">Other</option>
    </select>
  );
  if (field.type === "photo") return (
    <div>
      {value && <img src={value} alt="student" style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover", marginBottom: 6, display: "block" }} />}
      <input type="file" accept="image/*" onChange={(e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 500 * 1024) { alert("Photo must be under 500 KB."); return; }
        const reader = new FileReader();
        reader.onload = (ev) => onChange(ev.target.result);
        reader.readAsDataURL(file);
      }} style={{ padding: "4px 0" }} />
      <div className="text-muted" style={{ fontSize: 11, marginTop: 3 }}>Max 500 KB · JPG/PNG</div>
    </div>
  );
  return (
    <input
      type={field.type}
      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
    />
  );
}

function Students() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [cardFields, setCardFields] = useState(["name","admission_no","roll_no","dob","father_name","mobile"]);

  // Manual add form
  const [addTab, setAddTab] = useState("manual"); // "manual" | "csv"
  const EMPTY_FORM = () => Object.fromEntries(STUDENT_FIELDS.map((f) => [f.key, ""]));
  const [form, setForm] = useState(EMPTY_FORM());
  const [formError, setFormError] = useState("");
  const [formMsg, setFormMsg] = useState("");

  // CSV upload
  const [csvRows, setCsvRows]   = useState(null); // parsed rows preview
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvFile, setCsvFile]   = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvMsg, setCsvMsg]     = useState("");

  // Delete (single)
  const [deleteConfirm, setDeleteConfirm] = useState(null); // student object
  const [deleteError, setDeleteError]     = useState("");

  // Bulk selection & delete
  const [selected, setSelected] = useState(new Set()); // Set of student IDs
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Edit
  const [editModal, setEditModal]   = useState(null);
  const [editError, setEditError]   = useState("");

  // Print
  const [selectedExamId, setSelectedExamId] = useState("");

  // Search & Sort
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("roll"); // "roll" | "name"

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data));
    api.get("/school-admin/exams").then((r) => setExams(r.data));
    api.get("/school-admin/result-config").then((r) => {
      let fields = r.data?.student_card_fields;
      if (typeof fields === "string") { try { fields = JSON.parse(fields); } catch { fields = null; } }
      if (Array.isArray(fields) && fields.length) setCardFields(fields);
    }).catch(() => {});
  }, []);

  function loadStudents(cid) {
    const id = cid !== undefined ? cid : selectedClass;
    if (id) api.get(`/school-admin/students?classSectionId=${id}`).then((r) => setStudents(r.data));
    else setStudents([]);
  }

  useEffect(() => { loadStudents(); }, [selectedClass]); // eslint-disable-line

  async function createStudent(e) {
    e.preventDefault(); setFormError(""); setFormMsg("");
    try {
      await api.post("/school-admin/students", { ...form, class_section_id: selectedClass });
      setForm(EMPTY_FORM());
      setFormMsg("Student added successfully.");
      loadStudents();
      setTimeout(() => setFormMsg(""), 3000);
    } catch (err) { setFormError(err.response?.data?.error || "Failed to add student."); }
  }

  function handleCSVFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file); setCsvMsg(""); setCsvRows(null); setCsvErrors([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = parseCSV(ev.target.result);
      if (result.error) { setCsvErrors([result.error]); setCsvRows(null); }
      else { setCsvRows(result.rows); setCsvErrors(result.errors); }
    };
    reader.readAsText(file);
  }

  async function importCSV() {
    if (!csvRows || csvRows.length === 0) return;
    setCsvUploading(true); setCsvMsg("");
    try {
      const students = csvRows.map((r) => ({ ...r, class_section_id: selectedClass }));
      const res = await api.post("/school-admin/students/bulk", { students });
      setCsvMsg(`✓ ${res.data.count} students imported successfully.`);
      setCsvRows(null); setCsvErrors([]); setCsvFile(null);
      loadStudents();
    } catch (err) {
      setCsvMsg("Import failed: " + (err.response?.data?.error || "Unknown error."));
    } finally {
      setCsvUploading(false);
    }
  }

  async function saveEdit(e) {
    e.preventDefault(); setEditError("");
    try {
      const payload = { class_section_id: editModal.class_section_id };
      STUDENT_FIELDS.forEach((f) => { if (f.key in editModal) payload[f.key] = editModal[f.key]; });
      await api.put(`/school-admin/students/${editModal.id}`, payload);
      setEditModal(null);
      loadStudents();
    } catch (err) { setEditError(err.response?.data?.error || "Failed to update."); }
  }

  async function confirmDelete() {
    setDeleteError("");
    try {
      await api.delete(`/school-admin/students/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      loadStudents();
    } catch (err) { setDeleteError(err.response?.data?.error || "Failed to delete."); }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} selected student(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await api.delete("/school-admin/students/bulk", { data: { ids: Array.from(selected) } });
      setSelected(new Set());
      loadStudents();
    } catch (err) {
      alert(err.response?.data?.error || "Bulk delete failed.");
    } finally {
      setBulkDeleting(false);
    }
  }

  function downloadExcel() {
    const cls = classes.find((c) => String(c.id) === String(selectedClass));
    const label = cls ? `${cls.name}-${cls.section}` : "Students";
    const headers = ["Roll No","Admission No","Name","Gender","Date of Birth","Mobile","Father Name","Mother Name","Email","Height","Weight"];
    const rows = filteredStudents.map((s) => [
      s.roll_no || "",
      s.admission_no || "",
      s.name || "",
      s.gender || "",
      s.dob ? formatDob(s.dob) : "",
      s.mobile || "",
      s.father_name || "",
      s.mother_name || "",
      s.email || "",
      s.height || "",
      s.weight || "",
    ]);
    // UTF-8 BOM so Excel opens it correctly
    const csv = "\uFEFF" + [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `Students_${label}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  const selectedCls = classes.find((c) => String(c.id) === String(selectedClass));
  const filteredStudents = (() => {
    let list = search.trim()
      ? students.filter((s) =>
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          s.admission_no?.toLowerCase().includes(search.toLowerCase()) ||
          s.roll_no?.toLowerCase().includes(search.toLowerCase())
        )
      : [...students];
    if (sortBy === "name") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      // Sort by roll number numerically if possible, else lexically
      list.sort((a, b) => {
        const ra = parseInt(a.roll_no) || 0, rb = parseInt(b.roll_no) || 0;
        return ra !== rb ? ra - rb : (a.roll_no || "").localeCompare(b.roll_no || "");
      });
    }
    return list;
  })();

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Students</h1>
        <p className="subtitle">Manage student enrolment. Add individually or import in bulk via CSV.</p>
      </div>

      {/* Class selector */}
      <div className="card">
        <div className="form-field" style={{ maxWidth: 340 }}>
          <label className="form-label">Select Class / Section *</label>
          <select value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); setSearch(""); setSelected(new Set()); }}>
            <option value="">— Choose a class section —</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
        </div>
      </div>

      {selectedClass && (
        <>
          {/* Add student tabs */}
          <div className="card">
            <div style={{ display: "flex", gap: 0, marginBottom: 20, border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", width: "fit-content" }}>
              {[{ id: "manual", label: "✏️ Add Manually" }, { id: "csv", label: "📄 Import CSV" }].map(({ id, label }) => (
                <button key={id} type="button"
                  onClick={() => { setAddTab(id); setFormError(""); setFormMsg(""); setCsvMsg(""); setCsvRows(null); }}
                  style={{
                    padding: "8px 20px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                    background: addTab === id ? "var(--navy)" : "#fff",
                    color: addTab === id ? "#fff" : "var(--muted)",
                  }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Manual add form */}
            {addTab === "manual" && (
              <>
                <h3 style={{ marginBottom: 4 }}>
                  Add Student to {selectedCls ? `${selectedCls.name}-${selectedCls.section}` : ""}
                </h3>
                <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
                  Fields marked * are required. Fields marked ✦ are required on the report card (configured in Result Settings).
                </p>
                {formError && <div className="error-text" style={{ marginBottom: 12 }}>{formError}</div>}
                {formMsg   && <div className="success-text" style={{ marginBottom: 12 }}>✓ {formMsg}</div>}
                <form onSubmit={createStudent}>
                  <div className="form-grid">
                    {STUDENT_FIELDS.map((field) => {
                      const isRequired = field.alwaysRequired || cardFields.includes(field.key);
                      const isCardField = !field.alwaysRequired && cardFields.includes(field.key);
                      return (
                        <div key={field.key} className={`form-field${field.type === "photo" ? " span-2" : ""}`}>
                          <label className="form-label">
                            {field.label}
                            {field.alwaysRequired && " *"}
                            {isCardField && " ✦"}
                          </label>
                          <StudentFieldInput
                            field={field}
                            value={form[field.key]}
                            onChange={(v) => setForm({ ...form, [field.key]: v })}
                            required={isRequired}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <button className="btn" type="submit" style={{ marginTop: 8 }}>Add Student</button>
                </form>
              </>
            )}

            {/* CSV import */}
            {addTab === "csv" && (
              <>
                <h3 style={{ marginBottom: 4 }}>
                  Import Students via CSV — {selectedCls ? `${selectedCls.name}-${selectedCls.section}` : ""}
                </h3>
                <p className="text-muted" style={{ fontSize: 13, marginBottom: 20 }}>
                  Upload a CSV file with student details. Required columns: <strong>admission_no, roll_no, name</strong>{cardFields.filter((f) => !["admission_no","roll_no","name"].includes(f)).length > 0 ? ` + report card fields: ${cardFields.filter((f) => !["admission_no","roll_no","name"].includes(f) && f !== "photo").join(", ")}` : ""}. Download the sample to get the exact column layout.
                </p>

                {/* Download sample */}
                <div style={{
                  background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10,
                  padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--navy)", marginBottom: 4 }}>Download Sample CSV</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                      Get a template with the correct columns and 3 sample rows to fill in.
                    </div>
                  </div>
                  <button
                    className="btn secondary"
                    onClick={() => downloadSampleCSV(selectedCls?.name, selectedCls?.section, cardFields)}
                  >
                    ⬇ Download Sample
                  </button>
                </div>

                {/* File upload */}
                <div className="form-field" style={{ marginBottom: 16 }}>
                  <label className="form-label">Select CSV File</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCSVFile}
                    style={{ padding: "6px 0" }}
                  />
                </div>

                {/* Parse errors */}
                {csvErrors.length > 0 && (
                  <div style={{ background: "var(--danger-bg)", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--danger)", marginBottom: 6 }}>
                      {csvErrors.length} row(s) skipped:
                    </div>
                    {csvErrors.map((e, i) => <div key={i} style={{ fontSize: 12, color: "var(--danger)" }}>{e}</div>)}
                  </div>
                )}

                {/* Preview table */}
                {csvRows && csvRows.length > 0 && (() => {
                  // Determine columns from the parsed rows (use STUDENT_FIELDS for label lookup, exclude photo)
                  const csvColKeys = Object.keys(csvRows[0]).filter((k) => k !== "photo");
                  const colLabels = csvColKeys.map((k) => {
                    const f = STUDENT_FIELDS.find((sf) => sf.csvKey === k);
                    return f ? f.label : k;
                  });
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--navy)" }}>
                        Preview — {csvRows.length} student{csvRows.length !== 1 ? "s" : ""} ready to import:
                      </div>
                      <div style={{ overflowX: "auto", maxHeight: 300, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
                        <table style={{ margin: 0, fontSize: 12 }}>
                          <thead style={{ position: "sticky", top: 0 }}>
                            <tr>
                              <th>#</th>
                              {colLabels.map((lbl, i) => <th key={i}>{lbl}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {csvRows.map((r, i) => (
                              <tr key={i}>
                                <td style={{ color: "var(--muted)" }}>{i + 1}</td>
                                {csvColKeys.map((k) => (
                                  <td key={k} style={k === "name" ? { fontWeight: 600 } : {}}>
                                    {r[k] || "—"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {csvMsg && (
                  <div className={csvMsg.startsWith("✓") ? "success-text" : "error-text"} style={{ marginBottom: 12 }}>
                    {csvMsg}
                  </div>
                )}

                {csvRows && csvRows.length > 0 && (
                  <button className="btn" onClick={importCSV} disabled={csvUploading}>
                    {csvUploading ? "Importing…" : `Import ${csvRows.length} Students`}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Student list */}
          <div className="card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h3 style={{ margin: 0 }}>
                  {selectedCls ? `${selectedCls.name}-${selectedCls.section}` : "Students"} &nbsp;
                  <span className="badge info">{students.length}</span>
                </h3>
                <button className="btn sm secondary" onClick={downloadExcel} disabled={filteredStudents.length === 0} title="Download as Excel">
                  ⬇ Export Excel
                </button>
                {selected.size > 0 && (
                  <button className="btn sm danger" onClick={bulkDelete} disabled={bulkDeleting}>
                    {bulkDeleting ? "Deleting…" : `🗑 Delete Selected (${selected.size})`}
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  placeholder="Search name / admission / roll..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 220, fontSize: 13 }}
                />
                {/* Sort toggle */}
                <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                  {[{ key: "roll", label: "Roll No" }, { key: "name", label: "A–Z" }].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSortBy(key)}
                      style={{
                        padding: "5px 12px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer",
                        background: sortBy === key ? "var(--primary)" : "#fff",
                        color: sortBy === key ? "#fff" : "var(--muted)",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <select value={selectedExamId} onChange={(e) => setSelectedExamId(e.target.value)} style={{ width: "auto", fontSize: 13 }}>
                  <option value="">Select exam for PDF</option>
                  {exams.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
              </div>
            </div>

            {filteredStudents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">👥</div>
                <div className="empty-state-text">
                  {students.length === 0 ? "No students enrolled yet. Add one above or import via CSV." : "No students match your search."}
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          title="Select all"
                          checked={filteredStudents.length > 0 && filteredStudents.every((s) => selected.has(s.id))}
                          onChange={(e) => {
                            if (e.target.checked) setSelected(new Set(filteredStudents.map((s) => s.id)));
                            else setSelected(new Set());
                          }}
                        />
                      </th>
                      <th>Photo</th>
                      <th>Roll No</th>
                      <th>Admission No</th>
                      <th>Name</th>
                      <th>Gender</th>
                      <th>DOB</th>
                      <th>Mobile</th>
                      <th>Father</th>
                      <th>Mother</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((s) => (
                      <tr key={s.id} style={{ background: selected.has(s.id) ? "var(--primary-light, #EFF6FF)" : undefined }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(s.id)}
                            onChange={(e) => {
                              const next = new Set(selected);
                              if (e.target.checked) next.add(s.id); else next.delete(s.id);
                              setSelected(next);
                            }}
                          />
                        </td>
                        <td>
                          {s.photo
                            ? <img src={s.photo} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover" }} />
                            : <div style={{ width: 36, height: 36, borderRadius: 6, background: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>👤</div>
                          }
                        </td>
                        <td style={{ fontWeight: 600 }}>{s.roll_no}</td>
                        <td style={{ color: "var(--muted)", fontSize: 13 }}>{s.admission_no}</td>
                        <td><strong>{s.name}</strong></td>
                        <td style={{ fontSize: 13 }}>{s.gender || "—"}</td>
                        <td style={{ fontSize: 13 }}>{s.dob ? formatDob(s.dob) : "—"}</td>
                        <td style={{ fontSize: 13 }}>{s.mobile || "—"}</td>
                        <td style={{ fontSize: 13 }}>{s.father_name || "—"}</td>
                        <td style={{ fontSize: 13 }}>{s.mother_name || "—"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button className="btn sm secondary"
                              onClick={() => { setEditModal({ ...s }); setEditError(""); }}>
                              Edit
                            </button>
                            {selectedExamId && (
                              <button className="btn sm secondary"
                                onClick={() => window.open(`/print/student/${s.id}/exam/${selectedExamId}`, "_blank")}>
                                PDF
                              </button>
                            )}
                            <button className="btn sm danger"
                              onClick={() => { setDeleteConfirm(s); setDeleteError(""); }}>
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit Student — {editModal.name}</strong>
              <button className="tpl-modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {editError && <div className="error-text" style={{ marginBottom: 12 }}>{editError}</div>}
              <form onSubmit={saveEdit}>
                <div className="form-field" style={{ marginBottom: 16 }}>
                  <label className="form-label">Class / Section (Transfer)</label>
                  <select
                    value={editModal.class_section_id || ""}
                    onChange={(e) => setEditModal({ ...editModal, class_section_id: e.target.value })}
                  >
                    <option value="">— Keep current class —</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
                  </select>
                </div>
                <div className="form-grid">
                  {STUDENT_FIELDS.map((field) => {
                    const isRequired = field.alwaysRequired || cardFields.includes(field.key);
                    const isCardField = !field.alwaysRequired && cardFields.includes(field.key);
                    // Normalise date value: strip time part
                    const rawVal = editModal[field.key] ?? "";
                    const val = field.type === "date" && rawVal ? rawVal.split("T")[0] : rawVal;
                    return (
                      <div key={field.key} className={`form-field${field.type === "photo" ? " span-2" : ""}`}>
                        <label className="form-label">
                          {field.label}
                          {field.alwaysRequired && " *"}
                          {isCardField && " ✦"}
                        </label>
                        <StudentFieldInput
                          field={{ ...field, type: field.type === "date" ? "date" : field.type }}
                          value={val}
                          onChange={(v) => setEditModal({ ...editModal, [field.key]: v })}
                          required={isRequired}
                        />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className="btn" type="submit">Save Changes</button>
                  <button className="btn secondary" type="button" onClick={() => setEditModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="tpl-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 420, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 10px" }}>Remove Student?</h3>
            <p style={{ marginBottom: 6 }}>
              <strong>{deleteConfirm.name}</strong> ({deleteConfirm.admission_no})
            </p>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
              The student will be removed from this class. Their existing marks and result data are preserved.
            </p>
            {deleteError && <div className="error-text" style={{ marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn danger" onClick={confirmDelete}>Yes, Remove</button>
              <button className="btn secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Teachers
   ====================================================== */
const EMPTY_TEACHER_FORM = () => ({
  name: "", email: "", mobile: "", address: "", qualification: "", designation: "", loginType: "auto",
});

// 3-dot settings dropdown for each teacher row
function TeacherRowMenu({ teacher, onEdit, onResetPwd, onToggleActive, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const Item = ({ label, color, onClick }) => (
    <button
      type="button"
      onClick={() => { setOpen(false); onClick(); }}
      style={{
        display: "block", width: "100%", textAlign: "left", padding: "9px 16px",
        background: "none", border: "none", cursor: "pointer", fontSize: 13,
        color: color || "var(--text)", borderBottom: "1px solid var(--border)",
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "none"}
    >
      {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="btn sm secondary"
        onClick={() => setOpen((o) => !o)}
        title="Settings"
        style={{ padding: "4px 10px", fontSize: 16, lineHeight: 1 }}
      >
        ⚙
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 200,
          background: "#fff", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "var(--shadow-md)", minWidth: 180, overflow: "hidden",
        }}>
          <Item label="✏️  Edit Details" onClick={onEdit} />
          <Item label="🔑  Reset Password" onClick={onResetPwd} />
          <Item
            label={teacher.is_active ? "⏸  Deactivate Account" : "▶  Activate Account"}
            color={teacher.is_active ? "#B45309" : "#065F46"}
            onClick={onToggleActive}
          />
          <Item label="🗑  Delete Account" color="#DC2626" onClick={onDelete} />
        </div>
      )}
    </div>
  );
}

function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_TEACHER_FORM());
  const [formError, setFormError] = useState("");
  const [addedCreds, setAddedCreds] = useState(null); // { username, tempPassword }

  // Edit modal
  const [editModal, setEditModal] = useState(null);
  const [editError, setEditError] = useState("");

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // Reset password modal
  const [resetModal, setResetModal] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetNotify, setResetNotify] = useState(true);
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState("");

  // CSV upload
  const [csvRows, setCsvRows]         = useState(null);   // parsed preview rows
  const [csvError, setCsvError]       = useState("");
  const [csvResults, setCsvResults]   = useState(null);   // bulk create results
  const [csvLoading, setCsvLoading]   = useState(false);
  const [showCsv, setShowCsv]         = useState(false);

  // Search
  const [search, setSearch] = useState("");

  function load() { api.get("/school-admin/teachers").then((r) => setTeachers(r.data)).catch(() => {}); }
  useEffect(load, []);

  async function createTeacher(e) {
    e.preventDefault(); setFormError(""); setAddedCreds(null);
    try {
      const res = await api.post("/school-admin/teachers", form);
      setAddedCreds({ username: res.data.teacher.username, tempPassword: res.data.teacher.tempPassword });
      setForm(EMPTY_TEACHER_FORM());
      load();
    } catch (err) { setFormError(err.response?.data?.error || "Failed to add teacher."); }
  }

  async function saveEdit(e) {
    e.preventDefault(); setEditError("");
    try {
      await api.put(`/school-admin/teachers/${editModal.id}`, editModal);
      setEditModal(null);
      load();
    } catch (err) { setEditError(err.response?.data?.error || "Failed to update."); }
  }

  async function toggleActive(t) {
    await api.put(`/school-admin/teachers/${t.id}/active`, { isActive: !t.is_active });
    load();
  }

  async function confirmDelete() {
    setDeleteError("");
    try {
      await api.delete(`/school-admin/teachers/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      load();
    } catch (err) { setDeleteError(err.response?.data?.error || "Failed to delete."); }
  }

  async function doResetPassword(e) {
    e.preventDefault(); setResetError(""); setResetResult(null);
    try {
      const res = await api.post(`/school-admin/teachers/${resetModal.id}/reset-password`, {
        password: resetPassword || undefined,
        notify: resetNotify,
      });
      setResetResult(res.data.tempPassword);
    } catch (err) { setResetError(err.response?.data?.error || "Failed to reset password."); }
  }

  const filtered = search.trim()
    ? teachers.filter((t) =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.username?.toLowerCase().includes(search.toLowerCase()) ||
        t.email?.toLowerCase().includes(search.toLowerCase()) ||
        t.mobile?.includes(search)
      )
    : teachers;

  function downloadTeacherSample() {
    const header = "name,email,mobile,designation,qualification,address";
    const sample = [
      "Sunita Sharma,sunita@school.edu,9876543210,Science Teacher,B.Sc B.Ed,123 Main Street",
      "Ramesh Gupta,ramesh@school.edu,9123456789,Math Teacher,M.Sc B.Ed,",
    ].join("\n");
    const blob = new Blob([header + "\n" + sample], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "teachers_sample.csv"; a.click();
  }

  function parseTeacherCSV(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return { error: "CSV must have a header row and at least one data row." };
    const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
      const row = {};
      headers.forEach((h, idx) => { row[h] = vals[idx] || ""; });
      if (row.name) rows.push(row);
    }
    if (!rows.length) return { error: "No valid rows found." };
    return { rows };
  }

  function onCsvFile(e) {
    setCsvError(""); setCsvRows(null); setCsvResults(null);
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { rows, error } = parseTeacherCSV(ev.target.result);
      if (error) { setCsvError(error); return; }
      setCsvRows(rows);
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  async function submitBulkTeachers() {
    if (!csvRows?.length) return;
    setCsvLoading(true); setCsvResults(null); setCsvError("");
    try {
      const r = await api.post("/school-admin/teachers/bulk", { teachers: csvRows });
      setCsvResults(r.data.results);
      setCsvRows(null);
      load();
    } catch (err) { setCsvError(err.response?.data?.error || "Upload failed."); }
    finally { setCsvLoading(false); }
  }

  const usernameNote = {
    auto: "Username is auto-generated from the teacher's name.",
    email: "The teacher's email address will be their username.",
    mobile: "The teacher's mobile number will be their username.",
  };

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Teachers</h1>
        <p className="subtitle">Manage teaching staff accounts. Login credentials are auto-generated and sent via email/SMS.</p>
      </div>

      {/* Upload Teachers via CSV */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showCsv ? 16 : 0 }}>
          <div>
            <h3 style={{ margin: 0 }}>📤 Upload Teachers via CSV</h3>
            {!showCsv && <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--muted)" }}>Bulk add teachers — credentials emailed automatically.</p>}
          </div>
          <button className="btn secondary" onClick={() => { setShowCsv(!showCsv); setCsvRows(null); setCsvResults(null); setCsvError(""); }}>
            {showCsv ? "Close" : "Upload CSV"}
          </button>
        </div>

        {showCsv && (
          <div>
            {/* Instructions */}
            <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13 }}>
              <strong>Required columns:</strong> <code>name</code>, <code>email</code> or <code>mobile</code> (at least one)<br />
              <strong>Optional columns:</strong> <code>designation</code>, <code>qualification</code>, <code>address</code><br />
              <span style={{ color: "var(--muted)" }}>A username and temporary password will be auto-generated and sent to each teacher by email/SMS.</span>
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <button className="btn secondary" onClick={downloadTeacherSample}>⬇ Download Sample CSV</button>
              <label className="btn" style={{ cursor: "pointer" }}>
                📂 Choose CSV File
                <input type="file" accept=".csv" style={{ display: "none" }} onChange={onCsvFile} />
              </label>
            </div>

            {csvError && <div className="error-text" style={{ marginBottom: 12 }}>{csvError}</div>}

            {/* Preview table */}
            {csvRows && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview — {csvRows.length} teacher{csvRows.length !== 1 ? "s" : ""} ready to import:</div>
                <div style={{ overflowX: "auto", marginBottom: 16 }}>
                  <table className="data-table" style={{ fontSize: 13 }}>
                    <thead><tr>
                      <th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>Designation</th><th>Qualification</th>
                    </tr></thead>
                    <tbody>
                      {csvRows.map((r, i) => (
                        <tr key={i} style={{ background: (!r.email && !r.mobile) ? "#FEF2F2" : undefined }}>
                          <td>{i + 1}</td>
                          <td>{r.name || <span style={{ color: "red" }}>Missing!</span>}</td>
                          <td>{r.email || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                          <td>{r.mobile || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                          <td>{r.designation || "—"}</td>
                          <td>{r.qualification || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn" onClick={submitBulkTeachers} disabled={csvLoading}>
                  {csvLoading ? "Creating accounts…" : `✅ Create ${csvRows.length} Teacher Account${csvRows.length !== 1 ? "s" : ""} & Send Credentials`}
                </button>
              </div>
            )}

            {/* Results */}
            {csvResults && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>
                  Results: {csvResults.filter((r) => r.status === "created").length} created,{" "}
                  {csvResults.filter((r) => r.status === "error").length} failed
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className="data-table" style={{ fontSize: 13 }}>
                    <thead><tr><th>Name</th><th>Status</th><th>Username</th><th>Temp Password</th><th>Note</th></tr></thead>
                    <tbody>
                      {csvResults.map((r, i) => (
                        <tr key={i} style={{ background: r.status === "error" ? "#FEF2F2" : "#F0FDF4" }}>
                          <td>{r.name}</td>
                          <td><span style={{ fontWeight: 600, color: r.status === "created" ? "#166534" : "#DC2626" }}>
                            {r.status === "created" ? "✅ Created" : "❌ Failed"}
                          </span></td>
                          <td>{r.username ? <code>{r.username}</code> : "—"}</td>
                          <td>{r.tempPassword ? <code>{r.tempPassword}</code> : "—"}</td>
                          <td style={{ fontSize: 12, color: "var(--muted)" }}>
                            {r.status === "created"
                              ? `Sent to ${[r.email, r.mobile].filter(Boolean).join(" & ")}`
                              : r.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Teacher */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: showAdd ? 16 : 0 }}>
          <h3 style={{ margin: 0 }}>Add Teacher</h3>
          <button className="btn secondary" onClick={() => { setShowAdd(!showAdd); setFormError(""); setAddedCreds(null); }}>
            {showAdd ? "Cancel" : "+ Add Teacher"}
          </button>
        </div>

        {showAdd && (
          <>
            {formError && <div className="error-text" style={{ marginBottom: 12 }}>{formError}</div>}

            {addedCreds && (
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: "#166534", marginBottom: 8 }}>Teacher added successfully!</div>
                <div style={{ fontSize: 13, color: "#166534", marginBottom: 4 }}>
                  Username: <code style={{ background: "#DCFCE7", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>{addedCreds.username}</code>
                </div>
                <div style={{ fontSize: 13, color: "#166534" }}>
                  Temporary Password: <code style={{ background: "#DCFCE7", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>{addedCreds.tempPassword}</code>
                </div>
                <div style={{ fontSize: 12, color: "#166534", marginTop: 6, opacity: 0.8 }}>
                  Credentials have been sent via email/SMS if provided. The teacher will be prompted to change their password on first login.
                </div>
              </div>
            )}

            <form onSubmit={createTeacher}>
              {/* Username type */}
              <div className="form-field" style={{ marginBottom: 8 }}>
                <label className="form-label">Login Username Type *</label>
                <select value={form.loginType} onChange={(e) => setForm({ ...form, loginType: e.target.value })}>
                  <option value="auto">Auto-generate (e.g. john1234)</option>
                  <option value="email">Use Email address as username</option>
                  <option value="mobile">Use Mobile number as username</option>
                </select>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{usernameNote[form.loginType]}</div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Full Name *</label>
                  <input placeholder="e.g. Sunita Sharma" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-field">
                  <label className="form-label">
                    Email {form.loginType === "email" ? "*" : ""}
                  </label>
                  <input type="email" placeholder="teacher@school.com" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required={form.loginType === "email"} />
                </div>
                <div className="form-field">
                  <label className="form-label">
                    Mobile {form.loginType === "mobile" ? "*" : ""}
                  </label>
                  <input type="tel" placeholder="10-digit number" value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    required={form.loginType === "mobile"} />
                </div>
                <div className="form-field">
                  <label className="form-label">Designation</label>
                  <input placeholder="e.g. Senior Teacher, HOD" value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })} />
                </div>
                <div className="form-field">
                  <label className="form-label">Qualification</label>
                  <input placeholder="e.g. B.Ed, M.Sc Mathematics" value={form.qualification}
                    onChange={(e) => setForm({ ...form, qualification: e.target.value })} />
                </div>
                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Address</label>
                  <input placeholder="Residential address" value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
              </div>
              <button className="btn" type="submit">Create Teacher Account</button>
            </form>
          </>
        )}
      </div>

      {/* Teacher list */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <h3 style={{ margin: 0 }}>
            Teaching Staff &nbsp;
            <span className="badge info">{teachers.length}</span>
          </h3>
          <input
            placeholder="Search name, username, email, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260, fontSize: 13 }}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👨‍🏫</div>
            <div className="empty-state-text">{teachers.length === 0 ? "No teachers added yet." : "No teachers match your search."}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username (Login)</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Designation</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right", width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.6 }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      {t.qualification && <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.qualification}</div>}
                    </td>
                    <td>
                      <code style={{ fontSize: 12, background: "var(--bg)", padding: "2px 6px", borderRadius: 4 }}>
                        {t.username}
                      </code>
                    </td>
                    <td style={{ fontSize: 13 }}>{t.email || "—"}</td>
                    <td style={{ fontSize: 13 }}>{t.mobile || "—"}</td>
                    <td style={{ fontSize: 13, color: "var(--muted)" }}>{t.designation || "—"}</td>
                    <td>
                      <span className={`badge ${t.is_active ? "active" : "suspended"}`}>
                        {t.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <TeacherRowMenu
                        teacher={t}
                        onEdit={() => { setEditModal({ ...t }); setEditError(""); }}
                        onResetPwd={() => { setResetModal(t); setResetPassword(""); setResetResult(null); setResetError(""); }}
                        onToggleActive={() => toggleActive(t)}
                        onDelete={() => { setDeleteConfirm(t); setDeleteError(""); }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit Teacher — {editModal.name}</strong>
              <button className="tpl-modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {editError && <div className="error-text" style={{ marginBottom: 12 }}>{editError}</div>}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13 }}>
                <span style={{ color: "var(--muted)" }}>Username (Login ID): </span>
                <code style={{ fontWeight: 700 }}>{editModal.username}</code>
                <span style={{ fontSize: 12, color: "var(--muted)", marginLeft: 8 }}>— Use "Reset Password" to change login credentials</span>
              </div>
              <form onSubmit={saveEdit}>
                <div className="form-grid">
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Full Name *</label>
                    <input value={editModal.name}
                      onChange={(e) => setEditModal({ ...editModal, name: e.target.value })} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Email</label>
                    <input type="email" value={editModal.email || ""}
                      onChange={(e) => setEditModal({ ...editModal, email: e.target.value })} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Mobile</label>
                    <input type="tel" value={editModal.mobile || ""}
                      onChange={(e) => setEditModal({ ...editModal, mobile: e.target.value })} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Designation</label>
                    <input value={editModal.designation || ""}
                      onChange={(e) => setEditModal({ ...editModal, designation: e.target.value })}
                      placeholder="e.g. Senior Teacher, HOD" />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Qualification</label>
                    <input value={editModal.qualification || ""}
                      onChange={(e) => setEditModal({ ...editModal, qualification: e.target.value })}
                      placeholder="e.g. B.Ed, M.Sc" />
                  </div>
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Address</label>
                    <input value={editModal.address || ""}
                      onChange={(e) => setEditModal({ ...editModal, address: e.target.value })}
                      placeholder="Residential address" />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn" type="submit">Save Changes</button>
                  <button className="btn secondary" type="button" onClick={() => setEditModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="tpl-modal-overlay" onClick={() => { if (!resetResult) setResetModal(null); }}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Reset Password — {resetModal.name}</strong>
              <button className="tpl-modal-close" onClick={() => setResetModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {resetResult ? (
                <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "16px 18px" }}>
                  <div style={{ fontWeight: 700, color: "#166534", marginBottom: 8 }}>Password reset successfully!</div>
                  <div style={{ fontSize: 14, color: "#166534", marginBottom: 4 }}>
                    New password: <code style={{ background: "#DCFCE7", padding: "3px 10px", borderRadius: 4, fontWeight: 700, fontSize: 15 }}>{resetResult}</code>
                  </div>
                  {resetNotify && (
                    <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>Credentials sent to teacher via email/SMS.</div>
                  )}
                  <button className="btn secondary" style={{ marginTop: 14 }} onClick={() => setResetModal(null)}>Close</button>
                </div>
              ) : (
                <>
                  {resetError && <div className="error-text" style={{ marginBottom: 12 }}>{resetError}</div>}
                  <form onSubmit={doResetPassword}>
                    <div className="form-field" style={{ marginBottom: 14 }}>
                      <label className="form-label">New Password</label>
                      <input
                        type="text"
                        placeholder="Leave blank to auto-generate a secure password"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                      />
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                        Min 6 characters. Leave blank to auto-generate. The teacher must change it on next login.
                      </div>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, marginBottom: 16 }}>
                      <input type="checkbox" checked={resetNotify}
                        onChange={(e) => setResetNotify(e.target.checked)} style={{ width: 15, height: 15 }} />
                      Notify teacher via email/SMS after reset
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn" type="submit">Reset Password</button>
                      <button className="btn secondary" type="button" onClick={() => setResetModal(null)}>Cancel</button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="tpl-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 420, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 10px" }}>Delete Teacher?</h3>
            <p style={{ marginBottom: 6 }}><strong>{deleteConfirm.name}</strong></p>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
              This permanently deletes the account and all subject assignments.
              If the teacher has entered any marks, deletion will be blocked — deactivate the account instead.
            </p>
            {deleteError && <div className="error-text" style={{ marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn danger" onClick={confirmDelete}>Yes, Delete</button>
              <button className="btn secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Assignments
   ====================================================== */
function Assignments() {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({ teacherId: "", subjectId: "", classSectionId: "", accessType: "entry" });
  const [formError, setFormError] = useState("");
  const [viewMode, setViewMode] = useState("teacher"); // "teacher" | "table"

  useEffect(() => {
    api.get("/school-admin/teachers").then((r) => setTeachers(r.data));
    api.get("/school-admin/classes").then((r) => setClasses(r.data));
    api.get("/school-admin/subjects").then((r) => setSubjects(r.data));
    api.get("/school-admin/assignments").then((r) => setAssignments(r.data));
  }, []);

  async function createAssignment(e) {
    e.preventDefault(); setFormError("");
    try {
      await api.post("/school-admin/assignments", form);
      setForm({ teacherId: form.teacherId, subjectId: "", classSectionId: "", accessType: "entry" });
      api.get("/school-admin/assignments").then((r) => { setAssignments(r.data); setViewMode("teacher"); });
    } catch (err) { setFormError(err.response?.data?.error || "Failed to create assignment."); }
  }

  async function revoke(id) {
    if (!window.confirm("Revoke this assignment?")) return;
    await api.delete(`/school-admin/assignments/${id}`);
    setAssignments(assignments.filter((a) => a.id !== id));
  }

  async function toggleClassTeacher(assignment) {
    const making = !assignment.is_class_teacher;
    try {
      await api.put(`/school-admin/assignments/${assignment.id}/class-teacher`, { isClassTeacher: making });
      // Refresh assignments
      const r = await api.get("/school-admin/assignments");
      setAssignments(r.data);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update class teacher.");
    }
  }

  const filteredSubjects = subjects.filter((s) => String(s.class_section_id) === String(form.classSectionId));

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Subject-Class-Teacher Assignments</h1>
        <p className="subtitle">Assign which teacher can enter marks for which subject and class.</p>
      </div>

      <div className="card">
        <h3>Create Assignment</h3>
        {formError && <div className="error-text" style={{ marginBottom: 12 }}>{formError}</div>}
        <form onSubmit={createAssignment}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Teacher *</label>
              <select value={form.teacherId} onChange={(e) => setForm({ ...form, teacherId: e.target.value })} required>
                <option value="">Select teacher</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Class / Section *</label>
              <select value={form.classSectionId} onChange={(e) => setForm({ ...form, classSectionId: e.target.value, subjectId: "" })} required>
                <option value="">Select class</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.section}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Subject *</label>
              <select value={form.subjectId} onChange={(e) => setForm({ ...form, subjectId: e.target.value })} required>
                <option value="">Select subject</option>
                {filteredSubjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Access Type</label>
              <select value={form.accessType} onChange={(e) => setForm({ ...form, accessType: e.target.value })}>
                <option value="entry">Entry (can enter marks)</option>
                <option value="view">View only</option>
              </select>
            </div>
          </div>
          <button className="btn" type="submit">Assign</button>
        </form>
      </div>

      {/* Assignments view */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ margin: 0 }}>
              Current Assignments &nbsp;
              <span className="badge info">{assignments.length}</span>
            </h3>
          </div>
          <div style={{ display: "flex", gap: 0, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            {[{ id: "teacher", label: "By Teacher" }, { id: "table", label: "All Assignments" }].map(({ id, label }) => (
              <button key={id} type="button"
                onClick={() => setViewMode(id)}
                style={{
                  padding: "6px 16px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                  background: viewMode === id ? "var(--navy)" : "#fff",
                  color: viewMode === id ? "#fff" : "var(--muted)",
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {assignments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📌</div>
            <div className="empty-state-text">No assignments yet. Use the form above to assign teachers to subjects.</div>
          </div>
        ) : viewMode === "teacher" ? (
          /* ── Grouped by teacher ── */
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(() => {
              // Group assignments by teacher
              const byTeacher = {};
              assignments.forEach((a) => {
                const tid = a.teacher_id;
                if (!byTeacher[tid]) byTeacher[tid] = { teacher: a.teacher, items: [] };
                byTeacher[tid].items.push(a);
              });
              return Object.values(byTeacher).map(({ teacher: t, items }) => {
                // Group this teacher's items by class section
                const byClass = {};
                items.forEach((a) => {
                  const cid = a.class_section_id;
                  if (!byClass[cid]) byClass[cid] = { cls: a.ClassSection, subjects: [] };
                  byClass[cid].subjects.push(a);
                });
                const teacherObj = teachers.find((x) => x.id === t?.id);
                return (
                  <div key={t?.id} style={{
                    border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden",
                  }}>
                    {/* Teacher header */}
                    <div style={{
                      background: "var(--navy)", color: "#fff",
                      padding: "12px 18px", display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%", background: "#ffffff22",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
                      }}>👩‍🏫</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{t?.name || "Unknown"}</div>
                        {teacherObj?.designation && (
                          <div style={{ fontSize: 12, opacity: 0.75 }}>{teacherObj.designation}</div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{items.length} assignment{items.length !== 1 ? "s" : ""}</div>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                          background: teacherObj?.is_active !== false ? "#22c55e33" : "#ef444433",
                          color: teacherObj?.is_active !== false ? "#86efac" : "#fca5a5",
                        }}>
                          {teacherObj?.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    {/* Classes and subjects */}
                    <div style={{ padding: "0 18px 14px" }}>
                      {Object.values(byClass).map(({ cls, subjects }) => (
                        <div key={cls?.id} style={{ marginTop: 14 }}>
                          <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--border)",
                          }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              🏫 {cls?.name}-{cls?.section}
                            </div>
                            {/* Class Teacher toggle — show on any one assignment for this class */}
                            {(() => {
                              const repAssignment = subjects[0]; // any assignment for this class
                              const isCT = repAssignment?.is_class_teacher;
                              return (
                                <button
                                  type="button"
                                  onClick={() => toggleClassTeacher(repAssignment)}
                                  title={isCT ? "Remove class teacher designation" : "Designate as class teacher for this class"}
                                  style={{
                                    fontSize: 13, fontWeight: 700, padding: "6px 16px", borderRadius: 20, cursor: "pointer",
                                    border: isCT ? "2px solid #7C3AED" : "2px dashed #A78BFA",
                                    background: isCT ? "#7C3AED" : "#fff",
                                    color: isCT ? "#fff" : "#7C3AED",
                                    display: "flex", alignItems: "center", gap: 5,
                                    boxShadow: isCT ? "0 2px 8px rgba(124,58,237,0.25)" : "none",
                                  }}
                                >
                                  {isCT ? "⭐ Class Teacher" : "+ Set as Class Teacher"}
                                </button>
                              );
                            })()}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {subjects.map((a) => (
                              <div key={a.id} style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "6px 10px 6px 12px", borderRadius: 8,
                                border: `1px solid ${a.is_class_teacher ? "#7C3AED" : "var(--border)"}`,
                                background: a.is_class_teacher ? "#EDE9FE" : "#fff",
                                fontSize: 13,
                              }}>
                                <span style={{ fontWeight: 600 }}>{a.Subject?.name}</span>
                                <span className={`badge ${a.access_type === "entry" ? "active" : "info"}`} style={{ fontSize: 10 }}>
                                  {a.access_type === "entry" ? "Entry" : "View"}
                                </span>
                                <button
                                  onClick={() => revoke(a.id)}
                                  style={{
                                    border: "none", background: "none", cursor: "pointer",
                                    color: "var(--danger)", fontSize: 14, padding: "0 2px", lineHeight: 1,
                                  }}
                                  title="Revoke assignment"
                                >✕</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          /* ── Flat table ── */
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Teacher</th>
                  <th>Class / Section</th>
                  <th>Subject</th>
                  <th>Access</th>
                  <th>Class Teacher</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.teacher?.name}</div>
                      {(() => {
                        const t = teachers.find((x) => x.id === a.teacher_id);
                        return t?.designation ? <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.designation}</div> : null;
                      })()}
                    </td>
                    <td style={{ fontWeight: 500 }}>{a.ClassSection?.name}-{a.ClassSection?.section}</td>
                    <td>{a.Subject?.name}</td>
                    <td>
                      <span className={`badge ${a.access_type === "entry" ? "active" : "info"}`}>
                        {a.access_type === "entry" ? "Entry" : "View"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => toggleClassTeacher(a)}
                        style={{
                          fontSize: 13, fontWeight: 700, padding: "6px 16px", borderRadius: 20, cursor: "pointer",
                          border: a.is_class_teacher ? "2px solid #7C3AED" : "2px dashed #A78BFA",
                          background: a.is_class_teacher ? "#7C3AED" : "#fff",
                          color: a.is_class_teacher ? "#fff" : "#7C3AED",
                          boxShadow: a.is_class_teacher ? "0 2px 8px rgba(124,58,237,0.25)" : "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.is_class_teacher ? "⭐ Class Teacher" : "+ Set as Class Teacher"}
                      </button>
                    </td>
                    <td>
                      <button className="btn sm danger" onClick={() => revoke(a.id)}>Revoke</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================================================
   Exam type label helper
   ====================================================== */
const EXAM_TYPE_LABELS = {
  periodic_test: "Periodic Test",
  unit_test: "Unit Test",
  half_yearly: "Half Yearly",
  annual: "Annual",
  pre_board: "Pre-Board",
  terminal: "Terminal",
  quarterly: "Quarterly",
  class_test: "Class Test",
  custom: "Custom",
};

function ExamTypeBadge({ type }) {
  const colorMap = {
    periodic_test: "info",
    unit_test: "info",
    half_yearly: "warn",
    annual: "active",
    pre_board: "active",
    terminal: "warn",
    quarterly: "info",
    class_test: "info",
    custom: "suspended",
  };
  return (
    <span className={`badge ${colorMap[type] || "info"}`} style={{ fontSize: 11 }}>
      {EXAM_TYPE_LABELS[type] || type}
    </span>
  );
}

/* ======================================================
   Exams & Panels
   ====================================================== */
function ExamsPanels() {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [panels, setPanels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState("");

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [examForm, setExamForm] = useState({ name: "", session: "", has_scholastic: true, has_co_scholastic: false, sequence_no: 1 });
  const [examError, setExamError] = useState("");
  const [examMsg, setExamMsg] = useState("");

  // Edit exam
  const [editModal, setEditModal] = useState(null);
  const [editError, setEditError] = useState("");

  // Delete exam
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // Panels
  const [panelForm, setPanelForm] = useState({ examId: "", classSectionId: "", openAt: "", closeAt: "" });
  const [panelError, setPanelError] = useState("");
  const [openPanels, setOpenPanels] = useState([]);  // currently open panels across all exams
  const [editPanelModal, setEditPanelModal] = useState(null); // panel being edited
  const [editPanelError, setEditPanelError] = useState("");
  const [deletePanelConfirm, setDeletePanelConfirm] = useState(null); // panel pending delete
  const [deletePanelError, setDeletePanelError] = useState("");

  // Filter exams by session
  const [filterSession, setFilterSession] = useState("");

  function loadExams() { api.get("/school-admin/exams").then((r) => setExams(r.data)); }
  function loadOpenPanels() {
    api.get("/school-admin/panels").then((r) => setOpenPanels(r.data.filter((p) => p.status === "open"))).catch(() => {});
  }

  useEffect(() => {
    loadExams();
    loadOpenPanels();
    api.get("/school-admin/classes").then((r) => setClasses(r.data));
    api.get("/school-admin/exam-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/school-admin/sessions").then((r) => {
      setSessions(r.data);
      const curr = r.data.find((s) => s.is_current);
      if (curr) {
        setCurrentSession(curr.name);
        setFilterSession(curr.name);
        setExamForm((f) => ({ ...f, session: curr.name }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (panelForm.examId) {
      api.get(`/school-admin/panels?examId=${panelForm.examId}`).then((r) => setPanels(r.data));
    } else {
      setPanels([]);
    }
  }, [panelForm.examId]);

  function applyTemplate(tpl) {
    setSelectedTemplate(tpl.id);
    setExamForm((f) => ({ ...f, name: tpl.name, sequence_no: tpl.sequence_no, has_co_scholastic: tpl.has_co_scholastic }));
  }

  async function createExam(e) {
    e.preventDefault(); setExamError(""); setExamMsg("");
    try {
      await api.post("/school-admin/exams", examForm);
      setExamForm({ name: "", session: currentSession || "", has_scholastic: true, has_co_scholastic: false, sequence_no: 1 });
      setSelectedTemplate(null); setShowCreateForm(false);
      setExamMsg("Exam created successfully.");
      loadExams();
      setTimeout(() => setExamMsg(""), 3000);
    } catch (err) { setExamError(err.response?.data?.error || "Failed to create exam."); }
  }

  async function saveEdit(e) {
    e.preventDefault(); setEditError("");
    try {
      await api.put(`/school-admin/exams/${editModal.id}`, editModal);
      setEditModal(null);
      loadExams();
    } catch (err) { setEditError(err.response?.data?.error || "Failed to update exam."); }
  }

  async function confirmDelete() {
    setDeleteError("");
    try {
      await api.delete(`/school-admin/exams/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      loadExams();
    } catch (err) { setDeleteError(err.response?.data?.error || "Failed to delete."); }
  }

  function reloadPanels(examIdOverride) {
    const eid = examIdOverride || panelForm.examId;
    if (eid) api.get(`/school-admin/panels?examId=${eid}`).then((r) => setPanels(r.data));
    loadOpenPanels();
  }

  // datetime-local gives "YYYY-MM-DDTHH:mm" in local time — convert to UTC before sending to server
  function localDtToUTC(localStr) {
    if (!localStr) return localStr;
    return new Date(localStr).toISOString();
  }

  async function createPanel(e) {
    e.preventDefault(); setPanelError("");
    try {
      await api.post("/school-admin/panels", {
        ...panelForm,
        openAt: localDtToUTC(panelForm.openAt),
        closeAt: localDtToUTC(panelForm.closeAt),
      });
      reloadPanels();
    } catch (err) { setPanelError(err.response?.data?.error || "Failed to open panel."); }
  }

  async function togglePanel(p) {
    await api.put(`/school-admin/panels/${p.id}/status`, { status: p.status === "closed" ? "open" : "closed" });
    reloadPanels(p.exam_id);
  }

  async function saveEditPanel(e) {
    e.preventDefault(); setEditPanelError("");
    try {
      await api.put(`/school-admin/panels/${editPanelModal.id}`, {
        openAt: localDtToUTC(editPanelModal.open_at),
        closeAt: localDtToUTC(editPanelModal.close_at),
        status: editPanelModal.status,
      });
      setEditPanelModal(null);
      reloadPanels(editPanelModal.exam_id);
    } catch (err) { setEditPanelError(err.response?.data?.error || "Failed to update panel."); }
  }

  async function confirmDeletePanel() {
    setDeletePanelError("");
    try {
      await api.delete(`/school-admin/panels/${deletePanelConfirm.id}`);
      const examId = deletePanelConfirm.exam_id;
      setDeletePanelConfirm(null);
      reloadPanels(examId);
    } catch (err) { setDeletePanelError(err.response?.data?.error || "Failed to delete panel."); }
  }

  const grouped = templates.reduce((acc, t) => {
    if (!acc[t.exam_type]) acc[t.exam_type] = [];
    acc[t.exam_type].push(t);
    return acc;
  }, {});

  const allSessions = [...new Set(exams.map((e) => e.session))].sort().reverse();
  const filteredExams = filterSession ? exams.filter((e) => e.session === filterSession) : exams;

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Exams &amp; Marks-Entry Panels</h1>
        <p className="subtitle">Create and manage exams, then control when teachers can enter marks using time-locked panels.</p>
      </div>

      {/* ── Exams card ── */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ margin: 0 }}>
              Exams &nbsp;<span className="badge info">{filteredExams.length}</span>
            </h3>
            {/* Session filter */}
            <select
              value={filterSession}
              onChange={(e) => setFilterSession(e.target.value)}
              style={{ fontSize: 13, padding: "4px 10px", borderRadius: 8, border: "1px solid var(--border)" }}
            >
              <option value="">All Sessions</option>
              {allSessions.map((s) => (
                <option key={s} value={s}>{s}{s === currentSession ? " (Current)" : ""}</option>
              ))}
            </select>
          </div>
          <button className="btn" onClick={() => { setShowCreateForm(!showCreateForm); setExamError(""); setExamMsg(""); }}>
            {showCreateForm ? "Cancel" : "+ Create Exam"}
          </button>
        </div>

        {examMsg && <div className="success-text" style={{ marginBottom: 12 }}>✓ {examMsg}</div>}

        {/* Create form */}
        {showCreateForm && (
          <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <h4 style={{ margin: "0 0 14px" }}>New Exam</h4>

            {/* Template picker */}
            {Object.keys(grouped).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>Quick-fill from template:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.values(grouped).flat().map((tpl) => (
                    <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)} style={{
                      padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                      border: selectedTemplate === tpl.id ? "2px solid var(--primary)" : "1px solid var(--border)",
                      background: selectedTemplate === tpl.id ? "var(--primary)" : "#fff",
                      color: selectedTemplate === tpl.id ? "#fff" : "var(--text)", fontWeight: selectedTemplate === tpl.id ? 600 : 400,
                    }}>{tpl.abbreviation} — {tpl.name}</button>
                  ))}
                </div>
              </div>
            )}

            {examError && <div className="error-text" style={{ marginBottom: 12 }}>{examError}</div>}
            <form onSubmit={createExam}>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Exam Name *</label>
                  <input placeholder="e.g. Unit Test 1, Mid-Term" value={examForm.name}
                    onChange={(e) => setExamForm({ ...examForm, name: e.target.value })} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Session *</label>
                  <select value={examForm.session} onChange={(e) => setExamForm({ ...examForm, session: e.target.value })} required>
                    <option value="">— Select Session —</option>
                    {sessions.map((s) => (
                      <option key={s.id} value={s.name}>{s.label || s.name}{s.is_current ? " (Current)" : ""}</option>
                    ))}
                    {sessions.length === 0 && <option disabled>No sessions — create one in Settings</option>}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Sequence No <span className="text-muted" style={{ fontWeight: 400 }}>(order within session)</span></label>
                  <input type="number" min="1" value={examForm.sequence_no}
                    onChange={(e) => setExamForm({ ...examForm, sequence_no: Number(e.target.value) })} />
                </div>
                <div className="form-field">
                  <label className="form-label">Includes Co-Scholastic</label>
                  <select value={String(examForm.has_co_scholastic)}
                    onChange={(e) => setExamForm({ ...examForm, has_co_scholastic: e.target.value === "true" })}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <button className="btn" type="submit">Create Exam</button>
            </form>
          </div>
        )}

        {/* Exam list */}
        {filteredExams.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <div className="empty-state-text">{exams.length === 0 ? "No exams created yet." : `No exams in session "${filterSession}".`}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Exam Name</th>
                  <th>Session</th>
                  <th>Seq</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExams.map((ex) => (
                  <tr key={ex.id}>
                    <td><strong>{ex.name}</strong></td>
                    <td>
                      <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 10, background: "#EFF6FF", color: "#1D4ED8", fontWeight: 600 }}>
                        {ex.session}
                      </span>
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: 13 }}>{ex.sequence_no}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        <span className="badge info" style={{ fontSize: 11 }}>Scholastic</span>
                        {ex.has_co_scholastic && <span className="badge active" style={{ fontSize: 11 }}>Co-Scholastic</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn sm secondary"
                          onClick={() => { setEditModal({ ...ex }); setEditError(""); }}>
                          Edit
                        </button>
                        <button className="btn sm danger"
                          onClick={() => { setDeleteConfirm(ex); setDeleteError(""); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Marks-Entry Panels card ── */}
      <div className="card">
        <h3 style={{ marginBottom: 4 }}>Marks-Entry Panels</h3>
        <p className="subtitle" style={{ marginBottom: 16 }}>
          Teachers can only enter marks while a panel is open for their exam and class.
        </p>
        {panelError && <div className="error-text" style={{ marginBottom: 12 }}>{panelError}</div>}
        <form onSubmit={createPanel}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Exam *</label>
              <select value={panelForm.examId} onChange={(e) => setPanelForm({ ...panelForm, examId: e.target.value })} required>
                <option value="">Select exam</option>
                {exams.map((ex) => <option key={ex.id} value={ex.id}>{ex.name} ({ex.session})</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Class / Section *</label>
              <select value={panelForm.classSectionId} onChange={(e) => setPanelForm({ ...panelForm, classSectionId: e.target.value })} required>
                <option value="">Select class</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Opens At *</label>
              <input type="datetime-local" value={panelForm.openAt} onChange={(e) => setPanelForm({ ...panelForm, openAt: e.target.value })} required />
            </div>
            <div className="form-field">
              <label className="form-label">Closes At *</label>
              <input type="datetime-local" value={panelForm.closeAt} onChange={(e) => setPanelForm({ ...panelForm, closeAt: e.target.value })} required />
            </div>
          </div>
          <button className="btn" type="submit">Open Panel</button>
        </form>

        {panels.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table>
              <thead><tr><th>Class</th><th>Opens At</th><th>Closes At</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {panels.map((p) => {
                  const cls = classes.find((c) => String(c.id) === String(p.class_section_id));
                  const isOpen = p.status === "open";
                  return (
                    <tr key={p.id} style={{ background: isOpen ? "#F0FDF4" : undefined }}>
                      <td><strong>{cls ? `${cls.name}-${cls.section}` : p.class_section_id}</strong></td>
                      <td style={{ fontSize: 13 }}>{new Date(p.open_at).toLocaleString("en-IN")}</td>
                      <td style={{ fontSize: 13 }}>{new Date(p.close_at).toLocaleString("en-IN")}</td>
                      <td>
                        <span className={`badge ${isOpen ? "active" : p.status === "scheduled" ? "info" : "suspended"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn sm secondary" style={{ fontSize: 11 }}
                            onClick={() => {
                              const toLocal = (dt) => dt ? new Date(new Date(dt).getTime() - new Date(dt).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
                              setEditPanelModal({ ...p, open_at: toLocal(p.open_at), close_at: toLocal(p.close_at) });
                              setEditPanelError("");
                            }}>
                            ✏️ Edit
                          </button>
                          <button className="btn sm secondary" style={{ fontSize: 11 }} onClick={() => togglePanel(p)}>
                            {p.status === "closed" ? "Reopen" : "Force Close"}
                          </button>
                          <button className="btn sm" style={{ fontSize: 11, background: "#DC2626", borderColor: "#DC2626", color: "white" }}
                            onClick={() => { setDeletePanelConfirm(p); setDeletePanelError(""); }}>
                            🗑 Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Always-visible: Currently Open Panels ── */}
      {openPanels.length > 0 && (
        <div className="card" style={{ borderLeft: "4px solid #16A34A", background: "#F0FDF4" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>🟢</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#15803D" }}>Currently Open Panels</h3>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#166534" }}>Teachers can enter marks for these classes right now.</p>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Exam</th>
                  <th>Class</th>
                  <th>Opens At</th>
                  <th>Closes At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {openPanels.map((p) => {
                  const cls = classes.find((c) => String(c.id) === String(p.class_section_id));
                  const exam = exams.find((e) => String(e.id) === String(p.exam_id));
                  return (
                    <tr key={p.id} style={{ background: "#DCFCE7" }}>
                      <td style={{ fontWeight: 700 }}>{exam?.name || `Exam #${p.exam_id}`}</td>
                      <td><strong>{cls ? `${cls.name}-${cls.section}` : p.class_section_id}</strong></td>
                      <td>{new Date(p.open_at).toLocaleString("en-IN")}</td>
                      <td>{new Date(p.close_at).toLocaleString("en-IN")}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn sm secondary" style={{ fontSize: 11 }}
                            onClick={() => {
                              const toLocal = (dt) => dt ? new Date(new Date(dt).getTime() - new Date(dt).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
                              setEditPanelModal({ ...p, open_at: toLocal(p.open_at), close_at: toLocal(p.close_at) });
                              setEditPanelError("");
                              setPanelForm((f) => ({ ...f, examId: String(p.exam_id) }));
                            }}>
                            ✏️ Edit
                          </button>
                          <button className="btn sm" style={{ fontSize: 11, background: "#DC2626", borderColor: "#DC2626", color: "white" }}
                            onClick={() => togglePanel(p)}>
                            Force Close
                          </button>
                          <button className="btn sm" style={{ fontSize: 11, background: "#7F1D1D", borderColor: "#7F1D1D", color: "white" }}
                            onClick={() => { setDeletePanelConfirm(p); setDeletePanelError(""); }}>
                            🗑 Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Panel Confirm Modal */}
      {deletePanelConfirm && (
        <div className="tpl-modal-overlay" onClick={() => setDeletePanelConfirm(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Delete Panel</strong>
              <button className="tpl-modal-close" onClick={() => setDeletePanelConfirm(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {deletePanelError && <div className="error-text" style={{ marginBottom: 12 }}>{deletePanelError}</div>}
              <p style={{ margin: "0 0 16px", fontSize: 14, color: "#374151" }}>
                Are you sure you want to delete this marks-entry panel?
              </p>
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 18, fontSize: 13, color: "#7f1d1d" }}>
                <strong>Panel:</strong> {(() => { const cls = classes.find((c) => String(c.id) === String(deletePanelConfirm.class_section_id)); const ex = exams.find((e) => String(e.id) === String(deletePanelConfirm.exam_id)); return `${ex?.name || "Exam"} — ${cls ? `${cls.name}-${cls.section}` : "Class"}`; })()}<br />
                <strong>Window:</strong> {new Date(deletePanelConfirm.open_at).toLocaleString("en-IN")} → {new Date(deletePanelConfirm.close_at).toLocaleString("en-IN")}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" style={{ background: "#DC2626", borderColor: "#DC2626" }} onClick={confirmDeletePanel}>Yes, Delete</button>
                <button className="btn secondary" onClick={() => setDeletePanelConfirm(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Panel Modal */}
      {editPanelModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditPanelModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit Marks-Entry Panel</strong>
              <button className="tpl-modal-close" onClick={() => setEditPanelModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {editPanelError && <div className="error-text" style={{ marginBottom: 12 }}>{editPanelError}</div>}
              <form onSubmit={saveEditPanel}>
                <div className="form-grid">
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Status</label>
                    <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                      {["open", "closed", "scheduled"].map((s) => (
                        <label key={s} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: editPanelModal.status === s ? 700 : 400 }}>
                          <input
                            type="radio"
                            name="panel_status"
                            value={s}
                            checked={editPanelModal.status === s}
                            onChange={() => setEditPanelModal((p) => ({ ...p, status: s }))}
                          />
                          <span style={{
                            padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                            background: s === "open" ? "#D1FAE5" : s === "scheduled" ? "#DBEAFE" : "#FEE2E2",
                            color: s === "open" ? "#065F46" : s === "scheduled" ? "#1D4ED8" : "#991B1B",
                          }}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Opens At *</label>
                    <input
                      type="datetime-local"
                      value={editPanelModal.open_at || ""}
                      onChange={(e) => setEditPanelModal((p) => ({ ...p, open_at: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Closes At *</label>
                    <input
                      type="datetime-local"
                      value={editPanelModal.close_at || ""}
                      onChange={(e) => setEditPanelModal((p) => ({ ...p, close_at: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div style={{ marginTop: 8, padding: "10px 14px", background: "#F8FAFC", borderRadius: 8, fontSize: 12, color: "var(--muted)" }}>
                  Setting status to <strong>Open</strong> allows teachers to enter marks immediately, regardless of the time window.
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button className="btn" type="submit">Save Changes</button>
                  <button className="btn secondary" type="button" onClick={() => setEditPanelModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Exam Modal */}
      {editModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit Exam</strong>
              <button className="tpl-modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {editError && <div className="error-text" style={{ marginBottom: 12 }}>{editError}</div>}
              <form onSubmit={saveEdit}>
                <div className="form-grid">
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Exam Name *</label>
                    <input value={editModal.name}
                      onChange={(e) => setEditModal({ ...editModal, name: e.target.value })} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Session *</label>
                    <select value={editModal.session} onChange={(e) => setEditModal({ ...editModal, session: e.target.value })} required>
                      {sessions.map((s) => <option key={s.id} value={s.name}>{s.label || s.name}</option>)}
                      {/* Also show current value if not in sessions list */}
                      {!sessions.find((s) => s.name === editModal.session) && (
                        <option value={editModal.session}>{editModal.session}</option>
                      )}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Sequence No</label>
                    <input type="number" min="1" value={editModal.sequence_no}
                      onChange={(e) => setEditModal({ ...editModal, sequence_no: Number(e.target.value) })} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Includes Co-Scholastic</label>
                    <select value={String(editModal.has_co_scholastic)}
                      onChange={(e) => setEditModal({ ...editModal, has_co_scholastic: e.target.value === "true" })}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn" type="submit">Save Changes</button>
                  <button className="btn secondary" type="button" onClick={() => setEditModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="tpl-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 420, padding: 28 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 10px" }}>Delete Exam?</h3>
            <p style={{ marginBottom: 6 }}><strong>{deleteConfirm.name}</strong> — {deleteConfirm.session}</p>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
              This also deletes all marks-entry panels for this exam. Cannot be undone.
              If marks have been entered, deletion will be blocked.
            </p>
            {deleteError && <div className="error-text" style={{ marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn danger" onClick={confirmDelete}>Yes, Delete</button>
              <button className="btn secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Admin Marks Entry
   ====================================================== */
/* ======================================================
   Admin Class Marks View
   ====================================================== */
function AdminClassMarksView() {
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selExam, setSelExam] = useState("");
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingCell, setSavingCell] = useState(null); // "studentId-subjectId"
  const [saveStatus, setSaveStatus] = useState({});
  const [sortCol, setSortCol] = useState("rank"); // "rank"|"roll"|"name"|"pct"
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/school-admin/exams").then((r) => setExams(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selClass || !selExam) { setSheet(null); return; }
    setLoading(true); setSheet(null);
    api.get("/school-admin/class-marks-view", { params: { classSectionId: selClass, examId: selExam } })
      .then((r) => setSheet(r.data))
      .catch((err) => alert(err.response?.data?.error || "Failed to load class marks."))
      .finally(() => setLoading(false));
  }, [selClass, selExam]);

  async function saveCellMark(studentId, subjectId, value, isCoScholastic = false) {
    const key = `${studentId}-${subjectId}`;
    const v = typeof value === "string" ? value.trim().toUpperCase() : String(value ?? "");
    const isAbsentMark = v === "AB";
    const parsed = isCoScholastic || isAbsentMark ? null : (v === "" ? null : Number(v));
    const gradeValue = isAbsentMark ? "AB" : (isCoScholastic ? (v || null) : null);
    setSavingCell(key);
    try {
      await api.post("/school-admin/marks", {
        studentId, subjectId, examId: Number(selExam),
        marksObtained: parsed, gradeValue,
      });
      setSaveStatus((p) => ({ ...p, [key]: "saved" }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [key]: null })), 2500);
      // Update local sheet
      setSheet((prev) => {
        if (!prev) return prev;
        const scholastic = prev.subjects.filter((s) => s.category === "scholastic");
        const newRows = prev.rows.map((r) => {
          if (r.studentId !== studentId) return r;
          const newMarks = { ...r.marks, [subjectId]: isAbsentMark ? "AB" : (isCoScholastic ? (v || null) : parsed) };
          // recompute totals
          let totalObtained = 0;
          const totalMax = scholastic.reduce((sum, s) => sum + (Number(s.max_marks) || 0), 0);
          let overallPass = true;
          let hasAllMarks = true;
          scholastic.forEach((sub) => {
            const mv = newMarks[sub.id];
            if (mv === undefined || mv === null) { overallPass = false; hasAllMarks = false; return; }
            if (mv === "AB") { overallPass = false; return; }
            const obt = Number(mv) || 0;
            totalObtained += obt;
            if (obt < Number(sub.pass_marks)) overallPass = false;
          });
          const percentage = totalMax > 0 ? Math.round((totalObtained / totalMax) * 10000) / 100 : 0;
          return { ...r, marks: newMarks, totalObtained, totalMax, percentage, overallPass, hasAllMarks };
        });
        // recompute ranks
        const sorted = [...newRows].sort((a, b) => b.percentage - a.percentage);
        let rank = 1;
        sorted.forEach((r, i) => {
          if (i > 0 && sorted[i - 1].percentage !== r.percentage) rank = i + 1;
          r.rank = rank;
        });
        return { ...prev, rows: newRows };
      });
    } catch (err) {
      setSaveStatus((p) => ({ ...p, [key]: "error" }));
      alert(err.response?.data?.error || "Could not save mark.");
    } finally { setSavingCell(null); }
  }

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sortedRows = (() => {
    if (!sheet?.rows) return [];
    const rows = [...sheet.rows];
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortCol === "name") return rows.sort((a, b) => dir * a.name.localeCompare(b.name));
    if (sortCol === "pct") return rows.sort((a, b) => dir * (a.percentage - b.percentage));
    if (sortCol === "roll") return rows.sort((a, b) => dir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0)));
    // rank (default)
    return rows.sort((a, b) => dir * (a.rank - b.rank));
  })();

  const SortTh = ({ col, label, style = {} }) => (
    <th
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }}
      onClick={() => toggleSort(col)}
    >
      {label} {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ color: "#CBD5E1" }}>↕</span>}
    </th>
  );

  const gradeColor = (g) => {
    if (!g) return {};
    if (g === "A1" || g === "A2") return { color: "#15803D", fontWeight: 700 };
    if (g === "B1" || g === "B2") return { color: "#1D4ED8", fontWeight: 700 };
    if (g === "C1" || g === "C2") return { color: "#D97706", fontWeight: 700 };
    if (g === "D") return { color: "#EA580C", fontWeight: 700 };
    return { color: "#DC2626", fontWeight: 700 }; // E/Fail
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h2 className="page-title">Class Marks View</h2>
        <p className="page-subtitle">See all subjects' marks, totals, percentage, grade, and class rank for any class.</p>
      </div>

      {/* Selectors */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label className="form-label">Class / Section</label>
            <select className="form-control" value={selClass} onChange={(e) => setSelClass(e.target.value)}>
              <option value="">— Select Class —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Exam</label>
            <select className="form-control" value={selExam} onChange={(e) => setSelExam(e.target.value)}>
              <option value="">— Select Exam —</option>
              {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>Loading class marks…</div>}

      {!loading && (!selClass || !selExam) && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32, fontSize: 15 }}>
          Select a class and an exam to view the full marks sheet.
        </div>
      )}

      {!loading && sheet && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {/* Header bar */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                {classes.find((c) => String(c.id) === String(selClass))?.name}
                &nbsp;&mdash;&nbsp;
                {exams.find((e) => String(e.id) === String(selExam))?.name}
                <span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>{sheet.rows.length} students</span>
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>
                Click column headers to sort. Marks are editable — click a cell and press Enter or click away to save.
              </p>
              {/* Download buttons */}
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {["CSV", "Excel", "PDF"].map((fmt) => (
                  <button key={fmt} type="button" className="btn sm secondary" style={{ fontSize: 11, padding: "3px 10px" }}
                    onClick={() => {
                      const cls = classes.find((c) => String(c.id) === String(selClass));
                      const clsName = cls ? `${cls.name}${cls.section ? `-${cls.section}` : ""}` : "Class";
                      const examName = exams.find((e) => String(e.id) === String(selExam))?.name || "Exam";
                      const title = `${clsName} — ${examName} — Class Marks`;
                      const headers = ["Rank", "Roll", "Student Name", ...sheet.subjects.map((s) => `${s.name}/${s.category === "co_scholastic" ? "Grade" : (s.max_marks ?? "?")}`), "Total Obtained", "Total Max", "Percentage", "Grade", "Result"];
                      const rows = sortedRows.map((r) => [
                        r.rank === 0 ? "—" : r.rank,
                        r.rollNo ?? "—",
                        r.name,
                        ...sheet.subjects.map((s) => r.marks[s.id] !== undefined && r.marks[s.id] !== null ? r.marks[s.id] : "—"),
                        r.totalObtained,
                        r.totalMax,
                        `${r.percentage.toFixed(1)}%`,
                        r.grade || "—",
                        !r.hasAllMarks ? "Pending" : r.overallPass ? "PASS" : "FAIL",
                      ]);
                      const slug = `${clsName}_${examName}_marks`.replace(/\s+/g, "_");
                      if (fmt === "CSV")   exportCSV(`${slug}.csv`, headers, rows);
                      if (fmt === "Excel") exportExcel(`${slug}.xls`, title, headers, rows);
                      if (fmt === "PDF")   exportPDF(title, headers, rows);
                    }}
                  >
                    ⬇ {fmt}
                  </button>
                ))}
              </div>
            </div>
            {/* Subject legend */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {sheet.subjects.map((sub) => (
                <span key={sub.id} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: sub.category === "scholastic" ? "#DBEAFE" : "#F0FDF4", color: sub.category === "scholastic" ? "#1D4ED8" : "#15803D", border: `1px solid ${sub.category === "scholastic" ? "#93C5FD" : "#86EFAC"}` }}>
                  {sub.name}
                  <span style={{ fontWeight: 400, marginLeft: 4 }}>/{sub.category === "co_scholastic" ? "Grade" : (sub.max_marks ?? "?")}</span>
                </span>
              ))}
            </div>
          </div>

          {sheet.rows.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--muted)", padding: 40 }}>No students or marks found.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ fontSize: 13, minWidth: 700 }}>
                <thead>
                  <tr>
                    <SortTh col="rank" label="Rank" style={{ width: 60, textAlign: "center", position: "sticky", left: 0, background: "var(--bg)", zIndex: 2, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" }} />
                    <SortTh col="roll" label="Roll" style={{ width: 60, position: "sticky", left: 60, zIndex: 2, background: "var(--bg)" }} />
                    <SortTh col="name" label="Student Name" style={{ minWidth: 160, position: "sticky", left: 120, zIndex: 2, background: "var(--bg)", boxShadow: "2px 0 5px rgba(0,0,0,0.08)" }} />
                    {sheet.subjects.map((sub) => (
                      <th key={sub.id} style={{ minWidth: 80, textAlign: "center", whiteSpace: "nowrap" }}>
                        {sub.name}
                        <div style={{ fontSize: 10, fontWeight: 400, color: "var(--muted)" }}>/{sub.category === "co_scholastic" ? "Grade" : (sub.max_marks ?? "?")}</div>
                      </th>
                    ))}
                    <SortTh col="pct" label="Total" style={{ width: 100, textAlign: "center" }} />
                    <th style={{ width: 80, textAlign: "center" }}>%</th>
                    <th style={{ width: 70, textAlign: "center" }}>Grade</th>
                    <th style={{ width: 80, textAlign: "center" }}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const pct = row.percentage;
                    const rowBg = !row.hasAllMarks ? undefined : !row.overallPass ? "#FEF2F2" : pct >= 75 ? "#F0FDF4" : undefined;
                    return (
                      <tr key={row.studentId} style={{ background: rowBg }}>
                        <td style={{ textAlign: "center", position: "sticky", left: 0, background: rowBg || "#fff", zIndex: 1, boxShadow: "2px 0 4px rgba(0,0,0,0.06)", fontWeight: 700 }}>
                          {!row.rank
                            ? <span style={{ color: "var(--muted)" }}>—</span>
                            : row.rank <= 3
                              ? <span style={{ fontSize: 15 }}>{row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : "🥉"}</span>
                              : <span style={{ fontWeight: 600, color: "var(--muted)" }}>{row.rank}</span>}
                        </td>
                        <td style={{ color: "var(--muted)", fontWeight: 500, position: "sticky", left: 60, zIndex: 1, background: rowBg || "#fff" }}>{row.rollNo ?? "—"}</td>
                        <td style={{ fontWeight: 600, position: "sticky", left: 120, zIndex: 1, background: rowBg || "#fff", boxShadow: "2px 0 5px rgba(0,0,0,0.08)" }}>{row.name}</td>
                        {sheet.subjects.map((sub) => {
                          const cellKey = `${row.studentId}-${sub.id}`;
                          const val = row.marks[sub.id];
                          const isSaving = savingCell === cellKey;
                          const status = saveStatus[cellKey];
                          const isAbsent = val === "AB";
                          const isFail = !isAbsent && val !== undefined && val !== null && sub.category === "scholastic" && Number(val) < (Number(sub.pass_marks) || 0);
                          return (
                            <td key={sub.id} style={{ textAlign: "center", padding: "4px 6px" }}>
                              <div style={{ position: "relative", display: "inline-block" }}>
                                <input
                                  type="text"
                                  defaultValue={isAbsent ? "AB" : (val !== undefined && val !== null ? String(val) : "")}
                                  key={`${row.studentId}-${sub.id}-${val}`}
                                  disabled={isSaving}
                                  onBlur={(e) => {
                                    const v = e.target.value.trim().toUpperCase();
                                    if (v !== String(val ?? "").toUpperCase()) saveCellMark(row.studentId, sub.id, v, sub.category === "co_scholastic");
                                  }}
                                  onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                                  placeholder="—"
                                  style={{
                                    width: 64, textAlign: "center", fontWeight: 700, fontSize: 13,
                                    padding: "4px 3px", borderRadius: 7,
                                    border: `1px solid ${status === "error" ? "#FCA5A5" : isFail ? "#FCA5A5" : isAbsent ? "#FCD34D" : status === "saved" ? "#86EFAC" : "#E2E8F0"}`,
                                    background: isSaving ? "#F8FAFC" : isAbsent ? "#FFFBEB" : isFail ? "#FEF2F2" : "#fff",
                                    color: isAbsent ? "#D97706" : isFail ? "#DC2626" : "#1E293B",
                                  }}
                                />
                                {status === "saved" && <span style={{ position: "absolute", top: -6, right: -4, fontSize: 10, color: "#16A34A", fontWeight: 900 }}>✓</span>}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "center", fontWeight: 700 }}>
                          {row.totalObtained} <span style={{ color: "var(--muted)", fontWeight: 400 }}>/ {row.totalMax}</span>
                        </td>
                        <td style={{ textAlign: "center", fontWeight: 700, ...gradeColor(row.grade) }}>
                          {pct.toFixed(1)}%
                        </td>
                        <td style={{ textAlign: "center", fontWeight: 800, ...gradeColor(row.grade) }}>
                          {row.grade}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{
                            display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: !row.hasAllMarks ? "#F1F5F9" : row.overallPass ? "#D1FAE5" : "#FEE2E2",
                            color: !row.hasAllMarks ? "#64748B" : row.overallPass ? "#065F46" : "#991B1B",
                            border: `1px solid ${!row.hasAllMarks ? "#CBD5E1" : row.overallPass ? "#6EE7B7" : "#FCA5A5"}`,
                          }}>
                            {!row.hasAllMarks ? "Pending" : row.overallPass ? "PASS" : "FAIL"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#F8FAFC", fontWeight: 700, borderTop: "2px solid var(--border)" }}>
                    <td colSpan={3} style={{ padding: "10px 14px", fontSize: 13, position: "sticky", left: 0, background: "#F8FAFC", zIndex: 1, boxShadow: "2px 0 4px rgba(0,0,0,0.06)" }}>
                      Class Average
                    </td>
                    {sheet.subjects.map((sub) => {
                      const vals = sheet.rows.map((r) => r.marks[sub.id]).filter((v) => v !== undefined && v !== null && v !== "AB").map(Number);
                      const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "—";
                      return <td key={sub.id} style={{ textAlign: "center", color: "var(--muted)" }}>{avg}</td>;
                    })}
                    <td style={{ textAlign: "center" }}>
                      {(() => {
                        const totals = sheet.rows.map((r) => r.totalObtained);
                        return (totals.reduce((a, b) => a + b, 0) / (totals.length || 1)).toFixed(1);
                      })()}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {(() => {
                        const pcts = sheet.rows.map((r) => r.percentage);
                        return (pcts.reduce((a, b) => a + b, 0) / (pcts.length || 1)).toFixed(1) + "%";
                      })()}
                    </td>
                    <td />
                    <td style={{ textAlign: "center", fontSize: 12, color: "var(--muted)" }}>
                      {sheet.rows.filter((r) => r.overallPass).length}/{sheet.rows.length} Pass
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <p style={{ padding: "10px 20px 14px", fontSize: 12, color: "var(--muted)", margin: 0 }}>
            Type a mark or "AB" for absent and press Enter / click away to save. Cells highlighted in red indicate a failing mark.
          </p>
        </div>
      )}
    </div>
  );
}

function AdminMarksEntry() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selSubject, setSelSubject] = useState("");
  const [selExam, setSelExam] = useState("");
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());
  const [saveStatus, setSaveStatus] = useState({}); // { studentId: "saved"|"error" }
  const [localMarks, setLocalMarks] = useState({}); // { studentId: string } — typed but unsaved
  const [dirty, setDirty] = useState(new Set()); // studentIds with unsaved changes
  const [savingAll, setSavingAll] = useState(false);
  const [sortCol, setSortCol] = useState("roll");
  const [sortDir, setSortDir] = useState("asc");
  const [csvPreview, setCsvPreview] = useState(null); // [{ studentId, rollNo, name, marksObtained, _error }]
  const [csvError, setCsvError] = useState("");
  const [csvSuccess, setCsvSuccess] = useState("");
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvDragOver, setCsvDragOver] = useState(false);
  const csvInputRef = useRef(null);

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/school-admin/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    api.get("/school-admin/exams").then((r) => setExams(r.data)).catch(() => {});
  }, []);

  async function loadSheet() {
    if (!selClass || !selSubject || !selExam) return;
    setLoading(true); setSheet(null); setLocalMarks({}); setDirty(new Set());
    try {
      const { data } = await api.get("/school-admin/marks-sheet", {
        params: { classSectionId: selClass, subjectId: selSubject, examId: selExam },
      });
      setSheet(data);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to load marks sheet.");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadSheet(); }, [selClass, selSubject, selExam]); // eslint-disable-line

  function onMarksChange(studentId, value) {
    setLocalMarks((p) => ({ ...p, [studentId]: value }));
    setDirty((p) => { const s = new Set(p); s.add(studentId); return s; });
    setSaveStatus((p) => ({ ...p, [studentId]: null }));
  }

  async function saveOneMark(row) {
    const rawValue = localMarks[row.studentId] ?? (isCoScholastic ? (row.gradeValue ?? "") : String(row.marksObtained ?? ""));
    if (!isCoScholastic && rawValue !== "") {
      const num = Number(rawValue);
      if (isNaN(num) || num < 0) { alert("Marks must be a non-negative number."); return; }
      if (num > maxMarks) { alert(`Marks cannot exceed maximum marks (${maxMarks}).`); return; }
    }
    setSavingIds((p) => { const s = new Set(p); s.add(row.studentId); return s; });
    try {
      const payload = { studentId: row.studentId, subjectId: selSubject, examId: selExam };
      if (isCoScholastic) {
        payload.gradeValue = rawValue || null;
        payload.marksObtained = null;
      } else {
        payload.marksObtained = rawValue === "" ? null : Number(rawValue);
        payload.gradeValue = null;
      }
      await api.post("/school-admin/marks", payload);
      setSaveStatus((p) => ({ ...p, [row.studentId]: "saved" }));
      setDirty((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
      setSheet((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => r.studentId === row.studentId
          ? { ...r, marksObtained: payload.marksObtained, gradeValue: payload.gradeValue, isAbsent: false } : r),
      }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [row.studentId]: null })), 3000);
    } catch (err) {
      setSaveStatus((p) => ({ ...p, [row.studentId]: "error" }));
      alert(err.response?.data?.error || "Could not save mark.");
    } finally {
      setSavingIds((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
    }
  }

  async function saveAllMarks() {
    const dirtyRows = sheet.rows.filter((r) => dirty.has(r.studentId) && !r.isAbsent);
    if (!dirtyRows.length) return;
    setSavingAll(true);
    for (const row of dirtyRows) await saveOneMark(row);
    setSavingAll(false);
  }

  async function saveMark(row, value) { // kept for absent toggle compat
    const parsed = value === "" ? null : Number(value);
    setSavingIds((p) => { const s = new Set(p); s.add(row.studentId); return s; });
    try {
      await api.post("/school-admin/marks", {
        studentId: row.studentId, subjectId: selSubject, examId: selExam,
        marksObtained: parsed, gradeValue: null,
      });
      setSaveStatus((p) => ({ ...p, [row.studentId]: "saved" }));
      setDirty((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
      setSheet((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => r.studentId === row.studentId
          ? { ...r, marksObtained: parsed, isAbsent: false, gradeValue: null } : r),
      }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [row.studentId]: null })), 3000);
    } catch (err) {
      setSaveStatus((p) => ({ ...p, [row.studentId]: "error" }));
      alert(err.response?.data?.error || "Could not save mark.");
    } finally {
      setSavingIds((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
    }
  }

  const selSubjectObj = subjects.find((s) => String(s.id) === String(selSubject));
  const isCoScholastic = selSubjectObj?.category === "co_scholastic";
  const gradeScale = isCoScholastic ? (selSubjectObj?.grade_scale || []) : [];
  const maxMarks = selSubjectObj?.max_marks ?? 100;
  const passMarks = selSubjectObj?.pass_marks ?? 33;

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sortedRows = (() => {
    if (!sheet?.rows) return [];
    const rows = [...sheet.rows];
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortCol === "name") return rows.sort((a, b) => dir * a.name.localeCompare(b.name));
    return rows.sort((a, b) => {
      const ra = parseInt(a.rollNo) || 0, rb = parseInt(b.rollNo) || 0;
      return dir * (ra !== rb ? ra - rb : (a.rollNo || "").localeCompare(b.rollNo || ""));
    });
  })();

  async function toggleAbsent(row) {
    const nowAbsent = !row.isAbsent;
    setSavingIds((p) => { const s = new Set(p); s.add(row.studentId); return s; });
    try {
      await api.post("/school-admin/marks", {
        studentId: row.studentId, subjectId: selSubject, examId: selExam,
        marksObtained: null, gradeValue: nowAbsent ? "AB" : null,
      });
      setSaveStatus((p) => ({ ...p, [row.studentId]: "saved" }));
      setDirty((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
      setLocalMarks((p) => { const m = { ...p }; delete m[row.studentId]; return m; });
      setSheet((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => r.studentId === row.studentId
          ? { ...r, isAbsent: nowAbsent, marksObtained: null, gradeValue: nowAbsent ? "AB" : null } : r),
      }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [row.studentId]: null })), 3000);
    } catch (err) {
      alert(err.response?.data?.error || "Could not update absent status.");
    } finally { setSavingIds((p) => { const s = new Set(p); s.delete(row.studentId); return s; }); }
  }

  function downloadAdminSampleCsv() {
    if (!sheet) return;
    const header = "roll_no,student_name,marks_obtained";
    const rows = sheet.rows.map((r) => `${r.rollNo ?? ""},${r.name},`).join("\n");
    const blob = new Blob([header + "\n" + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "marks_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  function parseAdminCsvText(text) {
    setCsvError(""); setCsvSuccess(""); setCsvPreview(null);
    if (!sheet) { setCsvError("Load a marks sheet first."); return; }
    const lines = text.trim().split("\n").filter(Boolean);
    if (lines.length < 2) { setCsvError("CSV must have a header row and at least one data row."); return; }
    const header = lines[0].toLowerCase().replace(/\r/g, "").split(",").map((h) => h.trim());
    const rollIdx = header.indexOf("roll_no");
    const marksIdx = header.indexOf("marks_obtained");
    if (rollIdx === -1 || marksIdx === -1) { setCsvError("CSV must have columns: roll_no, marks_obtained"); return; }

    const rollMap = Object.fromEntries(sheet.rows.map((r) => [String(r.rollNo ?? "").trim(), r]));
    const parsed = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].replace(/\r/g, "").split(",");
      const roll = (cols[rollIdx] || "").trim();
      const marksRaw = (cols[marksIdx] || "").trim();
      const student = rollMap[roll];
      if (!student) { parsed.push({ roll, name: "?", marksObtained: marksRaw, _error: `Roll ${roll} not found` }); continue; }
      if (marksRaw !== "" && (isNaN(Number(marksRaw)) || Number(marksRaw) < 0 || Number(marksRaw) > maxMarks)) {
        parsed.push({ ...student, marksObtained: marksRaw, _error: `Invalid marks: ${marksRaw}` }); continue;
      }
      parsed.push({ studentId: student.studentId, rollNo: student.rollNo, name: student.name, marksObtained: marksRaw === "" ? null : Number(marksRaw), _error: null });
    }
    setCsvPreview(parsed);
  }

  function handleAdminCsvFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => parseAdminCsvText(ev.target.result);
    reader.readAsText(file);
  }

  async function confirmAdminCsvUpload() {
    if (!csvPreview) return;
    const valid = csvPreview.filter((r) => !r._error);
    if (!valid.length) { setCsvError("No valid rows to upload."); return; }
    setCsvUploading(true); setCsvError(""); setCsvSuccess("");
    try {
      const records = valid.map((r) => ({ studentId: r.studentId, marksObtained: r.marksObtained }));
      // Admin bulk-save: call individual marks endpoint for each (admin has no panel restriction)
      let saved = 0;
      for (const rec of records) {
        await api.post("/school-admin/marks", {
          studentId: rec.studentId, subjectId: selSubject, examId: selExam,
          marksObtained: rec.marksObtained, gradeValue: null,
        });
        saved++;
      }
      setCsvSuccess(`${saved} mark(s) uploaded successfully.`);
      setCsvPreview(null);
      loadSheet();
    } catch (err) {
      setCsvError(err.response?.data?.error || "Upload failed.");
    } finally { setCsvUploading(false); }
  }

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h2 className="page-title">Marks Entry</h2>
        <p className="page-subtitle">Enter or edit marks for any class and subject directly.</p>
      </div>

      {/* Selectors */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label className="form-label">Class / Section</label>
            <select className="form-control" value={selClass} onChange={(e) => { setSelClass(e.target.value); setSelSubject(""); setSheet(null); }}>
              <option value="">— Select Class —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Subject</label>
            <select className="form-control" value={selSubject} onChange={(e) => { setSelSubject(e.target.value); setSheet(null); }}>
              <option value="">— Select Subject —</option>
              {subjects.filter((s) => String(s.class_section_id) === String(selClass)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Exam</label>
            <select className="form-control" value={selExam} onChange={(e) => { setSelExam(e.target.value); setSheet(null); }}>
              <option value="">— Select Exam —</option>
              {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>
        {selSubjectObj && (
          <div style={{ marginTop: 10, fontSize: 13, color: "var(--muted)" }}>
            Max Marks: <strong>{maxMarks}</strong> &nbsp;|&nbsp; Pass Marks: <strong>{passMarks}</strong>
          </div>
        )}
      </div>

      {/* Sheet */}
      {loading && <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>Loading marks sheet…</div>}

      {!loading && sheet && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                {classes.find((c) => String(c.id) === String(selClass))?.name} &mdash; {selSubjectObj?.name} &mdash; {exams.find((e) => String(e.id) === String(selExam))?.name}
                <span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>{sheet.rows.length} students</span>
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>Click column headers to sort. Enter marks and click Save. Check Absent to mark absent.</p>
            </div>
            {dirty.size > 0 && (
              <button
                className="btn"
                onClick={saveAllMarks}
                disabled={savingAll}
                style={{ background: "#16A34A", borderColor: "#16A34A", fontWeight: 700, minWidth: 140 }}
              >
                {savingAll ? "Saving…" : `💾 Save All (${dirty.size})`}
              </button>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }} onClick={() => toggleSort("roll")}>
                    Roll No {sortCol === "roll" ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ color: "#CBD5E1" }}>↕</span>}
                  </th>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("name")}>
                    Student Name {sortCol === "name" ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ color: "#CBD5E1" }}>↕</span>}
                  </th>
                  <th style={{ width: 130, textAlign: "center" }}>{isCoScholastic ? "Grade" : `Marks (/${maxMarks})`}</th>
                  <th style={{ width: 80, textAlign: "center" }}>Absent</th>
                  <th style={{ width: 110, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const isSaving = savingIds.has(row.studentId);
                  const isDirty = dirty.has(row.studentId);
                  const status = saveStatus[row.studentId];
                  const currentVal = isCoScholastic
                    ? (localMarks[row.studentId] ?? (row.gradeValue || ""))
                    : (localMarks[row.studentId] ?? (row.marksObtained !== null ? String(row.marksObtained) : ""));
                  const isFail = !isCoScholastic && !row.isAbsent && row.marksObtained !== null && !isDirty && Number(row.marksObtained) < Number(passMarks);
                  return (
                    <tr key={row.studentId} style={{ background: row.isAbsent ? "#FFFBEB" : isDirty ? "#F0FDF4" : isFail ? "#FEF2F2" : undefined }}>
                      <td style={{ color: "var(--muted)", fontWeight: 600 }}>{row.rollNo ?? "—"}</td>
                      <td style={{ fontWeight: 600 }}>{row.name}</td>
                      <td style={{ textAlign: "center" }}>
                        {row.isAbsent
                          ? <span style={{ color: "#D97706", fontWeight: 700, fontSize: 13 }}>Absent</span>
                          : isCoScholastic
                            ? <select
                                value={currentVal}
                                disabled={isSaving}
                                onChange={(e) => onMarksChange(row.studentId, e.target.value)}
                                style={{
                                  width: 110, textAlign: "center", fontSize: 14, fontWeight: 700,
                                  padding: "5px 6px", borderRadius: 8,
                                  border: `2px solid ${isDirty ? "#86EFAC" : "var(--border)"}`,
                                  background: isSaving ? "#F8FAFC" : isDirty ? "#F0FDF4" : "#fff",
                                  outline: "none", cursor: "pointer",
                                }}
                              >
                                <option value="">— Grade —</option>
                                {gradeScale.map((g) => (
                                  <option key={g.grade} value={g.grade}>{g.grade}{g.label ? ` — ${g.label}` : ""}</option>
                                ))}
                              </select>
                            : <input
                                type="number"
                                min={0}
                                max={maxMarks}
                                value={currentVal}
                                disabled={isSaving}
                                onChange={(e) => onMarksChange(row.studentId, e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && saveOneMark(row)}
                                style={{
                                  width: 90, textAlign: "center", fontSize: 15, fontWeight: 700,
                                  padding: "5px 6px", borderRadius: 8,
                                  border: `2px solid ${isDirty ? "#86EFAC" : isFail ? "#FCA5A5" : "var(--border)"}`,
                                  background: isSaving ? "#F8FAFC" : isDirty ? "#F0FDF4" : "#fff",
                                  outline: "none",
                                }}
                                placeholder="—"
                              />
                        }
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={!!row.isAbsent}
                            disabled={isSaving}
                            onChange={() => toggleAbsent(row)}
                            style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#D97706" }}
                          />
                          {row.isAbsent && <span style={{ fontSize: 11, color: "#D97706", fontWeight: 700 }}>AB</span>}
                        </label>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {isSaving && <span style={{ color: "var(--muted)", fontSize: 12 }}>Saving…</span>}
                        {!isSaving && isDirty && (
                          <button
                            className="btn sm"
                            onClick={() => saveOneMark(row)}
                            style={{ fontSize: 12, padding: "4px 12px", background: "#16A34A", borderColor: "#16A34A" }}
                          >
                            💾 Save
                          </button>
                        )}
                        {!isSaving && !isDirty && status === "saved" && (
                          <span style={{ color: "#16A34A", fontSize: 13, fontWeight: 700 }}>✓ Saved</span>
                        )}
                        {!isSaving && !isDirty && status === "error" && (
                          <span style={{ color: "var(--danger)", fontSize: 12, fontWeight: 600 }}>✕ Error</span>
                        )}
                        {!isSaving && !isDirty && !status && row.isAbsent && (
                          <span className="badge warn" style={{ fontSize: 11 }}>Absent</span>
                        )}
                        {!isSaving && !isDirty && !status && !row.isAbsent && isCoScholastic && row.gradeValue && (
                          <span className="badge active" style={{ fontSize: 11 }}>{row.gradeValue}</span>
                        )}
                        {!isSaving && !isDirty && !status && !row.isAbsent && isCoScholastic && !row.gradeValue && (
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>Not entered</span>
                        )}
                        {!isSaving && !isDirty && !status && !row.isAbsent && !isCoScholastic && row.marksObtained !== null && (
                          <span className={`badge ${isFail ? "danger" : "active"}`} style={{ fontSize: 11 }}>
                            {isFail ? "Fail" : "Pass"}
                          </span>
                        )}
                        {!isSaving && !isDirty && !status && !row.isAbsent && !isCoScholastic && row.marksObtained === null && (
                          <span style={{ color: "var(--muted)", fontSize: 12 }}>Not entered</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 12 }}>
            Enter marks and click 💾 Save per row, or use "Save All" to save all at once. Press Enter to save a row quickly.
          </p>
        </div>
      )}

      {/* CSV Upload Section — only for scholastic (numeric) subjects */}
      {!loading && sheet && !isCoScholastic && (
        <div className="card" style={{ marginTop: 18, border: "2px dashed #C4B5FD", background: "#FAFAFF" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#6D28D9" }}>📤 Bulk Upload via CSV</h3>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--muted)" }}>
                Download the template, fill in marks, then upload to save all at once.
              </p>
            </div>
            <button
              className="btn"
              onClick={downloadAdminSampleCsv}
              style={{ background: "#EDE9FE", color: "#6D28D9", borderColor: "#C4B5FD", fontSize: 13 }}
            >
              ⬇ Download Sample CSV
            </button>
          </div>

          {/* Drag-drop zone */}
          <div
            onClick={() => csvInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setCsvDragOver(true); }}
            onDragLeave={() => setCsvDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setCsvDragOver(false);
              const file = e.dataTransfer.files[0];
              if (file) handleAdminCsvFile(file);
            }}
            style={{
              border: `2px dashed ${csvDragOver ? "#7C3AED" : "#C4B5FD"}`,
              borderRadius: 12, padding: "28px 16px", textAlign: "center", cursor: "pointer",
              background: csvDragOver ? "#EDE9FE" : "#F5F3FF", transition: "all 0.2s",
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 30, marginBottom: 6 }}>📁</div>
            <p style={{ margin: 0, fontSize: 14, color: "#6D28D9", fontWeight: 600 }}>Click or drag a CSV file here</p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>Columns: roll_no, marks_obtained</p>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => { handleAdminCsvFile(e.target.files[0]); e.target.value = ""; }}
            />
          </div>

          {csvError && <div style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "10px 14px", color: "#991B1B", fontSize: 13, marginBottom: 10 }}>⚠ {csvError}</div>}
          {csvSuccess && <div style={{ background: "#D1FAE5", border: "1px solid #6EE7B7", borderRadius: 8, padding: "10px 14px", color: "#065F46", fontSize: 13, marginBottom: 10 }}>✅ {csvSuccess}</div>}

          {csvPreview && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  Preview: &nbsp;
                  <span style={{ color: "#16A34A" }}>✓ {csvPreview.filter((r) => !r._error).length} valid</span>
                  {csvPreview.some((r) => r._error) && (
                    <span style={{ color: "#DC2626", marginLeft: 10 }}>✕ {csvPreview.filter((r) => r._error).length} errors</span>
                  )}
                </div>
                <button
                  className="btn"
                  onClick={confirmAdminCsvUpload}
                  disabled={csvUploading || csvPreview.filter((r) => !r._error).length === 0}
                  style={{ background: "#16A34A", borderColor: "#16A34A", fontWeight: 700 }}
                >
                  {csvUploading ? "Uploading…" : `✅ Upload ${csvPreview.filter((r) => !r._error).length} rows`}
                </button>
              </div>
              <div style={{ overflowX: "auto", maxHeight: 280, overflowY: "auto" }}>
                <table style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Roll No</th>
                      <th>Student</th>
                      <th style={{ width: 120, textAlign: "center" }}>Marks</th>
                      <th style={{ width: 80, textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((r, i) => (
                      <tr key={i} style={{ background: r._error ? "#FEF2F2" : undefined }}>
                        <td style={{ color: "var(--muted)" }}>{r.rollNo ?? r.roll ?? "—"}</td>
                        <td>{r.name}</td>
                        <td style={{ textAlign: "center", fontWeight: 600 }}>{r.marksObtained !== null && r.marksObtained !== undefined ? r.marksObtained : "—"}</td>
                        <td style={{ textAlign: "center" }}>
                          {r._error
                            ? <span style={{ color: "#DC2626", fontSize: 11, fontWeight: 700 }}>✕ {r._error}</span>
                            : <span style={{ color: "#16A34A", fontSize: 12, fontWeight: 700 }}>✓ Ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {!loading && !sheet && selClass && selSubject && selExam && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>No students found for this class.</div>
      )}

      {!loading && !sheet && (!selClass || !selSubject || !selExam) && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32, fontSize: 15 }}>
          Select a class, subject, and exam above to start entering marks.
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Result Publication
   ====================================================== */
const HOLD_TEMPLATES_KEY = "sa_hold_reason_templates";
const DEFAULT_TEMPLATES = [
  "Fees not cleared",
  "Incomplete examination",
  "Disciplinary action pending",
  "Document verification pending",
  "Result under review",
];

function loadTemplates() {
  try {
    const saved = localStorage.getItem(HOLD_TEMPLATES_KEY);
    return saved ? JSON.parse(saved) : [...DEFAULT_TEMPLATES];
  } catch { return [...DEFAULT_TEMPLATES]; }
}

function Publication() {
  const [classes, setClasses]       = useState([]);
  const [exams, setExams]           = useState([]);
  const [selClass, setSelClass]     = useState("");
  const [selExam, setSelExam]       = useState("");
  const [students, setStudents]     = useState([]); // [{ studentId, name, rollNo, isPublished, withheldReason }]
  const [localStatus, setLocalStatus] = useState({}); // { studentId: { isPublished, withheldReason } }
  const [dirty, setDirty]           = useState(new Set());
  const [saving, setSaving]         = useState(null); // studentId
  const [savingAll, setSavingAll]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [saveMsg, setSaveMsg]       = useState("");
  const [sortBy, setSortBy]         = useState("roll");
  const [sortDir, setSortDir]       = useState("asc");
  const [search, setSearch]         = useState("");

  // Reason templates
  const [templates, setTemplates]   = useState(loadTemplates);
  const [newTemplate, setNewTemplate] = useState("");
  const [showTplMgr, setShowTplMgr] = useState(false);

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/school-admin/exams").then((r) => setExams(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selClass || !selExam) { setStudents([]); setLocalStatus({}); setDirty(new Set()); return; }
    setLoading(true); setSaveMsg("");
    api.get("/school-admin/publication/class-status", { params: { classSectionId: selClass, examId: selExam } })
      .then((r) => {
        setStudents(r.data);
        const ls = {};
        r.data.forEach((s) => { ls[s.studentId] = { isPublished: s.isPublished, withheldReason: s.withheldReason || "" }; });
        setLocalStatus(ls);
        setDirty(new Set());
      })
      .catch((err) => alert(err.response?.data?.error || "Failed to load students."))
      .finally(() => setLoading(false));
  }, [selClass, selExam]);

  function setStudentStatus(studentId, isPublished) {
    setLocalStatus((p) => ({ ...p, [studentId]: { ...p[studentId], isPublished } }));
    setDirty((p) => { const s = new Set(p); s.add(studentId); return s; });
    setSaveMsg("");
  }

  function setStudentReason(studentId, withheldReason) {
    setLocalStatus((p) => ({ ...p, [studentId]: { ...p[studentId], withheldReason } }));
    setDirty((p) => { const s = new Set(p); s.add(studentId); return s; });
  }

  async function saveOne(studentId) {
    const st = localStatus[studentId];
    setSaving(studentId);
    try {
      await api.post("/school-admin/publication", {
        studentId, examId: Number(selExam),
        isPublished: st.isPublished,
        withheldReason: st.isPublished ? null : (st.withheldReason || null),
      });
      setDirty((p) => { const s = new Set(p); s.delete(studentId); return s; });
      setStudents((prev) => prev.map((x) => x.studentId === studentId
        ? { ...x, isPublished: st.isPublished, withheldReason: st.withheldReason } : x));
    } catch (err) { alert(err.response?.data?.error || "Could not save."); }
    finally { setSaving(null); }
  }

  async function saveAll() {
    const dirtyIds = [...dirty];
    if (!dirtyIds.length) return;
    setSavingAll(true); setSaveMsg("");
    for (const id of dirtyIds) await saveOne(id);
    setSavingAll(false);
    setSaveMsg(`Saved ${dirtyIds.length} student(s) successfully.`);
    setTimeout(() => setSaveMsg(""), 3000);
  }

  async function bulkAction(isPublished, reason = "") {
    const confirmMsg = isPublished
      ? `Publish all students' results for this class/exam? They will become visible in the student portal.`
      : `Unpublish ALL students' results for this class/exam? They will be hidden from the student portal.${reason ? ` Reason: "${reason}"` : ""}`;
    if (!window.confirm(confirmMsg)) return;
    setSavingAll(true); setSaveMsg("");
    try {
      await api.post("/school-admin/publication/bulk", {
        classSectionId: selClass, examId: Number(selExam), isPublished, withheldReason: reason || null,
      });
      // Reload
      const { data } = await api.get("/school-admin/publication/class-status", { params: { classSectionId: selClass, examId: selExam } });
      setStudents(data);
      const ls = {};
      data.forEach((s) => { ls[s.studentId] = { isPublished: s.isPublished, withheldReason: s.withheldReason || "" }; });
      setLocalStatus(ls);
      setDirty(new Set());
      setSaveMsg(isPublished ? "All results published — now visible to students." : "All results unpublished — hidden from students.");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (err) { alert(err.response?.data?.error || "Bulk action failed."); }
    finally { setSavingAll(false); }
  }

  function saveTemplates(tpls) {
    setTemplates(tpls);
    localStorage.setItem(HOLD_TEMPLATES_KEY, JSON.stringify(tpls));
  }

  function addTemplate() {
    const t = newTemplate.trim();
    if (!t || templates.includes(t)) return;
    saveTemplates([...templates, t]);
    setNewTemplate("");
  }

  function removeTemplate(t) {
    saveTemplates(templates.filter((x) => x !== t));
  }

  const dir = sortDir === "asc" ? 1 : -1;
  const displayed = students
    .filter((s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || String(s.rollNo ?? "").includes(search))
    .sort((a, b) =>
      sortBy === "name"
        ? dir * a.name.localeCompare(b.name)
        : dir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0))
    );

  const holdCount = Object.values(localStatus).filter((s) => !s.isPublished).length;
  const publishedCount = students.length - holdCount;

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h2 className="page-title">Result Publication Control</h2>
        <p className="page-subtitle">
          Results are <strong>not visible to students</strong> until you explicitly publish them.
          You can unpublish anytime — put results on hold with an optional reason shown to the student.
        </p>
      </div>

      {/* Selectors */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label className="form-label">Class / Section</label>
            <select className="form-control" value={selClass} onChange={(e) => setSelClass(e.target.value)}>
              <option value="">— Select Class —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.section}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Exam</label>
            <select className="form-control" value={selExam} onChange={(e) => setSelExam(e.target.value)}>
              <option value="">— Select Exam —</option>
              {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Reason Templates Manager */}
      <div className="card" style={{ marginBottom: 18, borderLeft: "4px solid #7C3AED" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
          onClick={() => setShowTplMgr((v) => !v)}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#6D28D9" }}>📋 Reason Templates</h3>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>
              Saved reasons you can quickly apply when putting a result on hold.
            </p>
          </div>
          <span style={{ fontSize: 18, color: "#7C3AED" }}>{showTplMgr ? "▲" : "▼"}</span>
        </div>
        {showTplMgr && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {templates.map((t) => (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "#EDE9FE", border: "1px solid #C4B5FD" }}>
                  <span style={{ fontSize: 13, color: "#6D28D9" }}>{t}</span>
                  <button onClick={() => removeTemplate(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
                </div>
              ))}
              {templates.length === 0 && <span style={{ fontSize: 13, color: "var(--muted)" }}>No templates yet.</span>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={newTemplate}
                onChange={(e) => setNewTemplate(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTemplate()}
                placeholder="New reason template…"
                style={{ flex: 1, fontSize: 13, padding: "7px 12px", borderRadius: 10, border: "1px solid #E2E8F0" }}
              />
              <button className="btn sm" onClick={addTemplate} disabled={!newTemplate.trim()} style={{ background: "#7C3AED", borderColor: "#7C3AED" }}>
                + Add
              </button>
            </div>
          </div>
        )}
      </div>

      {loading && <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>Loading students…</div>}

      {!loading && (!selClass || !selExam) && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32, fontSize: 15 }}>
          Select a class and exam to manage result publication.
        </div>
      )}

      {!loading && students.length === 0 && selClass && selExam && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: 32 }}>No students found.</div>
      )}

      {!loading && students.length > 0 && (
        <div className="card">
          {/* Summary bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <span style={{ padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700, background: "#D1FAE5", color: "#065F46", border: "1px solid #6EE7B7" }}>
                ✅ Published: {publishedCount}
              </span>
              <span style={{ padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700, background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}>
                🔒 Unpublished: {holdCount}
              </span>
              {dirty.size > 0 && (
                <span style={{ padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 700, background: "#DBEAFE", color: "#1D4ED8", border: "1px solid #93C5FD" }}>
                  ✏️ Unsaved: {dirty.size}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn sm secondary" onClick={() => bulkAction(true)} disabled={savingAll} style={{ fontSize: 12 }}>
                ✅ Publish All
              </button>
              <button className="btn sm secondary" onClick={() => bulkAction(false)} disabled={savingAll} style={{ fontSize: 12, background: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" }}>
                🔒 Unpublish All
              </button>
              {dirty.size > 0 && (
                <button className="btn" onClick={saveAll} disabled={savingAll} style={{ fontWeight: 700, fontSize: 12 }}>
                  {savingAll ? "Saving…" : `💾 Save Changes (${dirty.size})`}
                </button>
              )}
            </div>
          </div>

          {saveMsg && (
            <div style={{ background: "#D1FAE5", border: "1px solid #6EE7B7", borderRadius: 8, padding: "8px 14px", color: "#065F46", fontSize: 13, marginBottom: 14 }}>
              ✅ {saveMsg}
            </div>
          )}

          {/* Search + Sort */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
            <input
              type="text" placeholder="Search name or roll no…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 160, fontSize: 13, padding: "7px 12px", borderRadius: 10, border: "1px solid #E2E8F0" }}
            />
            <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Sort:</span>
            {[{ val: "roll", label: "Roll No" }, { val: "name", label: "A–Z" }].map(({ val, label }) => {
              const active = sortBy === val;
              return (
                <button key={val}
                  onClick={() => { if (sortBy === val) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(val); setSortDir("asc"); } }}
                  style={{
                    padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                    background: active ? "#EDE9FE" : "#F1F5F9", color: active ? "#6D28D9" : "#64748B",
                    border: `1px solid ${active ? "#C4B5FD" : "#E2E8F0"}`,
                  }}
                >
                  {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              );
            })}
          </div>

          {/* Student list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {displayed.map((s) => {
              const ls = localStatus[s.studentId] ?? { isPublished: true, withheldReason: "" };
              const isDirty = dirty.has(s.studentId);
              const isSaving = saving === s.studentId;
              return (
                <div key={s.studentId} style={{
                  border: `1px solid ${isDirty ? "#93C5FD" : ls.isPublished ? "#BBF7D0" : "#FCD34D"}`,
                  borderRadius: 12, padding: "14px 16px",
                  background: isDirty ? "#EFF6FF" : ls.isPublished ? "#F0FDF4" : "#FFFBEB",
                  transition: "background .2s",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    {/* Student info */}
                    <div style={{ minWidth: 160, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>
                        Roll: {s.rollNo ?? "—"}{s.admissionNo ? ` · ${s.admissionNo}` : ""}
                      </div>
                    </div>

                    {/* Status toggle */}
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`status-${s.studentId}`}
                          checked={ls.isPublished}
                          onChange={() => setStudentStatus(s.studentId, true)}
                        />
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#16A34A" }}>✅ Published</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`status-${s.studentId}`}
                          checked={!ls.isPublished}
                          onChange={() => setStudentStatus(s.studentId, false)}
                        />
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#D97706" }}>🔒 Unpublished</span>
                      </label>
                    </div>

                    {/* Save button */}
                    <div style={{ flexShrink: 0 }}>
                      {isSaving
                        ? <span style={{ fontSize: 12, color: "var(--muted)" }}>Saving…</span>
                        : isDirty
                          ? <button className="btn sm" onClick={() => saveOne(s.studentId)} style={{ fontSize: 12 }}>💾 Save</button>
                          : <span style={{ fontSize: 12, color: ls.isPublished ? "#16A34A" : "#D97706", fontWeight: 700 }}>
                              {ls.isPublished ? "✓ Published" : "🔒 Unpublished"}
                            </span>
                      }
                    </div>
                  </div>

                  {/* Reason row — shown when on hold */}
                  {!ls.isPublished && (
                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {/* Template quick-select */}
                      {templates.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) setStudentReason(s.studentId, e.target.value); }}
                          style={{ fontSize: 12, padding: "5px 8px", borderRadius: 8, border: "1px solid #FCD34D", background: "#FFFBEB", color: "#92400E", minWidth: 160 }}
                        >
                          <option value="">📋 Quick-select reason…</option>
                          {templates.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                      {/* Custom reason input */}
                      <input
                        type="text"
                        value={ls.withheldReason || ""}
                        onChange={(e) => setStudentReason(s.studentId, e.target.value)}
                        placeholder="Optional message shown to student (e.g. Marks entry in progress)…"
                        style={{ flex: 1, minWidth: 200, fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "1px solid #FCD34D", background: "#FFFBEB" }}
                      />
                    </div>
                  )}

                  {/* Show saved reason when published/saved */}
                  {ls.isPublished && s.withheldReason && !isDirty && (
                    <div style={{ marginTop: 6, fontSize: 11, color: "var(--muted)" }}>
                      Previously held with reason: <em>{s.withheldReason}</em>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {displayed.length < students.length && (
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 12 }}>
              Showing {displayed.length} of {students.length} students
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Print & Export
   ====================================================== */
function ExportPrint() {
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [classSectionId, setClassSectionId] = useState("");
  const [examId, setExamId] = useState("");
  const [sortBy, setSortBy] = useState("roll_no");

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data));
    api.get("/school-admin/exams").then((r) => setExams(r.data));
  }, []);

  function downloadExcel() {
    openAuthenticatedUrl(`/api/school-admin/export/results.xlsx?classSectionId=${classSectionId}&examId=${examId}&sortBy=${sortBy}`, `results-export.xlsx`);
  }

  function bulkPrintPDF() {
    window.open(`/print/class/${classSectionId}/exam/${examId}?sortBy=${sortBy}`, "_blank");
  }

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Print &amp; Export</h1>
        <p className="subtitle">Export class results to Excel or generate bulk PDF result cards.</p>
      </div>

      <div className="card">
        <h3>Select Class &amp; Exam</h3>
        <div className="form-grid" style={{ marginBottom: 20 }}>
          <div className="form-field">
            <label className="form-label">Class / Section</label>
            <select value={classSectionId} onChange={(e) => setClassSectionId(e.target.value)}>
              <option value="">Select class</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.section}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">Exam</label>
            <select value={examId} onChange={(e) => setExamId(e.target.value)}>
              <option value="">Select exam</option>
              {exams.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">Sort Order</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="roll_no">Roll Number</option>
              <option value="name">Alphabetical (A–Z)</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button className="btn" disabled={!classSectionId || !examId} onClick={downloadExcel}>
            Export to Excel
          </button>
          <button className="btn secondary" disabled={!classSectionId || !examId} onClick={bulkPrintPDF}>
            Bulk Print PDF Cards
          </button>
        </div>
        <p className="text-muted" style={{ marginTop: 16, fontSize: 13 }}>
          For individual student result cards, go to the <strong>Students</strong> tab, select an exam, and click the Print button next to any student.
        </p>
      </div>
    </div>
  );
}

/* ======================================================
   Promotion
   ====================================================== */
function Promotion() {
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [form, setForm] = useState({ classSectionId: "", finalExamId: "", session: "2026-27", minPercentage: 33 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/school-admin/classes").then((r) => setClasses(r.data));
    api.get("/school-admin/exams").then((r) => setExams(r.data));
  }, []);

  async function generate(e) {
    e.preventDefault(); setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/school-admin/promotion/generate", form);
      setRows(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to generate promotion list.");
    } finally {
      setLoading(false);
    }
  }

  function downloadExcel() {
    openAuthenticatedUrl(`/api/school-admin/promotion/export.xlsx?classSectionId=${form.classSectionId}&session=${form.session}`, `promotion-export.xlsx`);
  }

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Promotion List</h1>
        <p className="subtitle">Generate and export a promotion list based on final exam performance.</p>
      </div>

      <div className="card">
        <h3>Generate Promotion List</h3>
        {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}
        <form onSubmit={generate}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Class / Section *</label>
              <select value={form.classSectionId} onChange={(e) => setForm({ ...form, classSectionId: e.target.value })} required>
                <option value="">Select class</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.section}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Final / Annual Exam *</label>
              <select value={form.finalExamId} onChange={(e) => setForm({ ...form, finalExamId: e.target.value })} required>
                <option value="">Select exam</option>
                {exams.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Session</label>
              <input placeholder="e.g. 2026-27" value={form.session} onChange={(e) => setForm({ ...form, session: e.target.value })} />
            </div>
            <div className="form-field">
              <label className="form-label">Minimum % to Promote</label>
              <input type="number" placeholder="33" value={form.minPercentage} onChange={(e) => setForm({ ...form, minPercentage: e.target.value })} min="0" max="100" />
            </div>
          </div>
          <button className="btn" type="submit" disabled={loading}>
            {loading ? "Generating..." : "Generate"}
          </button>
        </form>

        {rows.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div className="card-header" style={{ marginBottom: 12 }}>
              <span><strong>{rows.length}</strong> students</span>
              <button className="btn sm secondary" onClick={downloadExcel}>Export to Excel</button>
            </div>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table>
              <thead>
                <tr>
                  <th>Roll No</th>
                  <th>Name</th>
                  <th>Percentage</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.studentId}>
                    <td>{r.rollNo}</td>
                    <td><strong>{r.name}</strong></td>
                    <td>{r.percentage}%</td>
                    <td>
                      <span className={`badge ${r.status === "promoted" ? "active" : "suspended"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================================================
   School Branding / Profile
   ====================================================== */
function SchoolBranding() {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    api.get("/school-admin/branding")
      .then((r) => { setBranding(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function handleChange(field, value) {
    setBranding((b) => ({ ...b, [field]: value }));
  }

  function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 400;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        handleChange("logo_data", canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true); setMessage(""); setIsError(false);
    try {
      const { data } = await api.put("/school-admin/branding", branding);
      setBranding(data);
      setMessage("School profile saved successfully.");
    } catch (err) {
      setIsError(true);
      setMessage(err.response?.data?.error || "Failed to save branding.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="card"><p>Loading school profile...</p></div>;

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">School Profile &amp; Header</h1>
        <p className="subtitle">Configure your school's branding, which appears on result cards and reports.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>
        {/* Left: Form */}
        <div>
          <div className="card">
            <h3>School Information</h3>
            <form onSubmit={save}>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">School Display Name</label>
                  <input
                    placeholder="e.g. Delhi Public School, RK Puram"
                    value={branding?.school_display_name || ""}
                    onChange={(e) => handleChange("school_display_name", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Affiliation No.</label>
                  <input
                    placeholder="e.g. 2730148"
                    value={branding?.affiliation_no || ""}
                    onChange={(e) => handleChange("affiliation_no", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">School Code</label>
                  <input
                    placeholder="e.g. 55016"
                    value={branding?.school_code || ""}
                    onChange={(e) => handleChange("school_code", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Board</label>
                  <select
                    value={branding?.board || ""}
                    onChange={(e) => handleChange("board", e.target.value)}
                  >
                    <option value="">Select Board</option>
                    <option value="CBSE">CBSE</option>
                    <option value="ICSE">ICSE</option>
                    <option value="State Board">State Board</option>
                    <option value="IB">IB</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
                <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Address</label>
                  <input
                    placeholder="Street address"
                    value={branding?.address || ""}
                    onChange={(e) => handleChange("address", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">City</label>
                  <input
                    placeholder="City"
                    value={branding?.city || ""}
                    onChange={(e) => handleChange("city", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">State</label>
                  <input
                    placeholder="State"
                    value={branding?.state || ""}
                    onChange={(e) => handleChange("state", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Pincode</label>
                  <input
                    placeholder="110001"
                    value={branding?.pincode || ""}
                    onChange={(e) => handleChange("pincode", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Principal Name</label>
                  <input
                    placeholder="Principal / Director name"
                    value={branding?.principal_name || ""}
                    onChange={(e) => handleChange("principal_name", e.target.value)}
                  />
                </div>
              </div>

              <div className="form-field" style={{ marginTop: 8 }}>
                <label className="form-label">School Logo</label>
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ padding: "6px 0" }} />
                {branding?.logo_data && (
                  <div style={{ marginTop: 10 }}>
                    <img
                      src={branding.logo_data}
                      alt="School Logo"
                      style={{ height: 80, maxWidth: 200, objectFit: "contain", border: "1px solid var(--border)", borderRadius: 8, padding: 4 }}
                    />
                    <button
                      type="button"
                      className="btn sm danger"
                      style={{ marginLeft: 12 }}
                      onClick={() => handleChange("logo_data", null)}
                    >
                      Remove Logo
                    </button>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div className="form-field">
                  <label className="form-label">Result Card Title</label>
                  <input
                    placeholder="REPORT CARD"
                    value={branding?.result_title || ""}
                    onChange={(e) => handleChange("result_title", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Header Color</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      type="color"
                      value={branding?.header_color || "#1E3A5F"}
                      onChange={(e) => handleChange("header_color", e.target.value)}
                      style={{ width: 48, height: 38, padding: 2, borderRadius: 6, border: "1px solid var(--border)", cursor: "pointer" }}
                    />
                    <input
                      placeholder="#1E3A5F"
                      value={branding?.header_color || ""}
                      onChange={(e) => handleChange("header_color", e.target.value)}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 16, display: "flex", gap: 24 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!branding?.show_header}
                    onChange={(e) => handleChange("show_header", e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  Show Header on Report Card
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!branding?.show_footer}
                    onChange={(e) => handleChange("show_footer", e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  Show Footer on Report Card
                </label>
              </div>

              {branding?.show_footer && (
                <div className="form-field" style={{ marginTop: 12 }}>
                  <label className="form-label">Footer Text</label>
                  <textarea
                    placeholder="e.g. This is a computer-generated result card. No signature required."
                    value={branding?.footer_text || ""}
                    onChange={(e) => handleChange("footer_text", e.target.value)}
                    rows={2}
                    style={{ resize: "vertical" }}
                  />
                </div>
              )}

              {message && (
                <div className={isError ? "error-text" : "success-text"} style={{ marginTop: 12 }}>
                  {message}
                </div>
              )}

              <button className="btn" type="submit" disabled={saving} style={{ marginTop: 16 }}>
                {saving ? "Saving..." : "Save School Profile"}
              </button>
            </form>
          </div>
        </div>

        {/* Right: Preview */}
        <div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>
              Live Preview — Result Card Header
            </div>
            <BrandingPreview branding={branding} />
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandingPreview({ branding }) {
  const color = branding?.header_color || "#1E3A5F";
  const title = branding?.result_title || "REPORT CARD";
  const schoolName = branding?.school_display_name || "Your School Name";
  const board = branding?.board ? `Affiliated to ${branding.board}` : "";
  const affNo = branding?.affiliation_no ? `Affil. No: ${branding.affiliation_no}` : "";
  const addr = [branding?.address, branding?.city, branding?.state, branding?.pincode].filter(Boolean).join(", ");

  return (
    <div style={{ fontFamily: "Georgia, serif" }}>
      {branding?.show_header !== false && (
        <div style={{ background: color, color: "#fff", padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
          {branding?.logo_data ? (
            <img src={branding.logo_data} alt="Logo" style={{ height: 56, width: 56, objectFit: "contain", background: "#fff", borderRadius: 4, padding: 2 }} />
          ) : (
            <div style={{ height: 56, width: 56, background: "rgba(255,255,255,0.2)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              🏫
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>{schoolName}</div>
            {board && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{board}{affNo ? ` | ${affNo}` : ""}</div>}
            {addr && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{addr}</div>}
          </div>
        </div>
      )}
      <div style={{ background: "#f3f4f6", borderBottom: "2px solid " + color, textAlign: "center", padding: "8px 0", letterSpacing: 3, fontSize: 13, fontWeight: 700, color: color }}>
        {title}
      </div>
      <div style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#888" }}>Student Name</div>
            <div style={{ fontSize: 13, borderBottom: "1px solid #ddd", paddingBottom: 2 }}>_____________________</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#888" }}>Class</div>
            <div style={{ fontSize: 13, borderBottom: "1px solid #ddd", paddingBottom: 2 }}>___________</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#aaa", textAlign: "center", marginTop: 8 }}>[ Marks table will appear here ]</div>
      </div>
      {branding?.show_footer !== false && (
        <div style={{ borderTop: "1px solid #ddd", padding: "8px 16px", fontSize: 11, color: "#666", textAlign: "center" }}>
          {branding?.footer_text || "This is a computer-generated result card."}
          {branding?.principal_name && (
            <div style={{ marginTop: 4, fontStyle: "italic" }}>
              Principal: {branding.principal_name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Result Settings
   ====================================================== */
const CBSE_10POINT_GRADES = [
  { grade: "A1", range: "91–100", point: "10.0" },
  { grade: "A2", range: "81–90", point: "9.0" },
  { grade: "B1", range: "71–80", point: "8.0" },
  { grade: "B2", range: "61–70", point: "7.0" },
  { grade: "C1", range: "51–60", point: "6.0" },
  { grade: "C2", range: "41–50", point: "5.0" },
  { grade: "D", range: "33–40", point: "4.0" },
  { grade: "E1", range: "21–32", point: "Fail" },
  { grade: "E2", range: "00–20", point: "Fail" },
];

const CBSE_5POINT_GRADES = [
  { grade: "A+", range: "91–100" },
  { grade: "A", range: "76–90" },
  { grade: "B+", range: "56–75" },
  { grade: "B", range: "41–55" },
  { grade: "C", range: "33–40" },
  { grade: "D", range: "00–32", point: "Fail" },
];

const GRADE_PRESETS = {
  cbse10: [
    { minPct: 91, grade: "A1", desc: "Outstanding",      color: "#15803D" },
    { minPct: 81, grade: "A2", desc: "Excellent",         color: "#16A34A" },
    { minPct: 71, grade: "B1", desc: "Very Good",         color: "#2563EB" },
    { minPct: 61, grade: "B2", desc: "Good",              color: "#3B82F6" },
    { minPct: 51, grade: "C1", desc: "Average",           color: "#D97706" },
    { minPct: 41, grade: "C2", desc: "Satisfactory",      color: "#EA580C" },
    { minPct: 33, grade: "D",  desc: "Pass",              color: "#B45309" },
    { minPct: 0,  grade: "E",  desc: "Needs Improvement", color: "#DC2626" },
  ],
  cbse5: [
    { minPct: 91, grade: "A+", desc: "Outstanding",      color: "#15803D" },
    { minPct: 76, grade: "A",  desc: "Excellent",         color: "#16A34A" },
    { minPct: 56, grade: "B+", desc: "Very Good",         color: "#2563EB" },
    { minPct: 41, grade: "B",  desc: "Good",              color: "#3B82F6" },
    { minPct: 33, grade: "C",  desc: "Pass",              color: "#D97706" },
    { minPct: 0,  grade: "D",  desc: "Needs Improvement", color: "#DC2626" },
  ],
  simple: [
    { minPct: 75, grade: "Distinction", desc: "Outstanding",      color: "#15803D" },
    { minPct: 60, grade: "First",       desc: "First Division",   color: "#2563EB" },
    { minPct: 45, grade: "Second",      desc: "Second Division",  color: "#D97706" },
    { minPct: 33, grade: "Third",       desc: "Third Division",   color: "#EA580C" },
    { minPct: 0,  grade: "Fail",        desc: "Fail",             color: "#DC2626" },
  ],
  pass40: [
    { minPct: 91, grade: "A1", desc: "Outstanding",      color: "#15803D" },
    { minPct: 81, grade: "A2", desc: "Excellent",         color: "#16A34A" },
    { minPct: 71, grade: "B1", desc: "Very Good",         color: "#2563EB" },
    { minPct: 61, grade: "B2", desc: "Good",              color: "#3B82F6" },
    { minPct: 51, grade: "C1", desc: "Average",           color: "#D97706" },
    { minPct: 40, grade: "C2", desc: "Pass",              color: "#EA580C" },
    { minPct: 0,  grade: "F",  desc: "Fail",              color: "#DC2626" },
  ],
};

function ResultSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [gradeScale, setGradeScale] = useState(GRADE_PRESETS.cbse10);

  useEffect(() => {
    api.get("/school-admin/result-config")
      .then((r) => {
        setConfig(r.data);
        setGradeScale(r.data?.custom_grade_config?.scale || GRADE_PRESETS.cbse10);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  function handleChange(field, value) {
    setConfig((c) => ({ ...c, [field]: value }));
  }

  function handleScaleRowChange(idx, field, value) {
    setGradeScale((rows) => rows.map((r, i) => i === idx ? { ...r, [field]: field === "minPct" ? Number(value) : value } : r));
  }

  function deleteScaleRow(idx) {
    setGradeScale((rows) => rows.filter((_, i) => i !== idx));
  }

  function addScaleRow() {
    setGradeScale((rows) => [...rows, { minPct: 0, grade: "", desc: "", color: "#6B7280" }]);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true); setMessage(""); setIsError(false);
    try {
      const payload = { ...config, custom_grade_config: { scale: gradeScale } };
      const { data } = await api.put("/school-admin/result-config", payload);
      setConfig(data);
      setGradeScale(data?.custom_grade_config?.scale || gradeScale);
      setMessage("Result settings saved successfully.");
    } catch (err) {
      setIsError(true);
      setMessage(err.response?.data?.error || "Failed to save result settings.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="card"><p>Loading result settings...</p></div>;

  const gradingSystem = config?.grading_system || "percentage";
  const passPercent = config?.pass_percentage || 33;

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Result Settings</h1>
        <p className="subtitle">Configure grading system, pass criteria, and what appears on result cards.</p>
      </div>

      <div className="card">
        <h3>Grading &amp; Pass Criteria</h3>
        <form onSubmit={save}>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Grading System</label>
              <select value={gradingSystem} onChange={(e) => handleChange("grading_system", e.target.value)}>
                <option value="percentage">Marks &amp; Percentage</option>
                <option value="cbse_10point">CBSE 10-Point Grade</option>
                <option value="cbse_5point">CBSE 5-Point Grade (Primary)</option>
                <option value="icse">ICSE</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Pass Percentage (%)</label>
              <input
                type="number"
                placeholder="33"
                value={config?.pass_percentage ?? 33}
                onChange={(e) => handleChange("pass_percentage", Number(e.target.value))}
                min="0"
                max="100"
              />
            </div>
          </div>

          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 24 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!config?.show_grade}
                onChange={(e) => handleChange("show_grade", e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Show Grade on Result Card
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!config?.show_rank}
                onChange={(e) => handleChange("show_rank", e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Show Rank in Class
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!config?.rank_passed_only}
                onChange={(e) => handleChange("rank_passed_only", e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Rank Only Passed Students
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!config?.teacher_remarks_enabled}
                onChange={(e) => handleChange("teacher_remarks_enabled", e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Enable Teacher Remarks
            </label>
          </div>

          <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <h4 style={{ margin: "0 0 12px" }}>Co-Scholastic Assessment</h4>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={!!config?.co_scholastic_enabled}
                onChange={(e) => handleChange("co_scholastic_enabled", e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Enable Co-Scholastic Grading
            </label>
            {config?.co_scholastic_enabled && (
              <div className="form-field" style={{ maxWidth: 320 }}>
                <label className="form-label">Co-Scholastic Scale</label>
                <select
                  value={config?.co_scholastic_scale || "grade_only"}
                  onChange={(e) => handleChange("co_scholastic_scale", e.target.value)}
                >
                  <option value="grade_only">Grade Only (A/B/C...)</option>
                  <option value="5point">5-Point Scale (A1=91-100...)</option>
                  <option value="8point">8-Point Scale</option>
                </select>
              </div>
            )}
          </div>

          {/* Student Fields on Result Card */}
          <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <h4 style={{ margin: "0 0 6px" }}>Student Fields on Result Card</h4>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 14 }}>
              Select which student details appear on the printed result card. Selected fields will also be
              <strong> mandatory to enter</strong> when adding or editing a student (marked ✦).
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {CARD_SELECTABLE_FIELDS.map((field) => {
                const currentFields = config?.student_card_fields || [];
                const checked = field.alwaysRequired || currentFields.includes(field.key);
                return (
                  <label
                    key={field.key}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, cursor: field.alwaysRequired ? "default" : "pointer",
                      padding: "8px 14px", border: "1px solid var(--border)", borderRadius: 8,
                      background: checked ? "#EFF6FF" : "#fff",
                      borderColor: checked ? "#BFDBFE" : "var(--border)",
                      opacity: field.alwaysRequired ? 0.7 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={field.alwaysRequired}
                      onChange={(e) => {
                        const curr = Array.isArray(config?.student_card_fields) ? config.student_card_fields : [];
                        const next = e.target.checked
                          ? [...curr.filter((k) => k !== field.key), field.key]
                          : curr.filter((k) => k !== field.key);
                        handleChange("student_card_fields", next);
                      }}
                      style={{ width: 15, height: 15 }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {field.label}
                      {field.alwaysRequired && <span className="text-muted" style={{ fontSize: 11, marginLeft: 4 }}>(always)</span>}
                    </span>
                  </label>
                );
              })}
              {/* Photo toggle separately */}
              {(() => {
                const photoField = STUDENT_FIELDS.find((f) => f.key === "photo");
                const curr = Array.isArray(config?.student_card_fields) ? config.student_card_fields : [];
                const photoChecked = curr.includes("photo");
                return (
                  <label
                    style={{
                      display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                      padding: "8px 14px", border: "1px solid var(--border)", borderRadius: 8,
                      background: photoChecked ? "#EFF6FF" : "#fff",
                      borderColor: photoChecked ? "#BFDBFE" : "var(--border)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={photoChecked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...curr.filter((k) => k !== "photo"), "photo"]
                          : curr.filter((k) => k !== "photo");
                        handleChange("student_card_fields", next);
                      }}
                      style={{ width: 15, height: 15 }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{photoField.label}</span>
                  </label>
                );
              })()}
            </div>
          </div>

          {/* Grade Scale Section */}
          <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <h4 style={{ margin: "0 0 4px" }}>Grade Scale</h4>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
              Customize how percentage marks are converted to letter grades on result cards.
            </p>

            {/* Preset buttons */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {[
                { key: "cbse10", label: "CBSE 10-Point" },
                { key: "cbse5",  label: "CBSE 5-Point"  },
                { key: "simple", label: "Simple (A/B/C/D/F)" },
                { key: "pass40", label: "40% Pass Scale" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 13, padding: "5px 14px" }}
                  onClick={() => setGradeScale(GRADE_PRESETS[key])}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Editable grade scale table */}
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--border)", width: 80 }}>Min %</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--border)", width: 110 }}>Grade</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--border)" }}>Description</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--border)", width: 80 }}>Color</th>
                    <th style={{ padding: "6px 8px", borderBottom: "2px solid var(--border)", width: 48 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {gradeScale.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "4px 8px" }}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={row.minPct}
                          onChange={(e) => handleScaleRowChange(idx, "minPct", e.target.value)}
                          style={{ width: 64, padding: "4px 6px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: "4px 8px" }}>
                        <input
                          type="text"
                          value={row.grade}
                          onChange={(e) => handleScaleRowChange(idx, "grade", e.target.value)}
                          style={{ width: 96, padding: "4px 6px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: "4px 8px" }}>
                        <input
                          type="text"
                          value={row.desc}
                          onChange={(e) => handleScaleRowChange(idx, "desc", e.target.value)}
                          style={{ width: "100%", padding: "4px 6px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 4 }}
                        />
                      </td>
                      <td style={{ padding: "4px 8px" }}>
                        <input
                          type="color"
                          value={row.color}
                          onChange={(e) => handleScaleRowChange(idx, "color", e.target.value)}
                          style={{ width: 40, height: 32, padding: 2, border: "1px solid var(--border)", borderRadius: 4, cursor: "pointer" }}
                        />
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => deleteScaleRow(idx)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 16, lineHeight: 1, padding: "2px 6px" }}
                          title="Remove row"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 10, fontSize: 13, padding: "5px 14px" }}
              onClick={addScaleRow}
            >
              + Add Row
            </button>
          </div>

          {message && (
            <div className={isError ? "error-text" : "success-text"} style={{ marginTop: 12 }}>
              {message}
            </div>
          )}

          <button className="btn" type="submit" disabled={saving} style={{ marginTop: 16 }}>
            {saving ? "Saving..." : "Save Result Settings"}
          </button>
        </form>
      </div>

      {/* Grade Scale Display */}
      {gradingSystem === "cbse_10point" && (
        <div className="card">
          <h3>CBSE 10-Point Grading Scale</h3>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table>
            <thead>
              <tr><th>Grade</th><th>Marks Range (%)</th><th>Grade Point</th></tr>
            </thead>
            <tbody>
              {CBSE_10POINT_GRADES.map((g) => (
                <tr key={g.grade}>
                  <td><strong>{g.grade}</strong></td>
                  <td>{g.range}</td>
                  <td>
                    <span className={`badge ${g.point === "Fail" ? "suspended" : "active"}`}>{g.point}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gradingSystem === "cbse_5point" && (
        <div className="card">
          <h3>CBSE 5-Point Grading Scale (Primary)</h3>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table>
            <thead>
              <tr><th>Grade</th><th>Marks Range (%)</th><th>Status</th></tr>
            </thead>
            <tbody>
              {CBSE_5POINT_GRADES.map((g) => (
                <tr key={g.grade}>
                  <td><strong>{g.grade}</strong></td>
                  <td>{g.range}</td>
                  <td>
                    <span className={`badge ${g.point === "Fail" ? "suspended" : "active"}`}>
                      {g.point === "Fail" ? "Fail" : "Pass"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gradingSystem === "percentage" && (
        <div className="card">
          <h3>Marks &amp; Percentage System</h3>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table>
            <thead>
              <tr><th>Criterion</th><th>Value</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Pass marks per subject</td>
                <td>{passPercent}% of that subject's maximum marks</td>
              </tr>
              <tr>
                <td>Overall pass</td>
                <td>Must pass ALL scholastic subjects individually</td>
              </tr>
              <tr>
                <td>Result calculation</td>
                <td>Percentage = (Total Obtained ÷ Total Maximum) × 100</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* How Results Are Calculated */}
      <div className="card" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
        <h4 style={{ margin: "0 0 12px", color: "var(--primary)" }}>How Results Are Calculated</h4>
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9, fontSize: 14, color: "var(--muted)" }}>
          <li>Each subject score = marks obtained out of maximum marks</li>
          <li>Percentage = (Total Obtained ÷ Total Maximum) × 100</li>
          <li>A student passes a subject if they score &ge; <strong>{passPercent}%</strong> of that subject's maximum marks</li>
          <li>A student passes overall if they pass <strong>ALL</strong> scholastic subjects</li>
          <li>Co-scholastic subjects are graded separately and do <strong>not</strong> affect pass/fail</li>
          <li>Grade is assigned based on the <em>{gradingSystem}</em> grading system configured above</li>
        </ol>
      </div>
    </div>
  );
}

/* ======================================================
   Result Template Gallery
   ====================================================== */
const VIEW_META = {
  one_exam:   { label: "Single Exam View",  cols: 1, desc: "Shows marks & grade for one exam. Best for PT, Unit Tests, Class Tests." },
  two_exam:   { label: "Two Exam View",     cols: 2, desc: "Compares two exams side-by-side. Ideal for Half-Yearly + Annual." },
  three_exam: { label: "Three Exam View",   cols: 3, desc: "Three term comparison. Perfect for Term 1 + Term 2 + Annual." },
  four_exam:  { label: "Four Exam View",    cols: 4, desc: "Full-year view: PT1 + PT2 + HY + Annual in one result card." },
};

function TemplateGalleryCards({ templates, activeId, onChoose, saving, onPreview, examSels = [], exams = [], isDefault = false }) {
  if (templates.length === 0) return (
    <div className="card">
      <div className="empty-state">
        <div className="empty-state-icon">📋</div>
        <div className="empty-state-text">No templates available yet. Ask your platform administrator to add templates.</div>
      </div>
    </div>
  );
  return (
    <div className="tpl-gallery">
      {templates.map((tpl, idx) => {
        const tplNum = idx + 1;
        const meta = VIEW_META[tpl.view_type] || { label: tpl.view_type, cols: 1, desc: "" };
        const cfg = parseCfg(tpl.layout_config);
        const isActive = activeId === tpl.id;
        // Exams assigned to this specific template
        const assignedExams = examSels
          .filter((e) => String(e.templateId) === String(tpl.id))
          .map((e) => e.examName || exams.find((x) => String(x.id) === String(e.examId))?.name || `Exam #${e.examId}`);
        const isAssignedToExam = assignedExams.length > 0;
        const isSchoolDefault = isDefault && isActive;
        return (
          <div key={tpl.id} className={`tpl-card ${isActive ? "tpl-card-active" : ""}`}
            style={{ "--tpl-theme": cfg.themeColor || "#1E3A5F" }}>
            {isActive && <div className="tpl-badge-selected">✓ Selected</div>}
            <div className="tpl-preview-wrap" onClick={() => onPreview(tpl)}>
              <div style={{
                position: "absolute", top: 8, left: 8, zIndex: 5,
                background: isActive ? (cfg.themeColor || "#1E3A5F") : "#334155",
                color: "#fff",
                fontWeight: 700, fontSize: 12, borderRadius: 6,
                padding: "2px 8px", letterSpacing: "0.03em", pointerEvents: "none",
              }}>
                #{tplNum}
              </div>
              <MiniResultCard cols={meta.cols} tpl={tpl} />
              <div className="tpl-preview-overlay"><span>🔍 Full Preview</span></div>
            </div>
            <div className="tpl-card-body">
              <div className="tpl-card-name">
                <span style={{ color: "#94A3B8", fontWeight: 600, marginRight: 6, fontSize: 12 }}>#{tplNum}</span>
                {tpl.name}
              </div>
              <div className="tpl-card-type" style={{ color: cfg.themeColor || "var(--blue)" }}>{meta.label}</div>
              <div className="tpl-card-desc">{cfg.subtitle || meta.desc}</div>
              <div className="tpl-card-badges">
                <span className="badge info">{meta.cols} Exam{meta.cols > 1 ? "s" : ""}</span>
                {cfg.showGraph        && <span className="badge info">📊 Graph</span>}
                {cfg.hasCoScholastic  && <span className="badge active">🎨 Co-Scholastic</span>}
                {cfg.showTeacherRemark && <span className="badge info">✏️ Remarks</span>}
                {cfg.showPhoto        && <span className="badge info">📷 Photo</span>}
                {cfg.showRank         && <span className="badge info">🏅 Rank</span>}
              </div>
              {/* Active assignments */}
              {(isSchoolDefault || isAssignedToExam) && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  {isSchoolDefault && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 6, background: "#EFF6FF", border: "1px solid #BFDBFE", fontSize: 11, fontWeight: 700, color: "#1D4ED8" }}>
                      🏫 School Default
                    </div>
                  )}
                  {assignedExams.map((name, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 6, background: "#F5F3FF", border: "1px solid #DDD6FE", fontSize: 11, fontWeight: 600, color: "#6D28D9" }}>
                      📝 {name}
                    </div>
                  ))}
                </div>
              )}
              <button
                className={`btn ${isActive ? "secondary" : ""}`}
                style={{ width: "100%", marginTop: 12 }}
                disabled={isActive || saving}
                onClick={() => onChoose(tpl.id)}
              >
                {isActive ? "✓ Currently Active" : saving ? "Saving..." : "Select This Template"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ResultTemplateGallery() {
  const [templates, setTemplates]   = useState([]);
  const [defaultSel, setDefaultSel] = useState(null);
  const [examSels, setExamSels]     = useState([]);
  const [exams, setExams]           = useState([]);
  const [saving, setSaving]         = useState(false);
  const [savingExam, setSavingExam] = useState(null);
  const [msg, setMsg]               = useState("");
  const [preview, setPreview]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [pickingFor, setPickingFor] = useState(null); // { examId, examName }

  useEffect(() => {
    Promise.allSettled([
      api.get("/school-admin/result-templates"),
      api.get("/school-admin/result-templates/selection"),
      api.get("/school-admin/result-templates/exam-selections"),
      api.get("/school-admin/exams"),
    ]).then(([tplRes, selRes, examSelRes, examsRes]) => {
      if (tplRes.status === "fulfilled")     setTemplates(tplRes.value.data);
      if (selRes.status === "fulfilled" && selRes.value.data) setDefaultSel(selRes.value.data.template_id);
      if (examSelRes.status === "fulfilled") setExamSels(examSelRes.value.data);
      if (examsRes.status === "fulfilled")   setExams(examsRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  async function chooseDefault(id) {
    if (id === defaultSel) return;
    setSaving(true); setMsg("");
    try {
      await api.put("/school-admin/result-templates/selection", { template_id: id });
      setDefaultSel(id);
      setMsg("Default template saved as school default.");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("Failed to save. Please try again."); }
    finally { setSaving(false); }
  }

  async function chooseForExam(examId, templateId) {
    setSavingExam(examId); setMsg("");
    try {
      await api.put("/school-admin/result-templates/exam-selections", { exam_id: examId, template_id: templateId });
      const examName = exams.find((e) => String(e.id) === String(examId))?.name || `Exam #${examId}`;
      const templateName = templates.find((t) => t.id === templateId)?.name || "";
      setExamSels((prev) => {
        const exists = prev.find((e) => String(e.examId) === String(examId));
        if (exists) return prev.map((e) => String(e.examId) === String(examId) ? { ...e, templateId, templateName } : e);
        return [...prev, { examId, examName, templateId, templateName }];
      });
      setPickingFor(null);
      setMsg(`Template "${templateName}" assigned to ${examName}.`);
      setTimeout(() => setMsg(""), 4000);
    } catch { setMsg("Failed to save exam template."); }
    finally { setSavingExam(null); }
  }

  async function removeExamSel(examId) {
    setSavingExam(examId);
    try {
      await api.delete(`/school-admin/result-templates/exam-selections/${examId}`);
      setExamSels((prev) => prev.filter((e) => String(e.examId) !== String(examId)));
    } catch { alert("Could not remove exam template."); }
    finally { setSavingExam(null); }
  }

  if (loading) return <div className="card"><p className="text-muted">Loading templates...</p></div>;

  const defaultTplName = templates.find((t) => t.id === defaultSel)?.name;
  const defaultTplNum = defaultSel ? templates.findIndex((t) => t.id === defaultSel) + 1 : null;

  return (
    <div>
      <div className="page-header sa-page-header">
        <h1 className="page-title">Result Card Templates</h1>
        <p className="subtitle">Select a template for your school. You can also assign a specific template per exam — that template will be used when printing results for that exam.</p>
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, color: "#065F46", fontSize: 13 }}>
          ✅ {msg}
        </div>
      )}

      {/* ── Exam-wise Assignment card ── */}
      {exams.length > 0 && (
        <div className="card" style={{ marginBottom: 24, borderLeft: "4px solid #7C3AED" }}>
          <div style={{ marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#6D28D9" }}>📝 Exam-wise Template Assignment</h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>
              Assign a specific template to each exam. When printing results for that exam, the assigned template will be used instead of the school default.
            </p>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ fontSize: 13, width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  <th style={{ padding: "8px 14px", textAlign: "left", fontWeight: 600, color: "#475569", borderBottom: "1px solid #E2E8F0" }}>Exam</th>
                  <th style={{ padding: "8px 14px", textAlign: "left", fontWeight: 600, color: "#475569", borderBottom: "1px solid #E2E8F0" }}>Assigned Template</th>
                  <th style={{ padding: "8px 14px", textAlign: "center", fontWeight: 600, color: "#475569", borderBottom: "1px solid #E2E8F0", width: 170 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {exams.map((exam) => {
                  const override = examSels.find((e) => String(e.examId) === String(exam.id));
                  const isLoading = savingExam === exam.id;
                  const overrideTplIdx = override ? templates.findIndex((t) => String(t.id) === String(override.templateId)) : -1;
                  const overrideTplNum = overrideTplIdx >= 0 ? overrideTplIdx + 1 : null;
                  const defaultTplIdx = templates.findIndex((t) => t.id === defaultSel);
                  const defaultTplNum = defaultTplIdx >= 0 ? defaultTplIdx + 1 : null;
                  return (
                    <tr key={exam.id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: "10px 14px", fontWeight: 600 }}>
                        {exam.name}
                        <span style={{ fontSize: 11, color: "#94A3B8", marginLeft: 6 }}>{exam.session}</span>
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        {override ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, background: "#EDE9FE", color: "#6D28D9", border: "1px solid #C4B5FD", fontSize: 12, fontWeight: 700 }}>
                            {overrideTplNum && <span style={{ background: "#6D28D9", color: "#fff", borderRadius: 4, padding: "0 5px", fontSize: 11, fontWeight: 700 }}>#{overrideTplNum}</span>}
                            🎨 {override.templateName || `Template #${override.templateId}`}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: "#94A3B8" }}>
                            School default{defaultTplNum ? ` (#${defaultTplNum}` : ""}{defaultTplName ? ` — ${defaultTplName}` : ""}{defaultTplNum ? ")" : ""}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <button
                            className="btn sm"
                            style={{ fontSize: 12 }}
                            disabled={isLoading}
                            onClick={() => setPickingFor({ examId: exam.id, examName: exam.name })}
                          >
                            {override ? "Change" : "Assign Template"}
                          </button>
                          {override && (
                            <button
                              className="btn sm secondary"
                              style={{ fontSize: 12 }}
                              disabled={isLoading}
                              onClick={() => removeExamSel(exam.id)}
                            >
                              {isLoading ? "…" : "Reset"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Template Gallery (school default) ── */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1E293B" }}>
          🏫 All Templates
          {defaultTplName && <span style={{ fontSize: 12, fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>School default: #{defaultTplNum} — {defaultTplName}</span>}
        </h3>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>
          Click "Select This Template" to set it as the school-wide default (used for all exams that don't have a specific assignment above).
        </p>
      </div>

      <TemplateGalleryCards
        templates={templates}
        activeId={defaultSel}
        onChoose={chooseDefault}
        saving={saving}
        onPreview={setPreview}
        examSels={examSels}
        exams={exams}
        isDefault={true}
      />

      {/* ── Template Picker Modal (for exam assignment) ── */}
      {pickingFor && (
        <div className="tpl-modal-backdrop" onClick={() => setPickingFor(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 960, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div>
                <strong>Assign template for: {pickingFor.examName}</strong>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>This template will be used when printing results for this exam.</div>
              </div>
              <button className="tpl-modal-close" onClick={() => setPickingFor(null)}>✕</button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <TemplateGalleryCards
                templates={templates}
                activeId={examSels.find((e) => String(e.examId) === String(pickingFor.examId))?.templateId ?? null}
                onChoose={(tplId) => chooseForExam(pickingFor.examId, tplId)}
                saving={savingExam === pickingFor.examId}
                onPreview={setPreview}
              />
            </div>
          </div>
        </div>
      )}

      {/* Full Preview Modal */}
      {preview && (
        <div className="tpl-modal-backdrop" onClick={() => setPreview(null)}>
          <div className="tpl-modal tpl-modal-a4 sa-modal-inner" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <div>
                <strong>{preview.name}</strong>
                <span className="text-muted" style={{ marginLeft: 10, fontSize: 13 }}>
                  {VIEW_META[preview.view_type]?.label} — A4 Preview
                </span>
              </div>
              <button className="tpl-modal-close" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div className="tpl-modal-body tpl-modal-body-a4">
              <FullResultCard tpl={preview} cols={VIEW_META[preview.view_type]?.cols || 1} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================
   Admin Accounts
   ====================================================== */
function SchoolAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", username: "", password: "", email: "", mobile: "" });
  const [createErr, setCreateErr] = useState("");
  const [createSaving, setCreateSaving] = useState(false);

  // Promote teacher form
  const [showPromote, setShowPromote] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [promoteForm, setPromoteForm] = useState({ teacherId: "", password: "" });
  const [promoteErr, setPromoteErr] = useState("");
  const [promoteSaving, setPromoteSaving] = useState(false);

  // Edit info modal
  const [editModal, setEditModal] = useState(null);
  const [editErr, setEditErr] = useState("");

  // Edit credentials modal
  const [credsModal, setCredsModal] = useState(null);
  const [credsForm, setCredsForm] = useState({ username: "", password: "" });
  const [credsErr, setCredsErr] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/school-admin/admins")
      .then((r) => { setAdmins(r.data); setLoading(false); })
      .catch(() => { setError("Failed to load admin accounts."); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  async function handleCreate(e) {
    e.preventDefault(); setCreateErr(""); setCreateSaving(true);
    try {
      await api.post("/school-admin/admins", createForm);
      setCreateForm({ name: "", username: "", password: "", email: "", mobile: "" });
      setShowCreate(false);
      load();
    } catch (err) { setCreateErr(err.response?.data?.error || "Failed to create admin."); }
    finally { setCreateSaving(false); }
  }

  async function openPromote() {
    setShowPromote(true); setPromoteErr("");
    setPromoteForm({ teacherId: "", password: "" });
    try {
      const r = await api.get("/school-admin/teachers");
      setTeachers(r.data || []);
    } catch { setTeachers([]); }
  }

  async function handlePromote(e) {
    e.preventDefault(); setPromoteErr(""); setPromoteSaving(true);
    try {
      const r = await api.post("/school-admin/admins/promote-teacher", promoteForm);
      alert(r.data.message);
      setShowPromote(false);
      load();
    } catch (err) { setPromoteErr(err.response?.data?.error || "Failed to promote teacher."); }
    finally { setPromoteSaving(false); }
  }

  async function handleSaveEdit(e) {
    e.preventDefault(); setEditErr("");
    try {
      await api.put(`/school-admin/admins/${editModal.id}`, { name: editModal.name, username: editModal.username, email: editModal.email, mobile: editModal.mobile });
      setEditModal(null); load();
    } catch (err) { setEditErr(err.response?.data?.error || "Failed to update."); }
  }

  async function handleSaveCreds(e) {
    e.preventDefault(); setCredsErr("");
    try {
      await api.put(`/school-admin/admins/${credsModal.id}/credentials`, credsForm);
      setCredsModal(null); setCredsForm({ username: "", password: "" }); load();
    } catch (err) { setCredsErr(err.response?.data?.error || "Failed to update credentials."); }
  }

  async function handleToggle(admin) {
    try {
      await api.put(`/school-admin/admins/${admin.id}/toggle-status`);
      load();
    } catch (err) { alert(err.response?.data?.error || "Failed to update status."); }
  }

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>Admin Accounts</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn sm secondary" onClick={openPromote}>⬆ Promote Teacher</button>
          <button className="btn sm" onClick={() => { setShowCreate(true); setShowPromote(false); setCreateErr(""); }}>+ Add Admin</button>
        </div>
      </div>
      <p className="subtitle" style={{ marginBottom: 16 }}>
        Manage school admin accounts. Each admin can log in and access all school admin features.
      </p>

      {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Promote Teacher form */}
      {showPromote && (
        <form onSubmit={handlePromote} style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "#92400E" }}>⬆ Promote Teacher to Admin</div>
          <p style={{ fontSize: 13, color: "#78350F", marginBottom: 12 }}>
            The selected teacher will be converted to a school admin account and will be able to log in to the admin panel.
          </p>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Select Teacher *</label>
              <select
                value={promoteForm.teacherId}
                onChange={(e) => setPromoteForm({ ...promoteForm, teacherId: e.target.value })}
                required
              >
                <option value="">— Select a teacher —</option>
                {teachers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.designation ? ` (${t.designation})` : ""} — {t.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">New Admin Password * (min 8 chars)</label>
              <input
                type="password"
                value={promoteForm.password}
                onChange={(e) => setPromoteForm({ ...promoteForm, password: e.target.value })}
                required
                placeholder="Set a password for admin login"
              />
            </div>
          </div>
          {promoteErr && <div className="error-text" style={{ marginBottom: 8 }}>{promoteErr}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" type="submit" style={{ background: "#D97706", borderColor: "#D97706" }} disabled={promoteSaving}>
              {promoteSaving ? "Promoting…" : "Promote to Admin"}
            </button>
            <button className="btn secondary" type="button" onClick={() => setShowPromote(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>New Admin Account</div>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Name *</label>
              <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required placeholder="Full name" />
            </div>
            <div className="form-field">
              <label className="form-label">Username *</label>
              <input value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} required placeholder="Login username" />
            </div>
            <div className="form-field">
              <label className="form-label">Password * (min 8 chars)</label>
              <input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required placeholder="Temporary password" />
            </div>
            <div className="form-field">
              <label className="form-label">Email</label>
              <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="Optional" />
            </div>
            <div className="form-field">
              <label className="form-label">Mobile</label>
              <input value={createForm.mobile} onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          {createErr && <div className="error-text" style={{ marginBottom: 8 }}>{createErr}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" type="submit" disabled={createSaving}>{createSaving ? "Creating..." : "Create Admin"}</button>
            <button className="btn secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Admins table */}
      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : admins.length === 0 ? (
        <p className="text-muted">No admin accounts yet. Click "Add Admin" to create one.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.name}</strong></td>
                  <td><code style={{ fontSize: 12 }}>{a.username}</code></td>
                  <td style={{ fontSize: 13 }}>{a.email || "—"}</td>
                  <td style={{ fontSize: 13 }}>{a.mobile || "—"}</td>
                  <td>
                    <span className={`badge ${a.is_active ? "active" : "suspended"}`}>
                      {a.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      <button className="btn sm secondary" onClick={() => { setEditModal({ ...a }); setEditErr(""); }}>Edit</button>
                      <button className="btn sm secondary" onClick={() => { setCredsModal(a); setCredsForm({ username: a.username, password: "" }); setCredsErr(""); }}>Credentials</button>
                      <button className={`btn sm ${a.is_active ? "danger" : ""}`} style={!a.is_active ? { background: "#16A34A", borderColor: "#16A34A", color: "white" } : {}} onClick={() => handleToggle(a)}>
                        {a.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit info modal */}
      {editModal && (
        <div className="tpl-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Edit Admin — {editModal.name}</strong>
              <button className="tpl-modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {editErr && <div className="error-text" style={{ marginBottom: 10 }}>{editErr}</div>}
              <form onSubmit={handleSaveEdit}>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">Name *</label>
                    <input value={editModal.name} onChange={(e) => setEditModal({ ...editModal, name: e.target.value })} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Username *</label>
                    <input value={editModal.username || ""} onChange={(e) => setEditModal({ ...editModal, username: e.target.value })} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Email</label>
                    <input type="email" value={editModal.email || ""} onChange={(e) => setEditModal({ ...editModal, email: e.target.value })} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Mobile</label>
                    <input value={editModal.mobile || ""} onChange={(e) => setEditModal({ ...editModal, mobile: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className="btn" type="submit">Save</button>
                  <button className="btn secondary" type="button" onClick={() => setEditModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Change credentials modal */}
      {credsModal && (
        <div className="tpl-modal-overlay" onClick={() => setCredsModal(null)}>
          <div className="tpl-modal sa-modal-inner" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <strong>Change Credentials — {credsModal.name}</strong>
              <button className="tpl-modal-close" onClick={() => setCredsModal(null)}>✕</button>
            </div>
            <div className="tpl-modal-body">
              {credsErr && <div className="error-text" style={{ marginBottom: 10 }}>{credsErr}</div>}
              <form onSubmit={handleSaveCreds}>
                <div className="form-grid">
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Username</label>
                    <input value={credsForm.username} onChange={(e) => setCredsForm({ ...credsForm, username: e.target.value })} placeholder="Leave unchanged to keep current" />
                  </div>
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">New Password (min 8 chars)</label>
                    <input type="password" value={credsForm.password} onChange={(e) => setCredsForm({ ...credsForm, password: e.target.value })} placeholder="Leave blank to keep current" />
                  </div>
                </div>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "8px 0 12px" }}>
                  The admin will be required to change their password on next login.
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn" type="submit">Update</button>
                  <button className="btn secondary" type="button" onClick={() => setCredsModal(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_COSCHO = ["Work Education", "Art Education", "Health & Physical Education", "Discipline"];



